import Phaser from 'phaser';

export interface AnimationPreset {
  play(scene: Phaser.Scene, sprite: Phaser.GameObjects.Sprite, tint: number): void;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Get the correct uniform scale for a sprite, using the stored baseScale.
 * This is the pixel scale set at creation — it stays constant across texture
 * swaps so that action sprites (which may be taller) extend upward naturally
 * instead of shrinking the character body.
 */
function getBaseScale(sprite: Phaser.GameObjects.Sprite): number {
  const scale = sprite.getData('baseScale') as number | undefined;
  if (scale) return scale;
  return sprite.scaleY;
}

function shakeSprite(scene: Phaser.Scene, sprite: Phaser.GameObjects.Sprite, intensity: number): void {
  const originalX = sprite.x;
  scene.tweens.add({
    targets: sprite,
    x: originalX + intensity,
    duration: 50,
    yoyo: true,
    repeat: 3,
    onComplete: () => { sprite.x = originalX; },
  });
}

function flashSpriteTint(scene: Phaser.Scene, sprite: Phaser.GameObjects.Sprite, color: number, duration: number): void {
  sprite.setTint(color);
  scene.time.delayedCall(duration, () => {
    sprite.clearTint();
    if (sprite.postFX) sprite.postFX.clear();
    if ('restoreVisual' in scene) {
      (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
    }
  });
}

/** Center-mass Y for a bottom-anchored sprite (origin 0.5, 1.0) */
function spriteCenterY(sprite: Phaser.GameObjects.Sprite): number {
  return sprite.y - sprite.displayHeight * 0.5;
}

function showHitEffect(scene: Phaser.Scene, x: number, y: number, color: number): void {
  const hit = scene.add.sprite(x, y, 'hit-effect');
  hit.setTint(color);
  hit.setAlpha(0.9);
  hit.setScale(0.5);
  hit.setDepth(10000);

  scene.tweens.add({
    targets: hit,
    scaleX: 2,
    scaleY: 2,
    alpha: 0,
    duration: 200,
    ease: 'Power2',
    onComplete: () => hit.destroy(),
  });
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

/** Sweeping slash line that grows from a point, holds, then shrinks — melee physical attacks.
 *  Outer colored stroke + bright white inner core sells the cut at combat scale.
 *  (Single 3px stroke was the prior failure mode — invisible on 200-400px sprites.) */
const slash: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.6;

    // Slash endpoints: top-left to bottom-right
    const x1 = cx - len * 0.5;
    const y1 = cy - len * 0.45;
    const x2 = cx + len * 0.5;
    const y2 = cy + len * 0.45;

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);

    const renderLine = (tail: number, head: number) => {
      gfx.clear();
      const tx0 = x1 + (x2 - x1) * tail;
      const ty0 = y1 + (y2 - y1) * tail;
      const tx1 = x1 + (x2 - x1) * head;
      const ty1 = y1 + (y2 - y1) * head;
      // Outer colored stroke
      gfx.lineStyle(7, tint, 1);
      gfx.beginPath();
      gfx.moveTo(tx0, ty0);
      gfx.lineTo(tx1, ty1);
      gfx.strokePath();
      // Bright white inner core for crispness
      gfx.lineStyle(3, 0xffffff, 1);
      gfx.beginPath();
      gfx.moveTo(tx0, ty0);
      gfx.lineTo(tx1, ty1);
      gfx.strokePath();
    };

    const draw = { head: 0, tail: 0 };

    scene.tweens.add({
      targets: draw,
      head: 1,
      duration: 110,
      ease: 'Power2.easeOut',
      onUpdate: () => renderLine(draw.tail, draw.head),
      onComplete: () => {
        scene.tweens.add({
          targets: draw,
          tail: 1,
          duration: 110,
          ease: 'Power2.easeIn',
          onUpdate: () => renderLine(draw.tail, draw.head),
          onComplete: () => gfx.destroy(),
        });
      },
    });

    shakeSprite(scene, sprite, 5);
    flashSpriteTint(scene, sprite, tint, 120);
  },
};

/** Heavy hit + strong shake + camera shake — heavy melee blows.
 *  Replaces the fixed 32px hit-effect texture with a sprite-scaled expanding
 *  ring + 4 chunk-debris triangles so the hit reads on 200-400px sprites. */
const impact: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const maxR = h * 0.55;

    // Expanding ring (outer colored + white inner core)
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    const ringState = { r: maxR * 0.15, alpha: 1 };
    scene.tweens.add({
      targets: ringState,
      r: maxR,
      alpha: 0,
      duration: 220,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(6, tint, ringState.alpha);
        ring.strokeCircle(cx, cy, ringState.r);
        ring.lineStyle(2, 0xffffff, Math.min(1, ringState.alpha * 1.2));
        ring.strokeCircle(cx, cy, ringState.r);
      },
      onComplete: () => ring.destroy(),
    });

    // 4 chunk-debris triangles flying outward
    for (let i = 0; i < 4; i++) {
      const baseAngle = (Math.PI * 2 * i) / 4 + (Math.random() - 0.5) * 0.6;
      const dist = h * 0.45 + Math.random() * h * 0.15;
      const tx = cx + Math.cos(baseAngle) * dist;
      const ty = cy + Math.sin(baseAngle) * dist;
      const chunk = scene.add.graphics();
      chunk.fillStyle(tint, 1);
      const size = 6 + Math.random() * 3;
      chunk.fillTriangle(0, -size, size, size, -size, size);
      chunk.setPosition(cx, cy);
      chunk.setDepth(10000);
      chunk.rotation = Math.random() * Math.PI * 2;
      scene.tweens.add({
        targets: chunk,
        x: tx,
        y: ty,
        alpha: 0,
        rotation: chunk.rotation + (Math.random() - 0.5) * 4,
        duration: 320,
        ease: 'Power2.easeOut',
        onComplete: () => chunk.destroy(),
      });
    }

    shakeSprite(scene, sprite, 10);
    flashSpriteTint(scene, sprite, tint, 130);
    scene.cameras.main.shake(120, 0.01);
  },
};

/** Expanding particle ring + camera flash — magical damage */
const burst: AnimationPreset = {
  play(scene, sprite, tint) {
    // Camera flash with the tint color
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(120, r, g, b, false);

    // Radial particle burst
    const count = 8;
    const burstCY = spriteCenterY(sprite);
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(tint, 1);
      particle.fillCircle(0, 0, 4);
      particle.setPosition(sprite.x, burstCY);
      particle.setDepth(10000);

      const angle = (Math.PI * 2 * i) / count;
      const distance = 35 + Math.random() * 20;

      scene.tweens.add({
        targets: particle,
        x: sprite.x + Math.cos(angle) * distance,
        y: burstCY + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.5,
        duration: 400,
        delay: i * 20,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 120);
  },
};

/** Upward-floating motes + soft glow on the healed target — healing abilities.
 *  Motes are layered (outer colored disc + white inner core) and sized for
 *  combat scale; 3px dots read as nothing on a 300px sprite. */
const healPulse: AnimationPreset = {
  play(scene, sprite, tint) {
    const healCY = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    // Brief soft glow on the target
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 0, 0, false, 0.1, 8);
      scene.tweens.add({
        targets: glow,
        outerStrength: 3,
        duration: 200,
        yoyo: true,
        hold: 80,
        ease: 'Sine.easeOut',
        onComplete: () => {
          if (sprite.postFX) sprite.postFX.remove(glow);
          if ('restoreVisual' in scene) {
            (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
          }
        },
      });
    }

    // 8 upward motes (larger, layered)
    const count = 8;
    for (let i = 0; i < count; i++) {
      const mote = scene.add.graphics();
      const r = 5 + Math.random() * 2;
      mote.fillStyle(tint, 1);
      mote.fillCircle(0, 0, r);
      mote.fillStyle(0xffffff, 0.85);
      mote.fillCircle(0, 0, r * 0.45);

      const startX = sprite.x + (Math.random() - 0.5) * w * 0.7;
      const startY = healCY + (Math.random() - 0.4) * h * 0.4;
      mote.setPosition(startX, startY);
      mote.setDepth(10000);

      scene.tweens.add({
        targets: mote,
        y: startY - 60 - Math.random() * 30,
        alpha: 0,
        scale: 0.4,
        duration: 700,
        delay: i * 40,
        ease: 'Power1.easeOut',
        onComplete: () => mote.destroy(),
      });
    }

    flashSpriteTint(scene, sprite, tint, 220);
  },
};

/** Brief sprite glow — buff/defensive abilities */
const buffGlow: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 300);
  },
};

/** Pokemon-style DoT tick — full tint flash + small shake + radial sparks */
const dotBurn: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 150);
    shakeSprite(scene, sprite, 4);

    // Small radial particle burst (fewer & tighter than `burst`)
    const count = 4;
    const dotCY = spriteCenterY(sprite);
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(tint, 1);
      particle.fillCircle(0, 0, 3);
      particle.setPosition(sprite.x, dotCY);
      particle.setDepth(10000);

      const angle = (Math.PI * 2 * i) / count;
      const distance = 15 + Math.random() * 10;

      scene.tweens.add({
        targets: particle,
        x: sprite.x + Math.cos(angle) * distance,
        y: dotCY + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.4,
        duration: 300,
        delay: i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  },
};

/** Brief golden shimmer + scale pulse + upward sparkles — buff application */
const buffShimmer: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 200);

    // Quick scale pulse — derive correct scale from baseScale
    const bs = getBaseScale(sprite);
    // Kill any existing scale tweens on this sprite to prevent stacking
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.04,
      scaleY: bs * 1.04,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Small upward sparkle particles
    for (let i = 0; i < 3; i++) {
      const sparkle = scene.add.graphics();
      sparkle.fillStyle(tint, 1);
      sparkle.fillCircle(0, 0, 2);

      const startX = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.5;
      const startY = spriteCenterY(sprite);
      sparkle.setPosition(startX, startY);
      sparkle.setDepth(10000);

      scene.tweens.add({
        targets: sparkle,
        y: startY - 25 - Math.random() * 15,
        alpha: 0,
        duration: 400,
        delay: i * 50,
        ease: 'Power1.easeOut',
        onComplete: () => sparkle.destroy(),
      });
    }
  },
};

/** Dark flash + downward nudge — debuff application */
const debuffFlash: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 150);

    // Slight downward nudge
    const origY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: origY + 3,
      duration: 50,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => { sprite.y = origY; },
    });
  },
};

/** Dramatic charge glow — slow menacing buildup with pulsing glow + scale throb + camera flash.
 *  Used on ENEMIES when their charged attack fires — the "oh no" moment. */
const chargeGlow: AnimationPreset = {
  play(scene, sprite, tint) {
    // Camera flash in tint color (slightly longer)
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(200, r, g, b, false);

    // Slow, intense pulsing glow via PostFX — holds for 1200ms
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 12, 0, false, 0.3, 10);
      scene.time.delayedCall(1200, () => {
        if (sprite.postFX) sprite.postFX.remove(glow);
        if ('restoreVisual' in scene) {
          (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
        }
      });
    }

    // Slow, menacing scale throb — 1.0 → 1.10 → 1.0, repeat 2x
    const bossBase = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bossBase);
    scene.tweens.add({
      targets: sprite,
      scaleX: bossBase * 1.10,
      scaleY: bossBase * 1.10,
      duration: 300,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Longer tint hold — unmistakable warning
    flashSpriteTint(scene, sprite, tint, 800);
  },
};

/** Charge buildup — glow ramp + scale pulse + energy-gather particles spiraling
 *  inward to the caster's chest. Played during CHARGING (cast_start/cast_tick).
 *  Stronger than the original "barely-there" version: charged attacks need to
 *  read as a clear threat at combat scale. The actual hit is still bigger. */
const chargeBuildup: AnimationPreset = {
  play(scene, sprite, tint) {
    const h = sprite.displayHeight;
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);

    // PostFX glow ramp — builds and holds visibly during the cast turn.
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 0, 0, false, 0.1, 10);
      scene.tweens.add({
        targets: glow,
        outerStrength: 0.45,
        duration: 500,
        ease: 'Sine.easeIn',
        onComplete: () => {
          scene.time.delayedCall(400, () => {
            if (sprite.postFX) sprite.postFX.remove(glow);
            if ('restoreVisual' in scene) {
              (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
            }
          });
        },
      });
    }

    // Scale pulse — 1.06x, once. Bigger than the original 1.03 so the windup
    // is visible past the targeted-victim's threat reticle.
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.06,
      scaleY: bs * 1.06,
      duration: 280,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Energy-gather particles — 6 streaks spiraling inward to the chest.
    // Reads as "drawing power" without the boss-tier camera flash of charge_glow.
    const particleCount = 6;
    const startRadius = h * 0.55;
    const innerThickness = Math.max(3, Math.floor(h / 80));
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.4;
      const startX = cx + Math.cos(angle) * startRadius;
      const startY = cy + Math.sin(angle) * startRadius * 0.85;

      const g = scene.add.graphics();
      g.setDepth(sprite.depth + 1);
      const state = { x: startX, y: startY, alpha: 0 };
      const delay = i * 35;

      scene.tweens.add({
        targets: state,
        x: cx,
        y: cy,
        alpha: 1,
        duration: 380,
        delay,
        ease: 'Cubic.easeIn',
        onUpdate: () => {
          if (!g.active) return;
          g.clear();
          // Tail-trail look: outer colored streak + white inner core for crispness.
          g.fillStyle(tint, state.alpha);
          g.fillCircle(state.x, state.y, innerThickness + 1);
          g.fillStyle(0xffffff, state.alpha * 0.7);
          g.fillCircle(state.x, state.y, Math.max(1, innerThickness - 1));
        },
        onComplete: () => g.destroy(),
      });
    }

    // Tint flash on the caster — longer hold to telegraph "something is brewing".
    flashSpriteTint(scene, sprite, tint, 450);
  },
};

/** Target lock — one-shot reticle that fires when an enemy starts charging an
 *  attack at this combatant. Four corner brackets close in on the sprite's
 *  silhouette (snapping into a frame) plus an expanding red ring. Plays once
 *  per cast; the persistent threat pulse on the sprite is the castTarget
 *  visual layer in CombatScene, not this preset. */
const targetLock: AnimationPreset = {
  play(scene, sprite, tint) {
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);

    // Frame the body. Brackets snap from outside the sprite inward to the edge.
    const inset = Math.max(w, h) * 0.08;
    const halfW = w * 0.45 + inset;
    const halfH = h * 0.42 + inset;
    const startSpread = Math.max(w, h) * 0.55;
    const armLen = Math.max(w, h) * 0.18;
    const thickness = Math.max(3, Math.floor(h / 70));

    type Corner = { sx: number; sy: number; ax: number; ay: number };
    const corners: Corner[] = [
      { sx: -1, sy: -1, ax: 1, ay: 1 },   // top-left bracket: arms point right + down
      { sx: 1, sy: -1, ax: -1, ay: 1 },   // top-right
      { sx: -1, sy: 1, ax: 1, ay: -1 },   // bottom-left
      { sx: 1, sy: 1, ax: -1, ay: -1 },   // bottom-right
    ];

    for (const c of corners) {
      const startX = cx + c.sx * (halfW + startSpread);
      const startY = cy + c.sy * (halfH + startSpread);
      const endX = cx + c.sx * halfW;
      const endY = cy + c.sy * halfH;

      const g = scene.add.graphics();
      g.setDepth(sprite.depth + 1);

      const state = { x: startX, y: startY, alpha: 0 };
      scene.tweens.add({
        targets: state,
        x: endX,
        y: endY,
        alpha: 1,
        duration: 220,
        ease: 'Cubic.easeOut',
        onUpdate: () => {
          if (!g.active) return;
          g.clear();
          g.lineStyle(thickness, tint, state.alpha);
          // Horizontal arm pointing inward
          g.beginPath();
          g.moveTo(state.x, state.y);
          g.lineTo(state.x + c.ax * armLen, state.y);
          g.strokePath();
          // Vertical arm pointing inward
          g.beginPath();
          g.moveTo(state.x, state.y);
          g.lineTo(state.x, state.y + c.ay * armLen);
          g.strokePath();
        },
        onComplete: () => {
          // Hold the locked frame briefly, then fade out.
          scene.tweens.add({
            targets: state,
            alpha: 0,
            duration: 280,
            delay: 160,
            onUpdate: () => {
              if (!g.active) return;
              g.clear();
              g.lineStyle(thickness, tint, state.alpha);
              g.beginPath();
              g.moveTo(state.x, state.y);
              g.lineTo(state.x + c.ax * armLen, state.y);
              g.strokePath();
              g.beginPath();
              g.moveTo(state.x, state.y);
              g.lineTo(state.x, state.y + c.ay * armLen);
              g.strokePath();
            },
            onComplete: () => g.destroy(),
          });
        },
      });
    }

    // Red threat ring expanding from the body outline outward — single beat that
    // says "danger here" the moment the lock fires.
    const ring = scene.add.graphics();
    ring.setDepth(sprite.depth + 1);
    const ringStartR = Math.max(halfW, halfH) * 0.85;
    const ringEndR = Math.max(halfW, halfH) * 1.15;
    const ringState = { r: ringStartR, alpha: 0.7 };
    scene.tweens.add({
      targets: ringState,
      r: ringEndR,
      alpha: 0,
      duration: 420,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        if (!ring.active) return;
        ring.clear();
        ring.lineStyle(Math.max(2, thickness - 1), tint, ringState.alpha);
        ring.strokeCircle(cx, cy, ringState.r);
      },
      onComplete: () => ring.destroy(),
    });
  },
};

/** Water gathering charge-up — ground ripples + rising droplets + glow ramp.
 *  Used during cast turns for water spells building up to an impact (e.g. Tidal Crush).
 *  Element-themed cousin of charge_buildup: same understated weight so the hit is the payoff,
 *  but water-specific visual language (swelling at the caster's feet). */
const waterGather: AnimationPreset = {
  play(scene, sprite, tint) {
    const h = sprite.displayHeight;
    const cx = sprite.x;
    const footY = sprite.y; // bottom-anchored origin

    // PostFX glow ramp — slightly stronger than charge_buildup to convey "mass gathering"
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 0, 0, false, 0.08, 8);
      scene.tweens.add({
        targets: glow,
        outerStrength: 0.22,
        duration: 500,
        ease: 'Sine.easeIn',
        onComplete: () => {
          scene.time.delayedCall(200, () => {
            if (sprite.postFX) sprite.postFX.remove(glow);
            if ('restoreVisual' in scene) {
              (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
            }
          });
        },
      });
    }

    // 2 ground ripple rings — horizontal ellipses expanding outward from feet
    const ringBaseW = h * 0.28;
    const ringBaseH = h * 0.08;
    for (let i = 0; i < 2; i++) {
      scene.time.delayedCall(i * 220, () => {
        const ring = scene.add.graphics();
        ring.setDepth(10000);
        ring.lineStyle(3, tint, 0.9);
        ring.strokeEllipse(0, 0, ringBaseW, ringBaseH);
        ring.setPosition(cx, footY - 4);
        scene.tweens.add({
          targets: ring,
          scaleX: 2.8,
          scaleY: 2.8,
          alpha: 0,
          duration: 480,
          ease: 'Sine.easeOut',
          onComplete: () => ring.destroy(),
        });
      });
    }

    // 5 rising water droplets — coloured circles with bright inner highlight
    const dropCount = 5;
    for (let i = 0; i < dropCount; i++) {
      const delay = 80 + i * 70;
      scene.time.delayedCall(delay, () => {
        const spread = h * 0.45;
        const startX = cx + (Math.random() - 0.5) * spread;
        const startY = footY - 6;
        const riseDist = h * (0.5 + Math.random() * 0.2);
        const radius = 3 + Math.random() * 2;

        const drop = scene.add.graphics();
        drop.setDepth(10000);
        drop.fillStyle(tint, 1);
        drop.fillCircle(0, 0, radius);
        drop.fillStyle(0xffffff, 0.8);
        drop.fillCircle(-radius * 0.3, -radius * 0.3, radius * 0.4);
        drop.setPosition(startX, startY);

        scene.tweens.add({
          targets: drop,
          y: startY - riseDist,
          alpha: 0,
          duration: 420,
          ease: 'Sine.easeOut',
          onComplete: () => drop.destroy(),
        });
      });
    }

    // Gentle scale pulse — a touch weightier than charge_buildup to hint at mass
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.04,
      scaleY: bs * 1.04,
      duration: 260,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Tint hold — 500ms to feel like swelling, not snapping
    flashSpriteTint(scene, sprite, tint, 500);
  },
};

/** Devastating impact — boss signature slam. Must read as the biggest thing on
 *  screen. Layered shockwave (concentric ring + bright inner ring + horizontal
 *  ground shockwave), heavy debris triangles, mixed-size particles, long flash,
 *  hard camera shake. Sized to sprite.displayHeight so it scales with the boss. */
const devastatingImpact: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const maxR = h * 0.9;

    // Strong camera flash in tint color
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(240, r, g, b, false);

    // Primary expanding shockwave ring (outer thick + bright inner core)
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    const ringState = { r: maxR * 0.18, alpha: 1 };
    scene.tweens.add({
      targets: ringState,
      r: maxR,
      alpha: 0,
      duration: 360,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(10, tint, ringState.alpha);
        ring.strokeCircle(cx, cy, ringState.r);
        ring.lineStyle(4, 0xffffff, Math.min(1, ringState.alpha * 1.3));
        ring.strokeCircle(cx, cy, ringState.r);
      },
      onComplete: () => ring.destroy(),
    });

    // Second offset shockwave for layered "concussion" feel
    const ring2 = scene.add.graphics();
    ring2.setDepth(10000);
    const ring2State = { r: maxR * 0.1, alpha: 0.7 };
    scene.tweens.add({
      targets: ring2State,
      r: maxR * 0.75,
      alpha: 0,
      duration: 320,
      delay: 90,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        ring2.clear();
        ring2.lineStyle(5, tint, ring2State.alpha);
        ring2.strokeCircle(cx, cy, ring2State.r);
      },
      onComplete: () => ring2.destroy(),
    });

    // Horizontal ground shockwave at the sprite base — sells "the floor cracked"
    const groundY = sprite.y - 4;
    const groundWave = scene.add.graphics();
    groundWave.setDepth(10000);
    const gState = { w: h * 0.2, alpha: 0.95 };
    scene.tweens.add({
      targets: gState,
      w: h * 1.4,
      alpha: 0,
      duration: 380,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        groundWave.clear();
        groundWave.lineStyle(6, tint, gState.alpha);
        groundWave.strokeEllipse(cx, groundY, gState.w, gState.w * 0.28);
        groundWave.lineStyle(2, 0xffffff, Math.min(1, gState.alpha * 1.2));
        groundWave.strokeEllipse(cx, groundY, gState.w, gState.w * 0.28);
      },
      onComplete: () => groundWave.destroy(),
    });

    // 10 radial debris (mixed sizes — 3 large triangles + 7 medium chunks)
    const debrisCount = 10;
    for (let i = 0; i < debrisCount; i++) {
      const angle = (Math.PI * 2 * i) / debrisCount + (Math.random() - 0.5) * 0.35;
      const distance = h * 0.5 + Math.random() * h * 0.25;
      const debris = scene.add.graphics();
      debris.setDepth(10000);
      const isBig = i < 3;
      const size = isBig ? 9 + Math.random() * 2 : 5 + Math.random() * 2;
      debris.fillStyle(tint, 1);
      if (isBig) {
        debris.fillTriangle(0, -size, size, size * 0.8, -size, size * 0.8);
      } else {
        debris.fillCircle(0, 0, size * 0.7);
      }
      // White inner core
      debris.fillStyle(0xffffff, 0.9);
      debris.fillCircle(0, 0, size * 0.3);
      debris.setPosition(cx, cy);
      debris.rotation = Math.random() * Math.PI * 2;

      scene.tweens.add({
        targets: debris,
        x: cx + Math.cos(angle) * distance,
        y: cy + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.4,
        rotation: debris.rotation + (Math.random() - 0.5) * 6,
        duration: 520,
        delay: i * 18,
        ease: 'Power2.easeOut',
        onComplete: () => debris.destroy(),
      });
    }

    // Heavy sprite shake (boss-tier)
    shakeSprite(scene, sprite, 14);
    // Long tint flash so the hit registers
    flashSpriteTint(scene, sprite, tint, 280);
    // Hard camera shake — the floor moved
    scene.cameras.main.shake(260, 0.022);
  },
};

// ---------------------------------------------------------------------------
// Cast animation presets (played on the ATTACKER, not the target)
// ---------------------------------------------------------------------------

export interface CastPreset {
  play(scene: Phaser.Scene, sprite: Phaser.GameObjects.Sprite, direction: number): void;
}

/** Quick lunge toward the enemy side — melee attacks */
const lunge: CastPreset = {
  play(scene, sprite, direction) {
    const origX = sprite.x;
    scene.tweens.add({
      targets: sprite,
      x: origX + 15 * direction,
      duration: 80,
      ease: 'Power2.easeOut',
      yoyo: true,
      hold: 30,
      onComplete: () => { sprite.x = origX; },
    });
  },
};

/** Gentle upward bob — heals and buffs (no horizontal motion toward enemies) */
const step: CastPreset = {
  play(scene, sprite, _direction) {
    const origY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: origY - 4,
      duration: 120,
      ease: 'Sine.easeInOut',
      yoyo: true,
      onComplete: () => { sprite.y = origY; },
    });
  },
};

/** Stationary channel — brief scale pulse for ranged / magic casts */
const channel: CastPreset = {
  play(scene, sprite, _direction) {
    const chanBase = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(chanBase);
    scene.tweens.add({
      targets: sprite,
      scaleX: chanBase * 1.06,
      scaleY: chanBase * 1.06,
      duration: 100,
      ease: 'Sine.easeInOut',
      yoyo: true,
      onComplete: () => {
        sprite.setScale(getBaseScale(sprite));
      },
    });
  },
};

/** Hold pose — no movement, lets texture swap speak for itself (Darkest Dungeon style) */
const pose: CastPreset = {
  play(_scene, _sprite, _direction) {
    // Intentionally empty — the texture swap is the animation
  },
};

/** Dragoon-style jump windup — sprite leaps up + slightly forward, scales smaller (gaining height),
 *  hovers briefly, then drops back into the lunge on the way down. Used for charged jump-attacks like
 *  Skirmistry's Lightning Leap where the cast turn IS the airborne arc. */
const jump: CastPreset = {
  play(scene, sprite, direction) {
    const origX = sprite.x;
    const origY = sprite.y;
    const jumpBase = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    // Phase 1 — leap up and slightly toward the enemy, scaling smaller as we "gain height"
    scene.tweens.add({
      targets: sprite,
      x: origX + 20 * direction,
      y: origY - 60,
      scaleX: jumpBase * 0.78,
      scaleY: jumpBase * 0.78,
      duration: 180,
      ease: 'Sine.easeOut',
      onComplete: () => {
        // Phase 2 — brief hover at apex
        scene.tweens.add({
          targets: sprite,
          y: origY - 64,
          duration: 110,
          ease: 'Sine.easeInOut',
          yoyo: true,
          onComplete: () => {
            // Phase 3 — crash back down and slightly past origin (the impact lunge)
            scene.tweens.add({
              targets: sprite,
              x: origX + 12 * direction,
              y: origY,
              scaleX: jumpBase,
              scaleY: jumpBase,
              duration: 130,
              ease: 'Power3.easeIn',
              onComplete: () => {
                // Settle back to origin
                scene.tweens.add({
                  targets: sprite,
                  x: origX,
                  duration: 90,
                  ease: 'Power2.easeOut',
                  onComplete: () => {
                    sprite.x = origX;
                    sprite.y = origY;
                    sprite.setScale(getBaseScale(sprite));
                  },
                });
              },
            });
          },
        });
      },
    });
  },
};

export const CAST_PRESETS: Record<string, CastPreset> = {
  lunge,
  step,
  channel,
  pose,
  jump,
};

const DEFAULT_CAST = 'lunge';

/** Play a cast motion on the attacker sprite. */
export function playCastPreset(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  castMotion: string,
  isEnemy: boolean,
): void {
  const preset = CAST_PRESETS[castMotion] ?? CAST_PRESETS[DEFAULT_CAST];
  // Players lunge right (+1), enemies lunge left (-1)
  const direction = isEnemy ? -1 : 1;
  preset.play(scene, sprite, direction);
}

/** Brief glow on the caster during cast phase. */
export function flashCasterGlow(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  tint: number,
): void {
  if (sprite.postFX) {
    const glow = sprite.postFX.addGlow(tint, 6, 0, false, 0.15, 10);
    scene.time.delayedCall(200, () => {
      if (sprite.postFX) sprite.postFX.remove(glow);
      if ('restoreVisual' in scene) {
        (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
      }
    });
  }
}

/** Sharp inward line stab + light shake + backward nudge — ranged projectile hits */
const pierce: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.3;

    // Line stabs inward from upper-left toward center
    const x1 = cx - len * 0.6;
    const y1 = cy - len * 0.3;
    const x2 = cx + len * 0.1;
    const y2 = cy + len * 0.1;

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const draw = { head: 0, alpha: 1 };

    // Phase 1: line draws on (60ms)
    scene.tweens.add({
      targets: draw,
      head: 1,
      duration: 60,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        gfx.clear();
        gfx.lineStyle(2, tint, draw.alpha);
        gfx.beginPath();
        gfx.moveTo(x1, y1);
        gfx.lineTo(x1 + (x2 - x1) * draw.head, y1 + (y2 - y1) * draw.head);
        gfx.strokePath();
      },
      onComplete: () => {
        // Phase 2: fade out (80ms)
        scene.tweens.add({
          targets: draw,
          alpha: 0,
          duration: 80,
          onUpdate: () => {
            gfx.clear();
            gfx.lineStyle(2, tint, draw.alpha);
            gfx.beginPath();
            gfx.moveTo(x1, y1);
            gfx.lineTo(x2, y2);
            gfx.strokePath();
          },
          onComplete: () => gfx.destroy(),
        });
      },
    });

    shakeSprite(scene, sprite, 3);
    flashSpriteTint(scene, sprite, tint, 80);

    // Small backward nudge
    const origY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: origY + 2,
      duration: 50,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => { sprite.y = origY; },
    });
  },
};

/** Extending/retracting point — melee thrust puncture */
const stab: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const draw = { extend: 0 };

    // Phase 1: extend line outward from center (80ms)
    scene.tweens.add({
      targets: draw,
      extend: 1,
      duration: 80,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        gfx.clear();
        const lineLen = draw.extend * 25;
        const radius = 2 + draw.extend;
        gfx.fillStyle(tint, 1);
        gfx.fillCircle(cx, cy, radius);
        gfx.lineStyle(2, tint, 1);
        gfx.beginPath();
        gfx.moveTo(cx, cy);
        gfx.lineTo(cx + lineLen, cy);
        gfx.strokePath();
      },
      onComplete: () => {
        // Phase 2: retract back to point (60ms)
        scene.tweens.add({
          targets: draw,
          extend: 0,
          duration: 60,
          ease: 'Power2.easeIn',
          onUpdate: () => {
            gfx.clear();
            const lineLen = draw.extend * 25;
            const radius = 2 + draw.extend;
            gfx.fillStyle(tint, 1);
            gfx.fillCircle(cx, cy, radius);
            if (lineLen > 0.5) {
              gfx.lineStyle(2, tint, 1);
              gfx.beginPath();
              gfx.moveTo(cx, cy);
              gfx.lineTo(cx + lineLen, cy);
              gfx.strokePath();
            }
          },
          onComplete: () => gfx.destroy(),
        });
      },
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 100);
  },
};

/** Knuckle-row impact — 3 stacked contact bursts arranged vertically at the hit point,
 *  reading as a fist landing across the upper / mid / lower body. Reusable for any pugilism
 *  punch (Authane's jab and hook, the Caligari Open Path jab_cross family, future bare-handed
 *  brawlers). Tint carries the punch heat; visible escalation between ranks rides on the
 *  AnimationDef screenShake field, not the preset internals. */
const knuckleStrike: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const spacing = h * 0.09;
    const knuckleYs = [cy - spacing, cy, cy + spacing];

    for (let i = 0; i < knuckleYs.length; i++) {
      const ky = knuckleYs[i];
      const delay = i * 22;

      const outer = scene.add.graphics();
      outer.setDepth(10000);
      const outerState = { r: h * 0.04, alpha: 1 };
      scene.tweens.add({
        targets: outerState,
        r: h * 0.14,
        alpha: 0,
        duration: 180,
        delay,
        ease: 'Power2.easeOut',
        onUpdate: () => {
          outer.clear();
          outer.lineStyle(4, tint, outerState.alpha);
          outer.strokeCircle(cx, ky, outerState.r);
        },
        onComplete: () => outer.destroy(),
      });

      const core = scene.add.graphics();
      core.setDepth(10001);
      const coreState = { r: h * 0.025, alpha: 1 };
      scene.tweens.add({
        targets: coreState,
        r: h * 0.06,
        alpha: 0,
        duration: 140,
        delay,
        ease: 'Power2.easeOut',
        onUpdate: () => {
          core.clear();
          core.fillStyle(0xffffff, coreState.alpha);
          core.fillCircle(cx, ky, coreState.r);
        },
        onComplete: () => core.destroy(),
      });

      for (let s = 0; s < 3; s++) {
        const baseAngle = -Math.PI / 2 + (s - 1) * 1.2;
        const sparkLen = h * 0.08 + Math.random() * h * 0.04;
        const ex = cx + Math.cos(baseAngle) * sparkLen;
        const ey = ky + Math.sin(baseAngle) * sparkLen;
        const spark = scene.add.graphics();
        spark.setDepth(10000);
        const sparkState = { progress: 0, alpha: 1 };
        scene.tweens.add({
          targets: sparkState,
          progress: 1,
          alpha: 0,
          duration: 160,
          delay,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            spark.clear();
            spark.lineStyle(2, tint, sparkState.alpha);
            spark.beginPath();
            const startX = cx + (ex - cx) * 0.3 * sparkState.progress;
            const startY = ky + (ey - ky) * 0.3 * sparkState.progress;
            const endX = cx + (ex - cx) * sparkState.progress;
            const endY = ky + (ey - ky) * sparkState.progress;
            spark.moveTo(startX, startY);
            spark.lineTo(endX, endY);
            spark.strokePath();
          },
          onComplete: () => spark.destroy(),
        });
      }
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 110);
  },
};

/** Jaws snapping shut — two fang-wedges converge vertically, flash, retract.
 *  Reusable for any biting predator (crocs, wolves, piranhas, hounds). */
const snapBite: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const size = sprite.displayHeight * 0.45;
    const fangW = size * 0.55;
    const fangH = size * 0.55;
    const startGap = size * 0.9;

    const topGfx = scene.add.graphics();
    const botGfx = scene.add.graphics();
    topGfx.setDepth(10000);
    botGfx.setDepth(10000);

    const drawFangs = (topY: number, botY: number, alpha: number) => {
      topGfx.clear();
      botGfx.clear();
      // Top fang-row: wide base at top, points down
      topGfx.fillStyle(tint, alpha);
      topGfx.fillTriangle(cx - fangW, topY - fangH, cx + fangW, topY - fangH, cx, topY);
      topGfx.fillStyle(0xffffff, alpha * 0.6);
      topGfx.fillTriangle(cx - fangW * 0.4, topY - fangH * 0.5, cx + fangW * 0.4, topY - fangH * 0.5, cx, topY);
      // Bottom fang-row: wide base at bottom, points up
      botGfx.fillStyle(tint, alpha);
      botGfx.fillTriangle(cx - fangW, botY + fangH, cx + fangW, botY + fangH, cx, botY);
      botGfx.fillStyle(0xffffff, alpha * 0.6);
      botGfx.fillTriangle(cx - fangW * 0.4, botY + fangH * 0.5, cx + fangW * 0.4, botY + fangH * 0.5, cx, botY);
    };

    const anim = { gap: startGap, alpha: 1 };
    drawFangs(cy - anim.gap, cy + anim.gap, anim.alpha);

    // Phase 1: Snap shut (90ms, fast ease-in)
    scene.tweens.add({
      targets: anim,
      gap: 0,
      duration: 90,
      ease: 'Power3.easeIn',
      onUpdate: () => drawFangs(cy - anim.gap, cy + anim.gap, anim.alpha),
      onComplete: () => {
        // Impact frame: shake + spray + camera shake
        shakeSprite(scene, sprite, 7);
        flashSpriteTint(scene, sprite, tint, 120);
        scene.cameras.main.shake(60, 0.004);

        // 5 droplets spray sideways (tint-colored — blood for physical, water for water)
        for (let i = 0; i < 5; i++) {
          const angle = (Math.random() - 0.5) * Math.PI * 1.2;
          const speed = 40 + Math.random() * 30;
          const dx = Math.cos(angle) * speed * (i % 2 === 0 ? 1 : -1);
          const dy = -Math.abs(Math.sin(angle)) * speed * 0.5;
          const drop = scene.add.graphics();
          drop.fillStyle(tint, 1);
          drop.fillCircle(0, 0, 3);
          drop.setPosition(cx, cy);
          drop.setDepth(10000);
          scene.tweens.add({
            targets: drop,
            x: cx + dx,
            y: cy + dy + 30,
            alpha: 0,
            duration: 220,
            ease: 'Power2.easeOut',
            onComplete: () => drop.destroy(),
          });
        }

        // Phase 2: Retract — fangs recoil apart slightly while fading (120ms)
        scene.tweens.add({
          targets: anim,
          gap: size * 0.3,
          alpha: 0,
          duration: 120,
          ease: 'Power1.easeOut',
          onUpdate: () => drawFangs(cy - anim.gap, cy + anim.gap, anim.alpha),
          onComplete: () => {
            topGfx.destroy();
            botGfx.destroy();
          },
        });
      },
    });
  },
};

/** Dark magic implosion — particles converge inward then release outward */
const shadowBurst: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);

    // Phase 1: 6 particles implode inward (200ms)
    const implodeCount = 6;
    let completed = 0;
    for (let i = 0; i < implodeCount; i++) {
      const angle = (Math.PI * 2 * i) / implodeCount;
      const startX = cx + Math.cos(angle) * 40;
      const startY = cy + Math.sin(angle) * 40;

      const particle = scene.add.graphics();
      particle.fillStyle(tint, 1);
      particle.fillCircle(0, 0, 3);
      particle.setPosition(startX, startY);
      particle.setDepth(10000);

      scene.tweens.add({
        targets: particle,
        x: cx,
        y: cy,
        alpha: 0.6,
        duration: 200,
        delay: i * 15,
        ease: 'Power2.easeIn',
        onComplete: () => {
          particle.destroy();
          completed++;
          // When all implode particles done, release burst
          if (completed === implodeCount) {
            // Phase 2: 4 particles burst outward (150ms)
            for (let j = 0; j < 4; j++) {
              const releaseAngle = (Math.PI * 2 * j) / 4 + Math.PI / 4;
              const rParticle = scene.add.graphics();
              rParticle.fillStyle(tint, 1);
              rParticle.fillCircle(0, 0, 2);
              rParticle.setPosition(cx, cy);
              rParticle.setDepth(10000);

              scene.tweens.add({
                targets: rParticle,
                x: cx + Math.cos(releaseAngle) * 20,
                y: cy + Math.sin(releaseAngle) * 20,
                alpha: 0,
                duration: 150,
                ease: 'Power2.easeOut',
                onComplete: () => rParticle.destroy(),
              });
            }

            // Brief camera flash
            const r = (tint >> 16) & 0xff;
            const g = (tint >> 8) & 0xff;
            const b = tint & 0xff;
            scene.cameras.main.flash(80, r, g, b, false);
          }
        },
      });
    }

    shakeSprite(scene, sprite, 5);
    flashSpriteTint(scene, sprite, tint, 120);
  },
};

/** Three thin dark slivers converge on the target, then a tiny 4-particle puff.
 *  Sub-trash "weak dark hit" — pesky pinprick. Distinct from `shadow_burst`'s
 *  full implode-then-release ritual. Reusable for any swarm/wisp dark attack. */
const shadowSting: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = Math.max(70, sprite.displayHeight * 0.45);
    const sliverLen = Math.max(28, sprite.displayHeight * 0.26);
    const sliverThick = 5;

    // Four slivers from spread angles converge on target — readable at combat scale.
    const angles = [Math.PI * 1.20, Math.PI * 1.80, Math.PI * 0.45, Math.PI * 0.55];
    let arrived = 0;
    for (let i = 0; i < angles.length; i++) {
      const a = angles[i];
      const startX = cx + Math.cos(a) * reach;
      const startY = cy + Math.sin(a) * reach;

      const pointerAngle = Math.atan2(cy - startY, cx - startX);

      const sliver = scene.add.graphics();
      // Outer halo (broad tint band) so the needle reads even through dim sprites.
      sliver.fillStyle(tint, 0.55);
      sliver.fillRect(-sliverLen / 2, -(sliverThick + 2) / 2, sliverLen, sliverThick + 2);
      // Tinted body
      sliver.fillStyle(tint, 1);
      sliver.fillRect(-sliverLen / 2, -sliverThick / 2, sliverLen, sliverThick);
      // White-hot core
      sliver.fillStyle(0xffffff, 0.95);
      sliver.fillRect(-sliverLen / 3, -1.5, (sliverLen * 2) / 3, 3);
      sliver.setPosition(startX, startY);
      sliver.setRotation(pointerAngle);
      sliver.setDepth(10000);
      sliver.setAlpha(0);

      scene.tweens.add({
        targets: sliver,
        x: cx,
        y: cy,
        alpha: { from: 0, to: 1 },
        duration: 150,
        delay: i * 35,
        ease: 'Power3.easeIn',
        onComplete: () => {
          sliver.destroy();
          arrived++;
          if (arrived === angles.length) {
            // Convergence burst — bright center pop + 6 outward particles so the
            // hit lands. (Old preset's tiny puff disappeared at combat scale.)
            const flash = scene.add.circle(cx, cy, 4, 0xffffff, 0.95);
            flash.setDepth(10001);
            scene.tweens.add({
              targets: flash,
              scaleX: 5,
              scaleY: 5,
              alpha: 0,
              duration: 180,
              ease: 'Power2.easeOut',
              onComplete: () => flash.destroy(),
            });

            for (let j = 0; j < 6; j++) {
              const puffAngle = (Math.PI * 2 * j) / 6 + Math.PI / 6;
              const p = scene.add.graphics();
              p.fillStyle(tint, 1);
              p.fillCircle(0, 0, 3);
              p.setPosition(cx, cy);
              p.setDepth(10000);
              scene.tweens.add({
                targets: p,
                x: cx + Math.cos(puffAngle) * 24,
                y: cy + Math.sin(puffAngle) * 24,
                alpha: 0,
                duration: 180,
                ease: 'Power2.easeOut',
                onComplete: () => p.destroy(),
              });
            }
          }
        },
      });
    }

    shakeSprite(scene, sprite, 5);
    flashSpriteTint(scene, sprite, tint, 160);
  },
};

/** Ground-rising particles + glow ramp — summoning / raise effects */
const summonRise: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y; // bottom of sprite (origin 0.5, 1.0)
    const cy = spriteCenterY(sprite);

    // 8 particles rise from below sprite
    const count = 8;
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      const radius = 2 + Math.random() * 2;
      particle.fillStyle(tint, 1);
      particle.fillCircle(0, 0, radius);

      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.6;
      particle.setPosition(startX, baseY + 5);
      particle.setDepth(10000);
      particle.setAlpha(0);

      scene.tweens.add({
        targets: particle,
        x: startX + (Math.random() - 0.5) * 15,
        y: cy - 10 - Math.random() * 20,
        alpha: { from: 0.8, to: 0 },
        duration: 400,
        delay: i * 30,
        ease: 'Power1.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    // PostFX glow ramp
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 0, 0, false, 0.1, 8);
      // Ramp up over 300ms
      scene.tweens.add({
        targets: glow,
        outerStrength: 0.2,
        duration: 300,
        ease: 'Sine.easeIn',
        onComplete: () => {
          // Hold 200ms then clear
          scene.time.delayedCall(200, () => {
            if (sprite.postFX) sprite.postFX.remove(glow);
            if ('restoreVisual' in scene) {
              (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
            }
          });
        },
      });
    }

    // Scale tween: materializing
    const origScaleX = sprite.scaleX;
    const origScaleY = sprite.scaleY;
    sprite.setScale(origScaleX * 0.95, origScaleY * 0.95);
    scene.tweens.add({
      targets: sprite,
      scaleX: origScaleX,
      scaleY: origScaleY,
      duration: 500,
      ease: 'Sine.easeOut',
    });

    // Long tint flash (no shake — eerie, not violent)
    flashSpriteTint(scene, sprite, tint, 300);
  },
};

/** Lazy-drifting spore particles that float upward and spread — toxic/fungal AoE */
const sporeCloud: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);

    // 6 particles drift lazily upward and outward
    const count = 6;
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      const radius = 3 + Math.random() * 2;
      particle.fillStyle(tint, 0.8);
      particle.fillCircle(0, 0, radius);

      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.3;
      const startY = cy + (Math.random() - 0.5) * 10;
      particle.setPosition(startX, startY);
      particle.setDepth(10000);

      const driftX = startX + (Math.random() - 0.5) * 30;
      const driftY = startY - 20 - Math.random() * 25;

      scene.tweens.add({
        targets: particle,
        x: driftX,
        y: driftY,
        alpha: 0,
        scale: 1.4,
        duration: 500 + Math.random() * 200,
        delay: i * 40,
        ease: 'Sine.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 150);
  },
};

/** Blood-red DoT tick — tint flash + light shake + downward-dripping particles */
const dotBleed: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 150);
    shakeSprite(scene, sprite, 3);

    // Downward-dripping blood particles (gravity-affected)
    const count = 4;
    const bleedCY = spriteCenterY(sprite);
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(tint, 1);
      particle.fillCircle(0, 0, 2);

      const startX = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.4;
      particle.setPosition(startX, bleedCY);
      particle.setDepth(10000);

      scene.tweens.add({
        targets: particle,
        x: startX + (Math.random() - 0.5) * 8,
        y: bleedCY + 20 + Math.random() * 15,
        alpha: 0,
        scale: 0.3,
        duration: 400,
        delay: i * 30,
        ease: 'Power2.easeIn',
        onComplete: () => particle.destroy(),
      });
    }
  },
};

/** Bellowing shockwave — triple-ring expansion + ground dust + heavy shake.
 *  Roars and warcries. Plays on the caster (self-targeted buff). */
const shoutWave: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const baseY = sprite.y;
    const maxRadius = sprite.displayHeight * 1.05;

    // Three concentric rings at staggered timing — feels like compounding pressure waves
    for (let i = 0; i < 3; i++) {
      scene.time.delayedCall(i * 80, () => {
        const gfx = scene.add.graphics();
        gfx.setDepth(10000);
        const ring = { radius: 18, alpha: 0.9 };
        scene.tweens.add({
          targets: ring,
          radius: maxRadius,
          alpha: 0,
          duration: 480,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            gfx.clear();
            gfx.lineStyle(7, tint, ring.alpha);
            gfx.strokeCircle(cx, cy, ring.radius);
          },
          onComplete: () => gfx.destroy(),
        });
      });
    }

    // Ground dust particles kicking outward in a radial burst
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const distance = 45 + Math.random() * 25;
      const particle = scene.add.circle(cx, baseY - 4, 4 + Math.random() * 3, tint, 0.85);
      particle.setDepth(10000);
      scene.tweens.add({
        targets: particle,
        x: cx + Math.cos(angle) * distance,
        y: baseY - 8 - Math.random() * 18,
        alpha: 0,
        duration: 420,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 300);
    scene.cameras.main.shake(220, 0.012);
  },
};

/** Screen shake + dust particles rising from sprite base — earthquake tremor */
const groundRumble: AnimationPreset = {
  play(scene, sprite, tint) {
    const baseY = sprite.y;
    const cx = sprite.x;
    // Dust particles rising from ground
    for (let i = 0; i < 6; i++) {
      const px = cx + (Math.random() - 0.5) * sprite.displayWidth * 1.2;
      const py = baseY - Math.random() * 10;
      const particle = scene.add.circle(px, py, 3 + Math.random() * 3, tint, 0.7);
      particle.setDepth(10000);
      scene.tweens.add({
        targets: particle,
        y: py - 20 - Math.random() * 30,
        alpha: 0,
        duration: 300 + Math.random() * 200,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 150);
    scene.cameras.main.shake(200, 0.012);
  },
};

/** Stone Resonance: expanding ring shockwave at target base + dust puff + sprite shake.
 *  Reads as "the radial pulse from the Tremor Totem reached me." Per-target hit; no per-target
 *  camera shake (AoE-wide camera shake handled at the AnimationDef level). */
const stoneResonance: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y - 2;
    const reach = sprite.displayHeight * 0.55;

    // Expanding ring outline at target base — the shockwave arriving
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    ring.lineStyle(5, tint, 0.85);
    ring.strokeEllipse(0, 0, reach * 2, reach * 0.55);
    ring.setPosition(cx, baseY);
    ring.setScale(0.1);
    scene.tweens.add({
      targets: ring,
      scale: 1,
      alpha: { from: 0.9, to: 0 },
      duration: 280,
      ease: 'Power2.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Dust burst — 6 small earth-toned particles puffing upward at the ring's contact line
    for (let i = 0; i < 6; i++) {
      const angle = Math.PI + (i / 5) * Math.PI; // lower hemisphere fan
      const startX = cx + Math.cos(angle) * reach * 0.45;
      const startY = baseY + Math.sin(angle) * reach * 0.18;
      const particle = scene.add.graphics();
      const radius = 3 + Math.random() * 2;
      particle.fillStyle(tint, 1);
      particle.fillCircle(0, 0, radius);
      particle.setPosition(startX, startY);
      particle.setDepth(10001);

      scene.tweens.add({
        targets: particle,
        x: startX + (Math.random() - 0.5) * 12,
        y: startY - 18 - Math.random() * 16,
        alpha: { from: 0.85, to: 0 },
        duration: 260,
        delay: 60 + i * 20,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    shakeSprite(scene, sprite, 5);
    flashSpriteTint(scene, sprite, tint, 180);
  },
};

/** Sprite fades to translucent then restores — disappearing into shadow */
const vanishFade: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 300);
    const originalAlpha = sprite.alpha;
    scene.tweens.add({
      targets: sprite,
      alpha: 0.2,
      duration: 200,
      ease: 'Power2.easeIn',
      yoyo: true,
      hold: 200,
      onComplete: () => { sprite.alpha = originalAlpha; },
    });
  },
};

/** Full screen white flash that fades — flashbang */
const flashBlind: AnimationPreset = {
  play(scene, sprite, tint) {
    const flash = scene.add.rectangle(
      scene.cameras.main.centerX, scene.cameras.main.centerY,
      scene.cameras.main.width * 2, scene.cameras.main.height * 2,
      0xffffff, 0.7
    );
    flash.setDepth(20000);
    flash.setScrollFactor(0);
    scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 500,
      ease: 'Power2.easeIn',
      onComplete: () => flash.destroy(),
    });
    shakeSprite(scene, sprite, 5);
    flashSpriteTint(scene, sprite, tint, 200);
    scene.cameras.main.shake(100, 0.008);
  },
};

/** Instant sharp impact flash with crisp shake — firearm crack */
const gunshot: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sH = sprite.displayHeight;

    // Powder smoke puff (rendered first, drifts behind everything else)
    const smoke = scene.add.ellipse(cx, cy, sH * 0.32, sH * 0.22, 0x6a6a6a, 0.55);
    smoke.setDepth(9999);
    scene.tweens.add({
      targets: smoke,
      scaleX: 1.7,
      scaleY: 1.7,
      alpha: 0,
      duration: 280,
      ease: 'Power1.easeOut',
      onComplete: () => smoke.destroy(),
    });

    // Hot impact flash — orange outer + white-hot inner core
    const flashOuter = scene.add.circle(cx, cy, sH * 0.18, tint, 0.95);
    flashOuter.setDepth(10000);
    const flashInner = scene.add.circle(cx, cy, sH * 0.08, 0xffffee, 1);
    flashInner.setDepth(10001);
    scene.tweens.add({
      targets: [flashOuter, flashInner],
      scaleX: 0.1,
      scaleY: 0.1,
      alpha: 0,
      duration: 130,
      ease: 'Power3.easeOut',
      onComplete: () => { flashOuter.destroy(); flashInner.destroy(); },
    });

    // Shrapnel chips — angular fragments spraying radially (cracked-armor motif from the icon)
    const chipCount = 5;
    for (let i = 0; i < chipCount; i++) {
      const chip = scene.add.graphics();
      chip.fillStyle(0xb0b0b0, 1);
      chip.lineStyle(1, 0x3a3a3a, 1);
      chip.fillTriangle(-4, -3, 5, 0, -3, 4);
      chip.strokeTriangle(-4, -3, 5, 0, -3, 4);
      chip.setPosition(cx, cy);
      chip.setDepth(10002);
      const angle = (Math.PI * 2 * i) / chipCount + (Math.random() - 0.5) * 0.5;
      const distance = sH * 0.28 + Math.random() * 14;
      scene.tweens.add({
        targets: chip,
        x: cx + Math.cos(angle) * distance,
        y: cy + Math.sin(angle) * distance,
        rotation: (Math.random() - 0.5) * Math.PI * 2,
        alpha: 0,
        duration: 300,
        ease: 'Power2.easeOut',
        onComplete: () => chip.destroy(),
      });
    }

    // Recoil nudge — energy transferred to target
    const origX = sprite.x;
    scene.tweens.add({
      targets: sprite,
      x: origX + 5,
      duration: 35,
      yoyo: true,
      repeat: 1,
      onComplete: () => { sprite.x = origX; },
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 100);
  },
};

/** Multiple quick impact marks in rapid sequence — unarmed multi-hit combos */
const rapidStrikes: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const reach = h * 0.38;

    // 3 fast diagonal jab-strokes converging on target from different angles.
    // Each stroke = colored line + white inner core + small impact star at the hit point.
    const angles = [
      -Math.PI * 0.75,  // upper-left into center
      Math.PI * 0.15,   // right side into center
      -Math.PI * 0.30,  // upper-right into center
    ];

    angles.forEach((angle, i) => {
      scene.time.delayedCall(i * 75, () => {
        const startX = cx + Math.cos(angle) * reach;
        const startY = cy + Math.sin(angle) * reach;
        const endX = cx + Math.cos(angle) * (reach * 0.18);
        const endY = cy + Math.sin(angle) * (reach * 0.18);

        const stroke = scene.add.graphics();
        stroke.setDepth(10000);
        stroke.lineStyle(5, tint, 1);
        stroke.beginPath();
        stroke.moveTo(startX, startY);
        stroke.lineTo(endX, endY);
        stroke.strokePath();
        stroke.lineStyle(2, 0xffffff, 1);
        stroke.beginPath();
        stroke.moveTo(startX, startY);
        stroke.lineTo(endX, endY);
        stroke.strokePath();

        // 4-point impact star at strike point
        const star = scene.add.graphics();
        star.setDepth(10001);
        const armLen = h * 0.09;
        star.lineStyle(3, tint, 1);
        for (let k = 0; k < 4; k++) {
          const a = (Math.PI * 2 * k) / 4 + Math.PI / 4;
          star.beginPath();
          star.moveTo(endX, endY);
          star.lineTo(endX + Math.cos(a) * armLen, endY + Math.sin(a) * armLen);
          star.strokePath();
        }
        star.lineStyle(1, 0xffffff, 1);
        for (let k = 0; k < 4; k++) {
          const a = (Math.PI * 2 * k) / 4 + Math.PI / 4;
          star.beginPath();
          star.moveTo(endX, endY);
          star.lineTo(endX + Math.cos(a) * armLen, endY + Math.sin(a) * armLen);
          star.strokePath();
        }

        scene.tweens.add({
          targets: [stroke, star],
          alpha: 0,
          duration: 200,
          ease: 'Power2.easeOut',
          onComplete: () => { stroke.destroy(); star.destroy(); },
        });
        shakeSprite(scene, sprite, 3);
      });
    });

    flashSpriteTint(scene, sprite, tint, 240);
  },
};

/** Bone Crackers — multi-hit pugilism Break tool. 3 staggered impacts each leave a
 *  cross-mark + 4 jagged radiating crack lines. Final hit larger + heavier shake.
 *  Distinct from `rapid_strikes` (light jabs) — cracks read as "armor is breaking." */
const boneCrackers: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;

    const positions = [
      { x: cx - h * 0.16, y: cy - h * 0.10, final: false },
      { x: cx + h * 0.18, y: cy + h * 0.06, final: false },
      { x: cx - h * 0.04, y: cy + h * 0.20, final: true },
    ];

    positions.forEach((pos, i) => {
      scene.time.delayedCall(i * 110, () => {
        const baseSize = h * (pos.final ? 0.36 : 0.24);

        // Cross-shaped impact mark
        const cross = scene.add.graphics();
        cross.setDepth(10000);
        cross.lineStyle(6, tint, 1);
        cross.beginPath();
        cross.moveTo(pos.x - baseSize / 2, pos.y);
        cross.lineTo(pos.x + baseSize / 2, pos.y);
        cross.moveTo(pos.x, pos.y - baseSize / 2);
        cross.lineTo(pos.x, pos.y + baseSize / 2);
        cross.strokePath();
        cross.lineStyle(2, 0xffffff, 1);
        cross.beginPath();
        cross.moveTo(pos.x - baseSize / 2, pos.y);
        cross.lineTo(pos.x + baseSize / 2, pos.y);
        cross.moveTo(pos.x, pos.y - baseSize / 2);
        cross.lineTo(pos.x, pos.y + baseSize / 2);
        cross.strokePath();

        // 4 jagged 2-segment crack lines radiating from impact
        const cracks = scene.add.graphics();
        cracks.setDepth(10000);
        for (let k = 0; k < 4; k++) {
          const angle = (Math.PI * 2 * k) / 4 + (Math.random() - 0.5) * 0.4;
          const len = h * (pos.final ? 0.26 : 0.16);
          const midX = pos.x + Math.cos(angle) * len * 0.55 + (Math.random() - 0.5) * 8;
          const midY = pos.y + Math.sin(angle) * len * 0.55 + (Math.random() - 0.5) * 8;
          const ex = pos.x + Math.cos(angle) * len;
          const ey = pos.y + Math.sin(angle) * len;

          cracks.lineStyle(3, tint, 1);
          cracks.beginPath();
          cracks.moveTo(pos.x, pos.y);
          cracks.lineTo(midX, midY);
          cracks.lineTo(ex, ey);
          cracks.strokePath();
          cracks.lineStyle(1, 0xffffff, 0.9);
          cracks.beginPath();
          cracks.moveTo(pos.x, pos.y);
          cracks.lineTo(midX, midY);
          cracks.lineTo(ex, ey);
          cracks.strokePath();
        }

        scene.tweens.add({
          targets: [cross, cracks],
          alpha: 0,
          duration: pos.final ? 340 : 240,
          ease: 'Power2.easeOut',
          onComplete: () => { cross.destroy(); cracks.destroy(); },
        });

        shakeSprite(scene, sprite, pos.final ? 7 : 5);
      });
    });

    flashSpriteTint(scene, sprite, tint, 300);
  },
};

/** Palm Strike Impact — flat percussive palm push. Wide horizontal shockwave ellipse
 *  + sideways air-trail dashes. Distinct from `impact` (round/heavy crush) by being
 *  flat and broad — sells the "open palm pushes them back" feel. */
const palmStrikeImpact: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;

    // Flat shockwave: horizontal ellipse expanding wider than tall
    const wave = scene.add.graphics();
    wave.setDepth(10000);
    const ws = { rx: h * 0.18, ry: h * 0.09, alpha: 1 };
    scene.tweens.add({
      targets: ws,
      rx: h * 0.65,
      ry: h * 0.20,
      alpha: 0,
      duration: 280,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        wave.clear();
        wave.lineStyle(7, tint, ws.alpha);
        wave.strokeEllipse(cx, cy, ws.rx * 2, ws.ry * 2);
        wave.lineStyle(3, 0xffffff, Math.min(1, ws.alpha * 1.3));
        wave.strokeEllipse(cx, cy, ws.rx * 2, ws.ry * 2);
      },
      onComplete: () => wave.destroy(),
    });

    // 6 short wind-trail dashes streaming sideways (3 per side)
    for (let i = 0; i < 6; i++) {
      const side = i < 3 ? 1 : -1;
      const idx = i % 3;
      const startX = cx + side * (h * 0.12);
      const startY = cy + (idx - 1) * h * 0.12;
      const dashLen = h * 0.22;
      const dash = scene.add.graphics();
      dash.setDepth(10001);
      dash.lineStyle(4, tint, 0.95);
      dash.beginPath();
      dash.moveTo(0, 0);
      dash.lineTo(side * dashLen, 0);
      dash.strokePath();
      dash.lineStyle(2, 0xffffff, 0.9);
      dash.beginPath();
      dash.moveTo(0, 0);
      dash.lineTo(side * dashLen, 0);
      dash.strokePath();
      dash.setPosition(startX, startY);
      scene.tweens.add({
        targets: dash,
        x: startX + side * h * 0.22,
        alpha: 0,
        duration: 280,
        delay: idx * 30,
        ease: 'Power2.easeOut',
        onComplete: () => dash.destroy(),
      });
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 220);
    scene.cameras.main.shake(110, 0.007);
  },
};

/** Haymaker Hook — charged knockout punch. Wide arc trail sweeps in, then a central
 *  cross-burst at impact + 10 triangle particles fanning outward + heavy camera shake.
 *  Distinct from `devastating_impact` (mace-smash) by leading with the curved hook arc
 *  before contact — sells a fist swinging in a wide arc rather than dropping straight down. */
const haymakerHook: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;

    const arcRadius = h * 0.5;
    const arcStart = -Math.PI * 0.85;
    const arcEnd = Math.PI * 0.15;
    const sweep = arcEnd - arcStart;

    // Sweeping arc trail (the hook's path)
    const swoosh = scene.add.graphics();
    swoosh.setDepth(10000);
    const swState = { progress: 0 };
    scene.tweens.add({
      targets: swState,
      progress: 1,
      duration: 180,
      ease: 'Power2.easeIn',
      onUpdate: () => {
        swoosh.clear();
        const curEnd = arcStart + sweep * swState.progress;
        const curStart = arcStart + sweep * Math.max(0, swState.progress - 0.5);
        swoosh.lineStyle(9, tint, 0.95);
        swoosh.beginPath();
        swoosh.arc(cx, cy, arcRadius, curStart, curEnd, false);
        swoosh.strokePath();
        swoosh.lineStyle(3, 0xffffff, 0.85);
        swoosh.beginPath();
        swoosh.arc(cx, cy, arcRadius * 0.92, curStart, curEnd, false);
        swoosh.strokePath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: swoosh,
          alpha: 0,
          duration: 150,
          ease: 'Power2.easeIn',
          onComplete: () => swoosh.destroy(),
        });

        // Impact point at the end of the hook arc
        const impactX = cx + Math.cos(arcEnd) * arcRadius * 0.55;
        const impactY = cy + Math.sin(arcEnd) * arcRadius * 0.55;
        const burstSize = h * 0.4;

        // Central burst: ring + cross arms
        const burst = scene.add.graphics();
        burst.setDepth(10001);
        burst.lineStyle(8, tint, 1);
        burst.strokeCircle(impactX, impactY, burstSize * 0.4);
        burst.lineStyle(3, 0xffffff, 1);
        burst.strokeCircle(impactX, impactY, burstSize * 0.4);
        burst.lineStyle(7, tint, 1);
        burst.beginPath();
        burst.moveTo(impactX - burstSize * 0.7, impactY);
        burst.lineTo(impactX + burstSize * 0.7, impactY);
        burst.moveTo(impactX, impactY - burstSize * 0.7);
        burst.lineTo(impactX, impactY + burstSize * 0.7);
        burst.strokePath();
        burst.lineStyle(3, 0xffffff, 1);
        burst.beginPath();
        burst.moveTo(impactX - burstSize * 0.7, impactY);
        burst.lineTo(impactX + burstSize * 0.7, impactY);
        burst.moveTo(impactX, impactY - burstSize * 0.7);
        burst.lineTo(impactX, impactY + burstSize * 0.7);
        burst.strokePath();
        scene.tweens.add({
          targets: burst,
          alpha: 0,
          duration: 300,
          ease: 'Power2.easeOut',
          onComplete: () => burst.destroy(),
        });

        // 10 triangle debris particles fanning outward
        for (let i = 0; i < 10; i++) {
          const angle = (Math.PI * 2 * i) / 10 + (Math.random() - 0.5) * 0.5;
          const dist = h * 0.55 + Math.random() * h * 0.2;
          const tx = impactX + Math.cos(angle) * dist;
          const ty = impactY + Math.sin(angle) * dist;
          const chunk = scene.add.graphics();
          const size = 7 + Math.random() * 4;
          chunk.fillStyle(tint, 1);
          chunk.fillTriangle(0, -size, size * 0.9, size, -size * 0.9, size);
          chunk.lineStyle(2, 0xffffff, 1);
          chunk.strokeTriangle(0, -size, size * 0.9, size, -size * 0.9, size);
          chunk.setPosition(impactX, impactY);
          chunk.setDepth(10000);
          chunk.rotation = Math.random() * Math.PI * 2;
          scene.tweens.add({
            targets: chunk,
            x: tx,
            y: ty,
            alpha: 0,
            rotation: chunk.rotation + (Math.random() - 0.5) * 5,
            duration: 400,
            ease: 'Power2.easeOut',
            onComplete: () => chunk.destroy(),
          });
        }

        shakeSprite(scene, sprite, 12);
        scene.cameras.main.shake(240, 0.02);
      },
    });

    flashSpriteTint(scene, sprite, tint, 340);
  },
};

/** Water barrier wrap — rising tide circle that envelops the target with cresting droplets */
const barrierApplyWater: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 220);

    // Rising water ring around the target
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const radius = sprite.displayHeight * 0.35;

    const ring = scene.add.graphics();
    ring.setDepth(10000);
    ring.lineStyle(3, tint, 0.9);
    ring.strokeEllipse(cx, cy + sprite.displayHeight * 0.4, radius * 1.6, radius * 0.5);

    // Ring rises to enclose the sprite
    scene.tweens.add({
      targets: ring,
      y: -sprite.displayHeight * 0.6,
      alpha: 0,
      duration: 450,
      ease: 'Sine.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Cresting droplets — 6 small particles spiraling upward
    for (let i = 0; i < 6; i++) {
      const droplet = scene.add.graphics();
      droplet.fillStyle(tint, 1);
      droplet.fillCircle(0, 0, 2.5);

      const angle = (i / 6) * Math.PI * 2;
      const startX = cx + Math.cos(angle) * radius;
      const startY = cy + sprite.displayHeight * 0.3 + Math.sin(angle) * radius * 0.3;
      droplet.setPosition(startX, startY);
      droplet.setDepth(10001);

      scene.tweens.add({
        targets: droplet,
        x: cx + Math.cos(angle) * (radius * 0.4),
        y: cy - sprite.displayHeight * 0.2 - Math.random() * 10,
        alpha: 0,
        duration: 500,
        delay: i * 30,
        ease: 'Sine.easeOut',
        onComplete: () => droplet.destroy(),
      });
    }

    // Subtle scale pulse (smaller than buffShimmer to avoid stepping on visual)
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 120,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Steel barrier raise — vertical sweep upward + metallic flash, knight bracing pose */
const barrierApplySteel: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 180);

    const cx = sprite.x;
    const baseY = sprite.y; // feet
    const headY = sprite.y - sprite.displayHeight;
    const width = sprite.displayWidth * 0.55;

    // Vertical sweep — a thin bright band rising from feet to head
    const band = scene.add.graphics();
    band.setDepth(10000);
    band.fillStyle(tint, 0.85);
    band.fillRect(cx - width / 2, baseY - 4, width, 4);

    scene.tweens.add({
      targets: band,
      y: headY - baseY,
      alpha: 0,
      duration: 280,
      ease: 'Power2.easeOut',
      onComplete: () => band.destroy(),
    });

    // Two side glints — small vertical lines on each flank suggesting raised edges
    for (const side of [-1, 1]) {
      const glint = scene.add.graphics();
      glint.setDepth(10001);
      glint.lineStyle(2, tint, 0.9);
      const x = cx + side * width * 0.5;
      glint.lineBetween(x, baseY - 6, x, headY + 6);
      glint.alpha = 0;

      scene.tweens.add({
        targets: glint,
        alpha: 0.9,
        duration: 80,
        delay: 80,
        yoyo: true,
        hold: 100,
        onComplete: () => glint.destroy(),
      });
    }
  },
};

/** Barrier shatter — quick burst of tinted shards when a barrier instance hits 0 */
const barrierShatter: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);

    // 5 angular shards flying outward
    for (let i = 0; i < 5; i++) {
      const shard = scene.add.graphics();
      shard.fillStyle(tint, 0.95);
      // Triangular shard
      shard.beginPath();
      shard.moveTo(0, -3);
      shard.lineTo(2, 3);
      shard.lineTo(-2, 3);
      shard.closePath();
      shard.fillPath();
      shard.setPosition(cx, cy);
      shard.setDepth(10002);

      const angle = (i / 5) * Math.PI * 2 + Math.random() * 0.4;
      const distance = sprite.displayHeight * 0.4 + Math.random() * 10;

      scene.tweens.add({
        targets: shard,
        x: cx + Math.cos(angle) * distance,
        y: cy + Math.sin(angle) * distance,
        rotation: (Math.random() - 0.5) * 4,
        alpha: 0,
        duration: 280,
        ease: 'Power2.easeOut',
        onComplete: () => shard.destroy(),
      });
    }

    // Brief inner flash on the sprite
    flashSpriteTint(scene, sprite, tint, 120);
  },
};

/** Break shatter — fires on the broken target the instant the Break threshold trips.
 *  Distinct from any single-ability hit: layered camera shake + sprite stagger + a
 *  large radial crystal-shard burst + one expanding shockwave ring, in the universal
 *  Break palette (purple → gold). Designed to sell a tier-1 mechanic moment in
 *  concert with the bar's shatter and the BROKEN! text. */
const breakShatter: AnimationPreset = {
  play(scene, sprite, _tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayHeight * 0.7;

    // Shockwave ring — single arc expands and fades. Stroke colour matches the
    // purple side of the palette so it reads as the same family as the shards.
    const ring = scene.add.graphics();
    ring.lineStyle(3, 0xd987ff, 1);
    ring.strokeCircle(0, 0, 12);
    ring.setPosition(cx, cy);
    ring.setDepth(10001);
    scene.tweens.add({
      targets: ring,
      scaleX: 4.5,
      scaleY: 4.5,
      alpha: 0,
      duration: 380,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });

    // 12 angular crystal shards radiating outward. Alternating purple / gold
    // bridges the bar's pre/post palette and reads as "crystallised threshold
    // shattering" rather than generic particles.
    const shardCount = 12;
    for (let i = 0; i < shardCount; i++) {
      const shard = scene.add.graphics();
      const shardTint = i % 2 === 0 ? 0xd987ff : 0xffd45e;
      shard.fillStyle(shardTint, 0.95);
      shard.beginPath();
      shard.moveTo(0, -5);
      shard.lineTo(3, 5);
      shard.lineTo(-3, 5);
      shard.closePath();
      shard.fillPath();
      shard.setPosition(cx, cy);
      shard.setDepth(10002);

      const angle = (i / shardCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
      const distance = reach * (0.8 + Math.random() * 0.4);

      scene.tweens.add({
        targets: shard,
        x: cx + Math.cos(angle) * distance,
        y: cy + Math.sin(angle) * distance,
        rotation: (Math.random() - 0.5) * 6,
        alpha: 0,
        scaleX: 1.4,
        scaleY: 1.4,
        duration: 460 + Math.random() * 140,
        ease: 'Power2.easeOut',
        onComplete: () => shard.destroy(),
      });
    }

    // Stagger reaction — tint flash sells "hit registered", shake sells "rocked".
    flashSpriteTint(scene, sprite, 0xd987ff, 200);
    shakeSprite(scene, sprite, 8);

    // Camera shake — felt but not dominant; tuned below a heavy-impact ability so
    // multiple Breaks in quick succession don't nausea the player.
    scene.cameras.main.shake(180, 0.008);
  },
};

/** Nature affliction — vine-like particles drift downward over the target + green tint.
 *  Used for Overgrowth AoE bleed application — feels like the forest reaching for you. */
const natureAfflict: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const headY = sprite.y - sprite.displayHeight;
    const cy = spriteCenterY(sprite);

    // Vine-like particles drifting down from above the sprite
    const count = 6;
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      // Elongated vertical shape — vine tendril feel
      particle.fillStyle(tint, 0.85);
      particle.fillEllipse(0, 0, 3, 6);
      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.8;
      particle.setPosition(startX, headY - 10 - Math.random() * 15);
      particle.setDepth(10000);

      scene.tweens.add({
        targets: particle,
        x: startX + (Math.random() - 0.5) * 20,
        y: cy + 10 + Math.random() * 20,
        alpha: 0,
        rotation: (Math.random() - 0.5) * 1.5,
        duration: 450,
        delay: i * 50,
        ease: 'Sine.easeIn',
        onComplete: () => particle.destroy(),
      });
    }

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 180);
  },
};

/** Sandstorm weather hit — horizontal sand particles drift across the target.
 *  Less explosive than burst, more environmental. Pokemon Sandstorm feel. */
const sandstormHit: AnimationPreset = {
  play(scene, sprite, tint) {
    const cy = spriteCenterY(sprite);
    const left = sprite.x - sprite.displayWidth * 0.6;
    const right = sprite.x + sprite.displayWidth * 0.6;

    // Sand grains blowing across the sprite left-to-right
    const count = 8;
    for (let i = 0; i < count; i++) {
      const grain = scene.add.graphics();
      grain.fillStyle(tint, 0.7 + Math.random() * 0.3);
      grain.fillCircle(0, 0, 1.5 + Math.random() * 1.5);

      const startY = cy + (Math.random() - 0.5) * sprite.displayHeight * 0.7;
      grain.setPosition(left - Math.random() * 10, startY);
      grain.setDepth(10000);

      scene.tweens.add({
        targets: grain,
        x: right + 10 + Math.random() * 15,
        y: startY + (Math.random() - 0.5) * 15,
        alpha: 0,
        duration: 350 + Math.random() * 150,
        delay: i * 30,
        ease: 'Sine.easeOut',
        onComplete: () => grain.destroy(),
      });
    }

    shakeSprite(scene, sprite, 3);
    flashSpriteTint(scene, sprite, tint, 120);
  },
};

/** Sand crust forms — granular grains sweep inward and settle into a hard layer.
 *  Earth-element self-buff: particles converge on the body, the sprite settles
 *  with a slight gravity nudge as the crust locks in. No camera shake.
 *  Use for sand/stone armor-up abilities (Sandskin, future Granocen tank kit). */
const sandCrust: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 280);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const radius = sprite.displayHeight * 0.55;

    // 10 sand grains converging from a ring around the sprite — mix circles and
    // small rectangles for granular variety, accelerate inward (Cubic.easeIn).
    for (let i = 0; i < 10; i++) {
      const grain = scene.add.graphics();
      grain.fillStyle(tint, 0.85);
      if (i % 3 === 0) {
        grain.fillRect(-2, -1.5, 4, 3);
      } else {
        grain.fillCircle(0, 0, 2 + Math.random() * 1.5);
      }

      const angle = (Math.PI * 2 * i) / 10 + Math.random() * 0.3;
      const startX = cx + Math.cos(angle) * radius;
      const startY = cy + Math.sin(angle) * radius * 0.8;
      grain.setPosition(startX, startY);
      grain.setDepth(10000);

      scene.tweens.add({
        targets: grain,
        x: cx + Math.cos(angle) * 4,
        y: cy + Math.sin(angle) * 4,
        scaleX: 0.4,
        scaleY: 0.4,
        alpha: 0,
        duration: 280 + Math.random() * 80,
        delay: i * 22,
        ease: 'Cubic.easeIn',
        onComplete: () => grain.destroy(),
      });
    }

    // Slight downward settle + brief scale compression — the weight of crust forming.
    const bs = getBaseScale(sprite);
    const origY = sprite.y;
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);

    scene.tweens.add({
      targets: sprite,
      y: origY + 2,
      duration: 250,
      delay: 200,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => { sprite.y = origY; },
    });

    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 0.985,
      scaleY: bs * 1.02,
      duration: 200,
      delay: 180,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** A single chunky stone wedge plunges from above onto the target's center mass,
 *  then bursts into 4 angular debris shards (mix of triangles + rectangles) that
 *  fan outward and downward. Solid medium thump — the deliberate default earth
 *  jab. Reusable across humanoids, statues, beasts, scarabs. Bigger than
 *  sandstorm_hit's wisp, smaller than ground_eruption's row of spikes. */
const stoneJab: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const wedgeH = sprite.displayHeight * 0.45;
    const halfW = sprite.displayWidth * 0.12;

    // The plunging wedge — chunky pointed-down stone with a white core.
    const wedge = scene.add.graphics();
    wedge.setDepth(10000);
    wedge.fillStyle(tint, 0.95);
    wedge.beginPath();
    wedge.moveTo(-halfW, -wedgeH);
    wedge.lineTo(halfW, -wedgeH);
    wedge.lineTo(halfW * 0.55, -wedgeH * 0.35);
    wedge.lineTo(0, 0);
    wedge.lineTo(-halfW * 0.55, -wedgeH * 0.35);
    wedge.closePath();
    wedge.fillPath();
    // Highlight core
    wedge.fillStyle(0xffffff, 0.55);
    wedge.beginPath();
    wedge.moveTo(-halfW * 0.35, -wedgeH * 0.85);
    wedge.lineTo(halfW * 0.35, -wedgeH * 0.85);
    wedge.lineTo(0, -wedgeH * 0.1);
    wedge.closePath();
    wedge.fillPath();

    const startY = cy - sprite.displayHeight * 0.7;
    wedge.setPosition(cx, startY);

    scene.tweens.add({
      targets: wedge,
      y: cy,
      duration: 110,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        // Impact: wedge fades in place, debris shards fan outward.
        scene.tweens.add({
          targets: wedge,
          alpha: 0,
          scaleY: 0.7,
          duration: 140,
          ease: 'Power2.easeIn',
          onComplete: () => wedge.destroy(),
        });

        const shardCount = 5;
        for (let i = 0; i < shardCount; i++) {
          const shard = scene.add.graphics();
          shard.setDepth(10001);
          shard.fillStyle(tint, 0.9);
          if (i % 2 === 0) {
            // angular triangle shard
            const sw = 5 + Math.random() * 3;
            const sh = 8 + Math.random() * 4;
            shard.beginPath();
            shard.moveTo(-sw, sh * 0.4);
            shard.lineTo(sw * 0.6, -sh);
            shard.lineTo(sw, sh * 0.5);
            shard.closePath();
            shard.fillPath();
          } else {
            // chunky rectangle shard
            const rw = 4 + Math.random() * 3;
            const rh = 6 + Math.random() * 3;
            shard.fillRect(-rw, -rh, rw * 2, rh * 2);
          }

          // Spread across a 180° fan biased upward-out (debris kicks up & out)
          const angle = -Math.PI + (Math.PI * (i + 0.5)) / shardCount + (Math.random() - 0.5) * 0.25;
          const dist = sprite.displayWidth * (0.35 + Math.random() * 0.2);
          shard.setPosition(cx, cy);
          shard.setRotation(Math.random() * Math.PI);

          scene.tweens.add({
            targets: shard,
            x: cx + Math.cos(angle) * dist,
            y: cy + Math.sin(angle) * dist * 0.55 + 8,
            rotation: shard.rotation + (Math.random() - 0.5) * 2.5,
            alpha: 0,
            duration: 260 + Math.random() * 80,
            ease: 'Power2.easeOut',
            onComplete: () => shard.destroy(),
          });
        }
      },
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 160);
    scene.cameras.main.shake(120, 0.006);
  },
};

/** Bark plates lock onto the body — staggered armor pieces fade in across the
 *  sprite silhouette, hold, then fade. Sprite puffs vertically as the bark
 *  wraps the body. Nature/wood self-buff feel; reusable for future plant-tank
 *  archetypes (Anthura wood-wardens, fungal armor variants). */
const barkForm: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 280);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const w = sprite.displayWidth;
    const h = sprite.displayHeight;

    // Darken the tint so bark plates contrast against the sprite body.
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    const darkColor = ((Math.floor(r * 0.55) & 0xff) << 16)
      | ((Math.floor(g * 0.55) & 0xff) << 8)
      | (Math.floor(b * 0.55) & 0xff);

    // 6 bark plates appear in stagger across the silhouette, hold, then fade.
    for (let i = 0; i < 6; i++) {
      const plate = scene.add.graphics();
      plate.fillStyle(darkColor, 0.9);

      const plateW = 5 + Math.random() * 3;
      const plateH = h * (0.18 + Math.random() * 0.10);
      plate.fillRoundedRect(-plateW / 2, -plateH / 2, plateW, plateH, 1);

      const px = cx + (Math.random() - 0.5) * w * 0.55;
      const py = cy + (Math.random() - 0.5) * h * 0.45;
      plate.setPosition(px, py);
      plate.setAlpha(0);
      plate.setDepth(10000);

      scene.tweens.add({
        targets: plate,
        alpha: 0.85,
        duration: 60,
        delay: i * 35,
        ease: 'Power2.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: plate,
            alpha: 0,
            duration: 200,
            delay: 140,
            ease: 'Power2.easeIn',
            onComplete: () => plate.destroy(),
          });
        },
      });
    }

    // Vertical scale puff — bark armor wrapping the body.
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.01,
      scaleY: bs * 1.05,
      duration: 220,
      delay: 80,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Boss power-up — dramatic scale throb + glow ramp + camera shake.
 *  More intense than buff_shimmer, signals "this just got worse." */
const bossIntensify: AnimationPreset = {
  play(scene, sprite, tint) {
    // Camera flash
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(150, r, g, b, false);

    // Heavy scale throb — 1.0 → 1.12 → 1.0
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.12,
      scaleY: bs * 1.12,
      duration: 250,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Radial energy particles bursting outward (power release)
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    for (let i = 0; i < 6; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(tint, 0.9);
      particle.fillCircle(0, 0, 3);
      particle.setPosition(cx, cy);
      particle.setDepth(10000);

      const angle = (Math.PI * 2 * i) / 6;
      const distance = 30 + Math.random() * 20;

      scene.tweens.add({
        targets: particle,
        x: cx + Math.cos(angle) * distance,
        y: cy + Math.sin(angle) * distance,
        alpha: 0,
        duration: 350,
        delay: i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 250);
    scene.cameras.main.shake(150, 0.01);
  },
};

/** Idle ambient pulse — barely-there visual showing the boss did something
 *  without demanding attention. Subtle glow flash + faint upward particle. */
const idlePulse: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 300);

    // 2 faint particles drifting up — just enough to show life
    for (let i = 0; i < 2; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(tint, 0.5);
      particle.fillCircle(0, 0, 2);
      const cx = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.3;
      const cy = spriteCenterY(sprite);
      particle.setPosition(cx, cy);
      particle.setDepth(10000);

      scene.tweens.add({
        targets: particle,
        y: cy - 20 - Math.random() * 10,
        alpha: 0,
        duration: 500,
        delay: i * 100,
        ease: 'Power1.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  },
};

/** Vertical beam of light slamming down from above — divine strikes, lightning pillars, judgment */
const divinePillar: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y;
    const beamHeight = sprite.displayHeight * 2.5;
    const beamWidth = sprite.displayWidth * 0.45;
    const beamCenterY = baseY - beamHeight / 2;

    // Outer colored beam
    const outerBeam = scene.add.rectangle(cx, beamCenterY, beamWidth, beamHeight, tint, 0.55);
    outerBeam.setDepth(9999);
    // Inner white-hot core
    const innerBeam = scene.add.rectangle(cx, beamCenterY, beamWidth * 0.3, beamHeight, 0xffffff, 0.85);
    innerBeam.setDepth(10000);

    // Snap in from razor-thin to full width
    outerBeam.setScale(0, 1);
    innerBeam.setScale(0, 1);

    scene.tweens.add({
      targets: [outerBeam, innerBeam],
      scaleX: 1,
      duration: 80,
      ease: 'Power3.easeOut',
    });

    // Outer beam expands + fades
    scene.tweens.add({
      targets: outerBeam,
      alpha: 0,
      scaleX: 1.6,
      duration: 350,
      delay: 120,
      ease: 'Power2.easeIn',
      onComplete: () => outerBeam.destroy(),
    });
    scene.tweens.add({
      targets: innerBeam,
      alpha: 0,
      duration: 280,
      delay: 120,
      ease: 'Power2.easeIn',
      onComplete: () => innerBeam.destroy(),
    });

    // Ground impact sparks fanning upward from base
    for (let i = 0; i < 8; i++) {
      const spark = scene.add.circle(cx, baseY, 3 + Math.random() * 2, tint, 0.9);
      spark.setDepth(10001);
      const angle = -Math.PI * Math.random(); // upper half only
      const dist = 25 + Math.random() * 35;
      scene.tweens.add({
        targets: spark,
        x: cx + Math.cos(angle) * dist,
        y: baseY + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 300,
        delay: 80 + i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }

    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(120, r, g, b, false);
    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 200);
  },
};

/** Two crossing diagonal slash lines in rapid succession — powerful cleaves, X-marks */
const crossSlash: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.6;

    const slashes = [
      { x1: cx - len * 0.5, y1: cy - len * 0.4, x2: cx + len * 0.5, y2: cy + len * 0.4 },
      { x1: cx + len * 0.5, y1: cy - len * 0.4, x2: cx - len * 0.5, y2: cy + len * 0.4 },
    ];

    slashes.forEach((s, idx) => {
      const gfx = scene.add.graphics();
      gfx.setDepth(10000);
      const draw = { head: 0, tail: 0 };

      scene.time.delayedCall(idx * 70, () => {
        scene.tweens.add({
          targets: draw,
          head: 1,
          duration: 80,
          ease: 'Power3.easeOut',
          onUpdate: () => {
            gfx.clear();
            // Thick colored line
            gfx.lineStyle(5, tint, 1);
            gfx.beginPath();
            gfx.moveTo(
              s.x1 + (s.x2 - s.x1) * draw.tail,
              s.y1 + (s.y2 - s.y1) * draw.tail,
            );
            gfx.lineTo(
              s.x1 + (s.x2 - s.x1) * draw.head,
              s.y1 + (s.y2 - s.y1) * draw.head,
            );
            gfx.strokePath();
            // Bright inner edge
            gfx.lineStyle(2, 0xffffff, 0.7);
            gfx.beginPath();
            gfx.moveTo(
              s.x1 + (s.x2 - s.x1) * draw.tail,
              s.y1 + (s.y2 - s.y1) * draw.tail,
            );
            gfx.lineTo(
              s.x1 + (s.x2 - s.x1) * draw.head,
              s.y1 + (s.y2 - s.y1) * draw.head,
            );
            gfx.strokePath();
          },
          onComplete: () => {
            scene.tweens.add({
              targets: draw,
              tail: 1,
              duration: 100,
              ease: 'Power2.easeIn',
              onUpdate: () => {
                gfx.clear();
                gfx.lineStyle(5, tint, 1);
                gfx.beginPath();
                gfx.moveTo(
                  s.x1 + (s.x2 - s.x1) * draw.tail,
                  s.y1 + (s.y2 - s.y1) * draw.tail,
                );
                gfx.lineTo(
                  s.x1 + (s.x2 - s.x1) * draw.head,
                  s.y1 + (s.y2 - s.y1) * draw.head,
                );
                gfx.strokePath();
              },
              onComplete: () => gfx.destroy(),
            });
          },
        });
      });
    });

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 180);
    scene.cameras.main.shake(80, 0.005);
  },
};

/** Jagged zigzag lightning bolt from sky to target — electric/storm abilities */
const lightningStrike: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const topY = sprite.y - sprite.displayHeight * 2.2;

    // Generate zigzag path
    const segments = 8;
    const points: { x: number; y: number }[] = [{ x: cx + (Math.random() - 0.5) * 15, y: topY }];
    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      points.push({
        x: cx + (Math.random() - 0.5) * sprite.displayWidth * 0.9,
        y: topY + (cy - topY) * t,
      });
    }
    points.push({ x: cx, y: cy });

    // Main bolt — thick glow + white core
    const bolt = scene.add.graphics();
    bolt.setDepth(10001);

    bolt.lineStyle(7, tint, 0.6);
    bolt.beginPath();
    bolt.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) bolt.lineTo(points[i].x, points[i].y);
    bolt.strokePath();

    bolt.lineStyle(2, 0xffffff, 0.95);
    bolt.beginPath();
    bolt.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) bolt.lineTo(points[i].x, points[i].y);
    bolt.strokePath();

    // Bolt flickers then fades
    scene.tweens.add({
      targets: bolt,
      alpha: 0,
      duration: 250,
      delay: 100,
      ease: 'Power2.easeIn',
      onComplete: () => bolt.destroy(),
    });

    // Secondary branch from midpoint
    const branch = scene.add.graphics();
    branch.setDepth(10000);
    const mid = Math.floor(segments * 0.35);
    branch.lineStyle(4, tint, 0.4);
    branch.beginPath();
    branch.moveTo(points[mid].x, points[mid].y);
    let bx = points[mid].x;
    let by = points[mid].y;
    for (let i = 0; i < 3; i++) {
      bx += (Math.random() - 0.3) * 35;
      by += 15 + Math.random() * 15;
      branch.lineTo(bx, by);
    }
    branch.strokePath();

    scene.tweens.add({
      targets: branch,
      alpha: 0,
      duration: 200,
      delay: 80,
      ease: 'Power2.easeIn',
      onComplete: () => branch.destroy(),
    });

    // Impact flash — wide horizontal ellipse at strike point
    const flash = scene.add.ellipse(cx, cy, sprite.displayWidth * 0.5, sprite.displayHeight * 0.15, 0xffffff, 0.85);
    flash.setDepth(10002);
    scene.tweens.add({
      targets: flash,
      scaleX: 2.5,
      scaleY: 0.3,
      alpha: 0,
      duration: 180,
      ease: 'Power2.easeOut',
      onComplete: () => flash.destroy(),
    });

    scene.cameras.main.flash(80, 255, 255, 255, false);
    shakeSprite(scene, sprite, 9);
    flashSpriteTint(scene, sprite, tint, 150);
  },
};

/** Lightning arc — short horizontal crackling bolt that jitters across the target center.
 *  Three rapid jittering frames suggest the arc "vibrating" in place, distinct from the
 *  sky-falling `lightning_strike` bolt or the AoE fanning `lightning_chain`.
 *  Used for precise single-target zaps like Voltaic Arc. */
const lightningArc: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const halfLen = sprite.displayWidth * 0.55;
    const amp = sprite.displayHeight * 0.13;
    const segments = 7;

    const genArc = (): { x: number; y: number }[] => {
      const points: { x: number; y: number }[] = [];
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = cx - halfLen + halfLen * 2 * t;
        const yOffset = (i === 0 || i === segments) ? 0 : (Math.random() - 0.5) * amp * 2;
        points.push({ x, y: cy + yOffset });
      }
      return points;
    };

    const drawArc = (gfx: Phaser.GameObjects.Graphics, pts: { x: number; y: number }[]): void => {
      // Outer tint halo
      gfx.lineStyle(5, tint, 0.7);
      gfx.beginPath();
      gfx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) gfx.lineTo(pts[i].x, pts[i].y);
      gfx.strokePath();
      // White-hot inner core
      gfx.lineStyle(2, 0xffffff, 1);
      gfx.beginPath();
      gfx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) gfx.lineTo(pts[i].x, pts[i].y);
      gfx.strokePath();
    };

    // 3 jittering arc frames in rapid succession — feels like crackling
    for (let frame = 0; frame < 3; frame++) {
      scene.time.delayedCall(frame * 55, () => {
        const gfx = scene.add.graphics();
        gfx.setDepth(10000);
        drawArc(gfx, genArc());
        scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 130,
          ease: 'Power2.easeIn',
          onComplete: () => gfx.destroy(),
        });
      });
    }

    // White-hot center pulse
    const orb = scene.add.circle(cx, cy, 8, 0xffffff, 0.95);
    orb.setDepth(10001);
    scene.tweens.add({
      targets: orb,
      scale: 2.2,
      alpha: 0,
      duration: 180,
      ease: 'Power2.easeOut',
      onComplete: () => orb.destroy(),
    });

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 200);
  },
};

/** Water crash — vertical column of water slams down onto the target, releasing
 *  an outward splash ring + droplet spray. Modeled structurally after `divine_pillar`
 *  but water-themed (softer edges, splash fan instead of angular sparks).
 *  Used for single-target heavy water impacts (Tidal Crush). */
const waterCrash: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const footY = sprite.y;
    const cy = spriteCenterY(sprite);
    const columnH = sprite.displayHeight * 1.8;
    const columnW = sprite.displayWidth * 0.5;
    const columnCY = footY - columnH / 2;

    // Outer water column (translucent tint)
    const outer = scene.add.rectangle(cx, columnCY, columnW, columnH, tint, 0.55);
    outer.setDepth(9999);
    // Inner bright core (soft white)
    const inner = scene.add.rectangle(cx, columnCY, columnW * 0.35, columnH, 0xe6f7ff, 0.8);
    inner.setDepth(10000);

    outer.setScale(0, 1);
    inner.setScale(0, 1);

    // Column crashes down (snaps to full width)
    scene.tweens.add({
      targets: [outer, inner],
      scaleX: 1,
      duration: 100,
      ease: 'Power3.easeOut',
    });

    // Column expands outward + fades (water "spreading on impact")
    scene.tweens.add({
      targets: outer,
      alpha: 0,
      scaleX: 1.7,
      duration: 360,
      delay: 130,
      ease: 'Power2.easeIn',
      onComplete: () => outer.destroy(),
    });
    scene.tweens.add({
      targets: inner,
      alpha: 0,
      duration: 280,
      delay: 120,
      ease: 'Power2.easeIn',
      onComplete: () => inner.destroy(),
    });

    // Splash ring at feet — horizontal ellipse expanding outward
    const splash = scene.add.graphics();
    splash.setDepth(10001);
    splash.lineStyle(4, tint, 0.9);
    splash.strokeEllipse(0, 0, sprite.displayWidth * 0.6, sprite.displayHeight * 0.15);
    splash.setPosition(cx, footY - 4);
    scene.tweens.add({
      targets: splash,
      scaleX: 2.4,
      scaleY: 2.4,
      alpha: 0,
      duration: 400,
      delay: 100,
      ease: 'Sine.easeOut',
      onComplete: () => splash.destroy(),
    });

    // Droplet spray — 10 droplets fanning upward/outward from impact
    for (let i = 0; i < 10; i++) {
      const angle = -Math.PI * (0.1 + 0.8 * Math.random()); // upper hemisphere
      const dist = sprite.displayHeight * (0.25 + Math.random() * 0.35);
      const radius = 2 + Math.random() * 2;

      const drop = scene.add.graphics();
      drop.setDepth(10002);
      drop.fillStyle(tint, 0.95);
      drop.fillCircle(0, 0, radius);
      drop.fillStyle(0xffffff, 0.7);
      drop.fillCircle(-radius * 0.3, -radius * 0.3, radius * 0.4);
      drop.setPosition(cx, cy);

      scene.tweens.add({
        targets: drop,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.4,
        duration: 380,
        delay: 110 + i * 12,
        ease: 'Power2.easeOut',
        onComplete: () => drop.destroy(),
      });
    }

    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(140, r, g, b, false);
    shakeSprite(scene, sprite, 10);
    flashSpriteTint(scene, sprite, tint, 220);
  },
};

/** Rising surge wave — water swells up from below, crests over the target, and
 *  bursts outward in twin side-sprays. Mid-weight water hit, distinct from the
 *  vertical-column slam of water_crash and the radial generic of burst. Used by
 *  Hydromancy's Surge (R1 rotation spell with self-barrier rider). */
const waterSurge: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const footY = sprite.y;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    // Rising swell — tall curved wave that rises from feet and crests above mid-body.
    // Implemented as a vertically-stretching ellipse that fades as it crests.
    const swell = scene.add.graphics();
    swell.setDepth(9999);
    swell.fillStyle(tint, 0.55);
    swell.fillEllipse(0, 0, w * 0.55, h * 0.25);
    swell.setPosition(cx, footY - h * 0.1);
    swell.setScale(0.4, 0.4);
    scene.tweens.add({
      targets: swell,
      scaleX: 1.5,
      scaleY: 2.6,
      y: footY - h * 0.55,
      alpha: 0,
      duration: 280,
      ease: 'Sine.easeOut',
      onComplete: () => swell.destroy(),
    });

    // Bright inner crest — narrower white-blue core riding atop the swell
    const crest = scene.add.graphics();
    crest.setDepth(10000);
    crest.fillStyle(0xe6f7ff, 0.85);
    crest.fillEllipse(0, 0, w * 0.25, h * 0.18);
    crest.setPosition(cx, footY - h * 0.15);
    crest.setScale(0.4, 0.4);
    scene.tweens.add({
      targets: crest,
      scaleX: 1.4,
      scaleY: 2.2,
      y: footY - h * 0.6,
      alpha: 0,
      duration: 260,
      delay: 30,
      ease: 'Sine.easeOut',
      onComplete: () => crest.destroy(),
    });

    // Twin side-sprays — two arcs of droplets fanning left and right after the crest
    const sprayDelay = 160;
    for (let side = -1; side <= 1; side += 2) {
      for (let i = 0; i < 4; i++) {
        const baseAngle = side > 0 ? -Math.PI * 0.15 : Math.PI * 1.15;
        const angle = baseAngle + (Math.random() - 0.5) * 0.6;
        const dist = h * (0.35 + Math.random() * 0.3);
        const radius = 2.5 + Math.random() * 2;

        const drop = scene.add.graphics();
        drop.setDepth(10002);
        drop.fillStyle(tint, 0.95);
        drop.fillCircle(0, 0, radius);
        drop.fillStyle(0xffffff, 0.7);
        drop.fillCircle(-radius * 0.3, -radius * 0.3, radius * 0.4);
        drop.setPosition(cx, cy);

        scene.tweens.add({
          targets: drop,
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist + h * 0.1, // slight downward arc as droplets fall
          alpha: 0,
          scale: 0.4,
          duration: 340,
          delay: sprayDelay + i * 18,
          ease: 'Power2.easeOut',
          onComplete: () => drop.destroy(),
        });
      }
    }

    // Compact splash ring at the impact point — quick horizontal ellipse fading out
    const ring = scene.add.graphics();
    ring.setDepth(10001);
    ring.lineStyle(3, tint, 0.85);
    ring.strokeEllipse(0, 0, w * 0.45, h * 0.12);
    ring.setPosition(cx, footY - 6);
    scene.tweens.add({
      targets: ring,
      scaleX: 1.8,
      scaleY: 1.8,
      alpha: 0,
      duration: 320,
      delay: 140,
      ease: 'Sine.easeOut',
      onComplete: () => ring.destroy(),
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 180);
  },
};

/** Particles raining from above — hail, arrow volley, bombardment, acid rain */
const rainDown: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const headY = sprite.y - sprite.displayHeight;
    const baseY = sprite.y;
    const width = sprite.displayWidth * 0.9;

    // Raining projectile particles — elongated teardrops falling
    const count = 12;
    for (let i = 0; i < count; i++) {
      const startX = cx + (Math.random() - 0.5) * width;
      const startY = headY - 30 - Math.random() * 50;

      const particle = scene.add.graphics();
      particle.fillStyle(tint, 0.9);
      particle.fillEllipse(0, 0, 3, 8);
      particle.setPosition(startX, startY);
      particle.setDepth(10000);

      const endX = startX + (Math.random() - 0.5) * 15;
      const endY = baseY - Math.random() * sprite.displayHeight * 0.3;

      scene.tweens.add({
        targets: particle,
        x: endX,
        y: endY,
        alpha: 0.2,
        duration: 180 + Math.random() * 120,
        delay: i * 30,
        ease: 'Power2.easeIn',
        onComplete: () => particle.destroy(),
      });
    }

    // Staggered ground-splash rings on impact
    for (let i = 0; i < 5; i++) {
      scene.time.delayedCall(60 + i * 45, () => {
        const splashX = cx + (Math.random() - 0.5) * width * 0.7;
        const splash = scene.add.ellipse(splashX, baseY - 3, 4, 2, tint, 0.8);
        splash.setDepth(10001);
        scene.tweens.add({
          targets: splash,
          scaleX: 4,
          scaleY: 1.5,
          alpha: 0,
          duration: 180,
          ease: 'Power2.easeOut',
          onComplete: () => splash.destroy(),
        });
      });
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 250);
  },
};

/** Thick sweeping arc across the sprite — powerful cleaving swings, fighting-game hit arcs */
const crescentSweep: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const radius = sprite.displayHeight * 0.55;

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);

    const sweep = { progress: 0 };
    const startAngle = -Math.PI * 0.8;
    const fullSweep = Math.PI * 1.1;

    scene.tweens.add({
      targets: sweep,
      progress: 1,
      duration: 140,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        gfx.clear();
        const currentEnd = startAngle + fullSweep * sweep.progress;
        // Trailing tail catches up — arc shortens as it sweeps
        const trailStart = startAngle + fullSweep * Math.max(0, sweep.progress - 0.45);

        // Thick colored arc
        gfx.lineStyle(8, tint, 0.9);
        gfx.beginPath();
        gfx.arc(cx, cy, radius, trailStart, currentEnd, false);
        gfx.strokePath();
        // Bright inner edge
        gfx.lineStyle(3, 0xffffff, 0.7);
        gfx.beginPath();
        gfx.arc(cx, cy, radius * 0.9, trailStart, currentEnd, false);
        gfx.strokePath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 120,
          ease: 'Power2.easeIn',
          onComplete: () => gfx.destroy(),
        });
      },
    });

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 150);
    scene.cameras.main.shake(80, 0.006);
  },
};

/** Tapered spikes erupting from the ground — earth magic, thorns, ice pillars */
const groundEruption: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y;
    const width = sprite.displayWidth * 0.8;

    // Vertical spike triangles erupting upward from base
    const spikeCount = 5;
    for (let i = 0; i < spikeCount; i++) {
      const spikeX = cx + (i / (spikeCount - 1) - 0.5) * width;
      const spikeHeight = sprite.displayHeight * (0.3 + Math.random() * 0.45);
      const halfW = 4 + Math.random() * 3;

      const spike = scene.add.graphics();
      spike.setDepth(10000);
      // Colored spike body
      spike.fillStyle(tint, 0.9);
      spike.beginPath();
      spike.moveTo(-halfW, 0);
      spike.lineTo(0, -spikeHeight);
      spike.lineTo(halfW, 0);
      spike.closePath();
      spike.fillPath();
      // White highlight core
      spike.fillStyle(0xffffff, 0.45);
      spike.beginPath();
      spike.moveTo(-halfW * 0.25, 0);
      spike.lineTo(0, -spikeHeight * 0.8);
      spike.lineTo(halfW * 0.25, 0);
      spike.closePath();
      spike.fillPath();

      spike.setPosition(spikeX, baseY);
      spike.setScale(1, 0); // start flat, scale upward from base

      // Staggered eruption
      scene.tweens.add({
        targets: spike,
        scaleY: 1,
        duration: 100,
        delay: i * 40,
        ease: 'Back.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: spike,
            alpha: 0,
            scaleY: 0.5,
            duration: 280,
            delay: 120,
            ease: 'Power2.easeIn',
            onComplete: () => spike.destroy(),
          });
        },
      });
    }

    // Debris puffs at base
    for (let i = 0; i < 6; i++) {
      const debris = scene.add.circle(
        cx + (Math.random() - 0.5) * width,
        baseY, 2.5 + Math.random() * 2, tint, 0.7,
      );
      debris.setDepth(10001);
      scene.tweens.add({
        targets: debris,
        y: baseY - 12 - Math.random() * 25,
        x: debris.x + (Math.random() - 0.5) * 20,
        alpha: 0,
        duration: 300,
        delay: 40 + i * 25,
        ease: 'Power2.easeOut',
        onComplete: () => debris.destroy(),
      });
    }

    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 200);
    scene.cameras.main.shake(150, 0.012);
  },
};

/** Particles spiraling inward to center then flash — wind, void, drain, vortex effects */
const vortexPreset: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const maxRadius = sprite.displayHeight * 0.55;

    const count = 10;
    for (let i = 0; i < count; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(i % 2 === 0 ? tint : 0xffffff, i % 2 === 0 ? 0.9 : 0.5);
      particle.fillCircle(0, 0, 2.5 + Math.random() * 1.5);
      particle.setDepth(10000);

      const startAngle = (Math.PI * 2 * i) / count;
      const startRadius = maxRadius + Math.random() * 10;
      particle.setPosition(
        cx + Math.cos(startAngle) * startRadius,
        cy + Math.sin(startAngle) * startRadius,
      );

      // Spiral inward — 1.5 rotations converging to center
      const spiral = { angle: startAngle, radius: startRadius };
      scene.tweens.add({
        targets: spiral,
        angle: startAngle + Math.PI * 3,
        radius: 0,
        duration: 400,
        delay: i * 20,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          particle.setPosition(
            cx + Math.cos(spiral.angle) * spiral.radius,
            cy + Math.sin(spiral.angle) * spiral.radius,
          );
          particle.alpha = 0.3 + 0.7 * (spiral.radius / maxRadius);
        },
        onComplete: () => particle.destroy(),
      });
    }

    // Center flash when particles converge
    scene.time.delayedCall(380, () => {
      const convergFlash = scene.add.circle(cx, cy, 10, 0xffffff, 0.85);
      convergFlash.setDepth(10001);
      scene.tweens.add({
        targets: convergFlash,
        scaleX: 3.5,
        scaleY: 3.5,
        alpha: 0,
        duration: 200,
        ease: 'Power2.easeOut',
        onComplete: () => convergFlash.destroy(),
      });
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 250);
  },
};

/** Whip-cracking thorned vine: serpentine curve snaps across the target with
 *  perpendicular barbs, holds briefly, then yanks back. Plant-debris flecks burst
 *  at the strike point. For vine, thorn, tendril, and tail-lash attacks. */
const vineLash: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayWidth * 1.4;
    const wave = sprite.displayHeight * 0.18;

    // Sample points along a sine path crossing the target left→right (one S-curve).
    const segments = 14;
    const points: { x: number; y: number; nx: number; ny: number }[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const px = cx - reach * 0.5 + reach * t;
      const py = cy + Math.sin(t * Math.PI * 2) * wave;
      // Perpendicular to tangent for thorn placement
      const tangentY = Math.cos(t * Math.PI * 2) * wave * (Math.PI * 2 / segments);
      const tangentX = reach / segments;
      const len = Math.hypot(tangentX, tangentY) || 1;
      points.push({ x: px, y: py, nx: -tangentY / len, ny: tangentX / len });
    }

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const draw = { head: 0, tail: 0 };

    function render() {
      gfx.clear();
      const headIdx = Math.floor(draw.head * segments);
      const tailIdx = Math.floor(draw.tail * segments);
      if (headIdx <= tailIdx) return;

      // Outer thick vine
      gfx.lineStyle(7, tint, 0.95);
      gfx.beginPath();
      gfx.moveTo(points[tailIdx].x, points[tailIdx].y);
      for (let i = tailIdx + 1; i <= headIdx; i++) gfx.lineTo(points[i].x, points[i].y);
      gfx.strokePath();

      // White inner highlight
      gfx.lineStyle(2, 0xffffff, 0.55);
      gfx.beginPath();
      gfx.moveTo(points[tailIdx].x, points[tailIdx].y);
      for (let i = tailIdx + 1; i <= headIdx; i++) gfx.lineTo(points[i].x, points[i].y);
      gfx.strokePath();

      // Triangular thorn barbs alternating sides every other vertex
      gfx.fillStyle(tint, 0.95);
      for (let i = tailIdx; i <= headIdx; i += 2) {
        const p = points[i];
        const side = i % 4 === 0 ? 1 : -1;
        const tipX = p.x + p.nx * 9 * side;
        const tipY = p.y + p.ny * 9 * side;
        const baseAX = p.x + p.nx * side - p.ny * 3;
        const baseAY = p.y + p.ny * side + p.nx * 3;
        const baseBX = p.x + p.nx * side + p.ny * 3;
        const baseBY = p.y + p.ny * side - p.nx * 3;
        gfx.beginPath();
        gfx.moveTo(tipX, tipY);
        gfx.lineTo(baseAX, baseAY);
        gfx.lineTo(baseBX, baseBY);
        gfx.closePath();
        gfx.fillPath();
      }
    }

    // Phase 1: snap forward (whip in)
    scene.tweens.add({
      targets: draw,
      head: 1,
      duration: 90,
      ease: 'Power3.easeIn',
      onUpdate: render,
      onComplete: () => {
        // Brief bite hold, then yank back (tail catches up)
        scene.time.delayedCall(40, () => {
          scene.tweens.add({
            targets: draw,
            tail: 1,
            duration: 110,
            ease: 'Power2.easeIn',
            onUpdate: render,
            onComplete: () => gfx.destroy(),
          });
        });
      },
    });

    // Plant-fleck debris from strike point
    const fleckCount = 6;
    for (let i = 0; i < fleckCount; i++) {
      const fleck = scene.add.graphics();
      fleck.setDepth(10001);
      fleck.fillStyle(tint, 0.9);
      fleck.beginPath();
      fleck.moveTo(0, -3);
      fleck.lineTo(2, 0);
      fleck.lineTo(0, 3);
      fleck.lineTo(-2, 0);
      fleck.closePath();
      fleck.fillPath();
      fleck.setPosition(cx, cy);

      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
      const dist = 25 + Math.random() * 25;
      scene.tweens.add({
        targets: fleck,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        rotation: (Math.random() - 0.5) * Math.PI,
        alpha: 0,
        duration: 320,
        delay: 80,
        ease: 'Power2.easeOut',
        onComplete: () => fleck.destroy(),
      });
    }

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 130);
    scene.cameras.main.shake(80, 0.005);
  },
};

/**
 * Sticky tongue / hook lash — thick fleshy line whips out from off-target,
 * latches at impact, then yanks back with elastic recoil. Target leans toward
 * the line briefly to sell the pull (and the slow debuff). Wet residue dot
 * lingers post-impact. Reusable for tongue-grabs, hook-shots, lasso pulls.
 */
const tongueHook: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayWidth * 1.6;
    // Origin is off-screen-left of the target — tongue whips in horizontally
    const ox = cx - reach;
    const oy = cy + sprite.displayHeight * 0.05;

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const draw = { extend: 0, retract: 0, sag: 0, alpha: 1 };

    // Inner-tint highlight (lighter shade of the same tint family)
    const innerTint = 0xffffff;

    function render() {
      gfx.clear();
      // Current head position along the line, with a slight downward sag mid-flight
      const t = Math.max(draw.extend - draw.retract, 0);
      const headX = ox + (cx - ox) * (draw.extend);
      const headY = oy + (cy - oy) * (draw.extend);
      const tailX = ox + (cx - ox) * (draw.retract);
      const tailY = oy + (cy - oy) * (draw.retract);
      if (t <= 0.001) return;

      // Sag: control point dips below the straight line at mid-flight
      const midX = (tailX + headX) * 0.5;
      const midY = (tailY + headY) * 0.5 + draw.sag * 14;

      // Outer fleshy stroke — quadratic curve through midpoint
      gfx.lineStyle(8, tint, 0.95 * draw.alpha);
      gfx.beginPath();
      gfx.moveTo(tailX, tailY);
      // Approximate a quadratic with two cubic-ish segments
      const steps = 10;
      for (let i = 1; i <= steps; i++) {
        const u = i / steps;
        const oneU = 1 - u;
        const px = oneU * oneU * tailX + 2 * oneU * u * midX + u * u * headX;
        const py = oneU * oneU * tailY + 2 * oneU * u * midY + u * u * headY;
        gfx.lineTo(px, py);
      }
      gfx.strokePath();

      // Wet inner highlight
      gfx.lineStyle(3, innerTint, 0.55 * draw.alpha);
      gfx.beginPath();
      gfx.moveTo(tailX, tailY);
      for (let i = 1; i <= steps; i++) {
        const u = i / steps;
        const oneU = 1 - u;
        const px = oneU * oneU * tailX + 2 * oneU * u * midX + u * u * headX;
        const py = oneU * oneU * tailY + 2 * oneU * u * midY + u * u * headY;
        gfx.lineTo(px, py);
      }
      gfx.strokePath();

      // Hook bulge at the head
      gfx.fillStyle(tint, 0.95 * draw.alpha);
      gfx.fillCircle(headX, headY, 6);
      gfx.fillStyle(innerTint, 0.5 * draw.alpha);
      gfx.fillCircle(headX - 1, headY - 1, 2.5);
    }

    // Phase 1: tongue snaps outward (90ms, fast ease-out, slight sag)
    scene.tweens.add({
      targets: draw,
      extend: 1,
      sag: 1,
      duration: 90,
      ease: 'Power2.easeOut',
      onUpdate: render,
      onComplete: () => {
        // Impact frame: shake, tint flash, target lurches inward (toward origin)
        shakeSprite(scene, sprite, 5);
        flashSpriteTint(scene, sprite, tint, 100);

        // Target slides toward the line briefly — sells the yank/slow
        const origX = sprite.x;
        scene.tweens.add({
          targets: sprite,
          x: origX - 6,
          duration: 70,
          yoyo: true,
          ease: 'Sine.easeOut',
          onComplete: () => { sprite.x = origX; },
        });

        // Sticky residue dot at impact — lingers ~220ms then fades
        const residue = scene.add.graphics();
        residue.setDepth(10001);
        const drip = { r: 5, alpha: 0.9 };
        const drawResidue = () => {
          residue.clear();
          residue.fillStyle(tint, drip.alpha);
          residue.fillCircle(cx, cy, drip.r);
          residue.fillStyle(innerTint, drip.alpha * 0.5);
          residue.fillCircle(cx - 1, cy - 1, drip.r * 0.4);
          // A single tear-drip below
          residue.fillStyle(tint, drip.alpha * 0.7);
          residue.fillTriangle(cx - 2, cy + 2, cx + 2, cy + 2, cx, cy + 8 + (1 - drip.alpha) * 6);
        };
        drawResidue();
        scene.tweens.add({
          targets: drip,
          alpha: 0,
          r: 3,
          duration: 220,
          delay: 60,
          ease: 'Sine.easeIn',
          onUpdate: drawResidue,
          onComplete: () => residue.destroy(),
        });

        // Phase 2: tail catches up — tongue retracts elastically (140ms)
        scene.tweens.add({
          targets: draw,
          retract: 1,
          sag: 0,
          duration: 140,
          ease: 'Power2.easeIn',
          onUpdate: render,
          onComplete: () => gfx.destroy(),
        });
      },
    });
  },
};

/** Pointed multi-ray star expanding outward — explosive magic, crit hits, power moves */
const starburst: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const outerR = sprite.displayHeight * 0.5;
    const innerR = outerR * 0.28;
    const rayCount = 8;

    const star = scene.add.graphics();
    star.setDepth(10000);

    // Draw 8-pointed star
    star.fillStyle(tint, 0.9);
    star.beginPath();
    for (let i = 0; i < rayCount * 2; i++) {
      const angle = (Math.PI * 2 * i) / (rayCount * 2) - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : innerR;
      const px = Math.cos(angle) * r;
      const py = Math.sin(angle) * r;
      if (i === 0) star.moveTo(px, py);
      else star.lineTo(px, py);
    }
    star.closePath();
    star.fillPath();

    // White-hot inner star
    star.fillStyle(0xffffff, 0.7);
    star.beginPath();
    for (let i = 0; i < rayCount * 2; i++) {
      const angle = (Math.PI * 2 * i) / (rayCount * 2) - Math.PI / 2;
      const r = i % 2 === 0 ? outerR * 0.5 : innerR * 0.5;
      const px = Math.cos(angle) * r;
      const py = Math.sin(angle) * r;
      if (i === 0) star.moveTo(px, py);
      else star.lineTo(px, py);
    }
    star.closePath();
    star.fillPath();

    star.setPosition(cx, cy);
    star.setScale(0.1);

    // Burst outward with slight rotation
    scene.tweens.add({
      targets: star,
      scaleX: 1.3,
      scaleY: 1.3,
      rotation: Math.PI * 0.12,
      duration: 130,
      ease: 'Power3.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: star,
          alpha: 0,
          scaleX: 1.6,
          scaleY: 1.6,
          duration: 200,
          ease: 'Power2.easeIn',
          onComplete: () => star.destroy(),
        });
      },
    });

    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(100, r, g, b, false);
    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 180);
  },
};

// ---------------------------------------------------------------------------
// DoT tick presets — per-status-effect visual
// ---------------------------------------------------------------------------

/** Green bubbling drips — poison DoT tick */
const dotPoison: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 150);
    shakeSprite(scene, sprite, 3);
    const cy = spriteCenterY(sprite);
    for (let i = 0; i < 5; i++) {
      const bubble = scene.add.graphics();
      bubble.fillStyle(tint, 0.85);
      bubble.fillCircle(0, 0, 2 + Math.random() * 2);
      const sx = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.4;
      bubble.setPosition(sx, cy + sprite.displayHeight * 0.15);
      bubble.setDepth(10000);
      scene.tweens.add({
        targets: bubble,
        y: cy - 15 - Math.random() * 20,
        x: sx + (Math.random() - 0.5) * 12,
        alpha: 0,
        scale: 0.3,
        duration: 350 + Math.random() * 150,
        delay: i * 40,
        ease: 'Power2.easeOut',
        onComplete: () => bubble.destroy(),
      });
    }
  },
};

/** Purple wisps dissolving outward — decay DoT tick */
const dotDecay: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 150);
    shakeSprite(scene, sprite, 3);
    const cy = spriteCenterY(sprite);
    for (let i = 0; i < 5; i++) {
      const wisp = scene.add.graphics();
      wisp.fillStyle(tint, 0.7);
      wisp.fillEllipse(0, 0, 6, 3);
      wisp.setPosition(sprite.x, cy);
      wisp.setDepth(10000);
      const angle = (Math.PI * 2 * i) / 5 + Math.random() * 0.3;
      const dist = 20 + Math.random() * 15;
      scene.tweens.add({
        targets: wisp,
        x: sprite.x + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.2,
        duration: 400,
        delay: i * 25,
        ease: 'Power3.easeOut',
        onComplete: () => wisp.destroy(),
      });
    }
  },
};

// ---------------------------------------------------------------------------
// Per-debuff application presets
// ---------------------------------------------------------------------------

/** Water splash droplets radiating outward — Soaked debuff */
const debuffSoaked: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 240);
    shakeSprite(scene, sprite, 3);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;
    const baseY = sprite.y; // ground line

    // Drench curtain — vertical sheets of water cascading down over the sprite
    const sheetCount = 4;
    for (let i = 0; i < sheetCount; i++) {
      const sheet = scene.add.graphics();
      sheet.setDepth(10000);
      const sx = cx + (i / (sheetCount - 1) - 0.5) * sw * 0.7;
      const wWidth = sw * 0.18;
      const wHeight = sh * 0.55;
      // Outer water sheet
      sheet.fillStyle(tint, 0.55);
      sheet.fillEllipse(0, 0, wWidth, wHeight);
      // Lighter inner streak
      sheet.fillStyle(0xddeeff, 0.6);
      sheet.fillEllipse(-wWidth * 0.12, 0, wWidth * 0.4, wHeight * 0.85);
      sheet.setPosition(sx, cy - sh * 0.6);
      sheet.setScale(1, 0.2);
      scene.tweens.add({
        targets: sheet,
        y: cy + sh * 0.05,
        scaleY: 1,
        alpha: 0,
        duration: 320,
        delay: i * 30,
        ease: 'Power2.easeIn',
        onComplete: () => sheet.destroy(),
      });
    }

    // Splash ring at the feet — flat ellipse expanding outward
    const splash = scene.add.graphics();
    splash.setDepth(10001);
    const splashData = { r: sw * 0.1, alpha: 0.85 };
    const drawSplash = () => {
      splash.clear();
      splash.lineStyle(4, tint, splashData.alpha);
      splash.strokeEllipse(cx, baseY - 4, splashData.r * 2, splashData.r * 0.55);
      splash.lineStyle(2, 0xddeeff, splashData.alpha * 0.7);
      splash.strokeEllipse(cx, baseY - 4, splashData.r * 1.5, splashData.r * 0.4);
    };
    drawSplash();
    scene.tweens.add({
      targets: splashData,
      r: sw * 0.55,
      alpha: 0,
      duration: 360,
      delay: 120,
      ease: 'Power2.easeOut',
      onUpdate: drawSplash,
      onComplete: () => splash.destroy(),
    });

    // Splash droplets kicked sideways/up from the splash ring
    for (let i = 0; i < 8; i++) {
      const drop = scene.add.graphics();
      drop.setDepth(10002);
      drop.fillStyle(tint, 0.95);
      drop.fillCircle(0, 0, 3);
      drop.fillStyle(0xffffff, 0.6);
      drop.fillCircle(-1, -1, 1.2);
      drop.setPosition(cx, baseY - 6);
      const sideways = (i % 2 === 0 ? 1 : -1) * (sw * 0.2 + Math.random() * sw * 0.25);
      const upward = sh * 0.15 + Math.random() * sh * 0.15;
      scene.tweens.add({
        targets: drop,
        x: cx + sideways,
        y: baseY - 6 - upward,
        alpha: 0,
        duration: 260,
        delay: 140 + i * 12,
        ease: 'Sine.easeOut',
        onComplete: () => drop.destroy(),
      });
    }

    // Lingering drip beads from sprite's lower edge
    for (let i = 0; i < 5; i++) {
      const drip = scene.add.graphics();
      drip.setDepth(10001);
      drip.fillStyle(tint, 0.85);
      drip.fillCircle(0, 0, 2.5);
      drip.fillTriangle(-2, 0, 2, 0, 0, 5);
      const dx = cx + (Math.random() - 0.5) * sw * 0.65;
      const startY = baseY - sh * 0.15 - Math.random() * sh * 0.1;
      drip.setPosition(dx, startY);
      scene.tweens.add({
        targets: drip,
        y: baseY - 2,
        alpha: 0,
        duration: 380,
        delay: 200 + i * 35,
        ease: 'Power1.easeIn',
        onComplete: () => drip.destroy(),
      });
    }
  },
};

/** Crosshair flash — Marked debuff */
const debuffMarked: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 250);
    const cy = spriteCenterY(sprite);
    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const size = sprite.displayHeight * 0.3;
    // Draw crosshair
    gfx.lineStyle(3, tint, 0.9);
    gfx.strokeCircle(sprite.x, cy, size * 0.5);
    gfx.lineBetween(sprite.x - size, cy, sprite.x + size, cy);
    gfx.lineBetween(sprite.x, cy - size, sprite.x, cy + size);
    gfx.setAlpha(0);
    scene.tweens.add({
      targets: gfx,
      alpha: 1,
      duration: 80,
      yoyo: true,
      hold: 150,
      ease: 'Sine.easeIn',
      onComplete: () => gfx.destroy(),
    });
  },
};

/** Impact shockwave + stun stars — Stagger debuff */
const debuffStagger: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 200);
    shakeSprite(scene, sprite, 6);
    const cy = spriteCenterY(sprite);
    // Expanding ring
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    const ringData = { radius: 5, alpha: 0.8 };
    scene.tweens.add({
      targets: ringData,
      radius: sprite.displayHeight * 0.4,
      alpha: 0,
      duration: 300,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(3, tint, ringData.alpha);
        ring.strokeCircle(sprite.x, cy, ringData.radius);
      },
      onComplete: () => ring.destroy(),
    });
    // Stun stars orbiting — sized to sprite, with white inner core for readability
    const armH = Math.max(14, sprite.displayHeight * 0.07);
    const armW = Math.max(7, sprite.displayHeight * 0.035);
    const innerH = armH * 0.55;
    const innerW = armW * 0.55;
    for (let i = 0; i < 6; i++) {
      const star = scene.add.graphics();
      // Outer golden 4-point star
      star.fillStyle(0xffdd44, 1);
      star.fillTriangle(-armW, 0, armW, 0, 0, -armH);
      star.fillTriangle(-armW, 0, armW, 0, 0, armH);
      star.fillTriangle(0, -armW, 0, armW, -armH, 0);
      star.fillTriangle(0, -armW, 0, armW, armH, 0);
      // White-hot inner core
      star.fillStyle(0xffffff, 0.95);
      star.fillTriangle(-innerW, 0, innerW, 0, 0, -innerH);
      star.fillTriangle(-innerW, 0, innerW, 0, 0, innerH);
      star.fillTriangle(0, -innerW, 0, innerW, -innerH, 0);
      star.fillTriangle(0, -innerW, 0, innerW, innerH, 0);
      const a = (Math.PI * 2 * i) / 6;
      const r = sprite.displayWidth * 0.32;
      star.setPosition(sprite.x + Math.cos(a) * r, cy - sprite.displayHeight * 0.32 + Math.sin(a) * 10);
      star.setDepth(10001);
      star.setScale(0.2);
      // Pop-in scale, then orbit + fade out
      scene.tweens.add({
        targets: star,
        scale: 1,
        duration: 140,
        ease: 'Back.easeOut',
      });
      scene.tweens.add({
        targets: star,
        x: sprite.x + Math.cos(a + Math.PI * 0.6) * r,
        rotation: Math.PI,
        alpha: 0,
        duration: 650,
        delay: 80 + i * 30,
        ease: 'Power2.easeOut',
        onComplete: () => star.destroy(),
      });
    }
  },
};

/** Cracking overlay lines — Fractured debuff */
const debuffFractured: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 220);
    shakeSprite(scene, sprite, 5);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;

    // Brief sprite scale pulse — body recoils from the impact
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.04,
      scaleY: bs * 0.96,
      duration: 70,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Jagged crack network — 6 main cracks, each branches into 1-2 sub-cracks
    type Pt = { x: number; y: number };
    const segments: { a: Pt; b: Pt }[] = [];
    const mainReach = sh * 0.48;
    const mainCount = 6;
    for (let i = 0; i < mainCount; i++) {
      const angle = (Math.PI * 2 * i) / mainCount + (Math.random() - 0.5) * 0.5;
      const reach = mainReach * (0.7 + Math.random() * 0.4);
      // Build a 3-segment jagged path
      const p0: Pt = { x: cx, y: cy };
      const midR = reach * 0.45;
      const midJitter = sh * 0.05;
      const p1: Pt = {
        x: cx + Math.cos(angle) * midR + (Math.random() - 0.5) * midJitter,
        y: cy + Math.sin(angle) * midR + (Math.random() - 0.5) * midJitter,
      };
      const p2: Pt = {
        x: cx + Math.cos(angle) * reach + (Math.random() - 0.5) * midJitter,
        y: cy + Math.sin(angle) * reach + (Math.random() - 0.5) * midJitter,
      };
      segments.push({ a: p0, b: p1 });
      segments.push({ a: p1, b: p2 });

      // Sub-crack branching off the midpoint, ~70% of the time
      if (Math.random() < 0.7) {
        const branchAngle = angle + (Math.random() < 0.5 ? -1 : 1) * (Math.PI * 0.35);
        const branchLen = reach * 0.4;
        const p3: Pt = {
          x: p1.x + Math.cos(branchAngle) * branchLen,
          y: p1.y + Math.sin(branchAngle) * branchLen,
        };
        segments.push({ a: p1, b: p3 });
      }
    }

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const drawCracks = (alpha: number, glowExtra: number) => {
      gfx.clear();
      // Outer dark crack
      gfx.lineStyle(4 + glowExtra, tint, alpha);
      for (const seg of segments) gfx.lineBetween(seg.a.x, seg.a.y, seg.b.x, seg.b.y);
      // Inner white-hot crack core (the fracture light)
      gfx.lineStyle(1.5 + glowExtra * 0.4, 0xffffff, alpha * 0.85);
      for (const seg of segments) gfx.lineBetween(seg.a.x, seg.a.y, seg.b.x, seg.b.y);
    };

    // Phase 1: cracks SNAP in with a glow flare
    drawCracks(0, 4);
    const snap = { alpha: 0, glow: 4 };
    scene.tweens.add({
      targets: snap,
      alpha: 1,
      glow: 0,
      duration: 80,
      ease: 'Power3.easeOut',
      onUpdate: () => drawCracks(snap.alpha, snap.glow),
      onComplete: () => {
        // Phase 2: hold + fade
        scene.tweens.add({
          targets: snap,
          alpha: 0,
          duration: 280,
          delay: 100,
          ease: 'Sine.easeIn',
          onUpdate: () => drawCracks(snap.alpha, 0),
          onComplete: () => gfx.destroy(),
        });
      },
    });

    // Stone/armor flecks ejected from impact center
    for (let i = 0; i < 7; i++) {
      const fleck = scene.add.graphics();
      fleck.setDepth(10001);
      fleck.fillStyle(tint, 0.9);
      const fw = 2 + Math.random() * 2;
      const fh = 3 + Math.random() * 3;
      fleck.fillTriangle(-fw, fh, fw, fh, 0, -fh);
      fleck.setPosition(cx, cy);
      const angle = Math.random() * Math.PI * 2;
      const dist = sh * 0.2 + Math.random() * sh * 0.2;
      scene.tweens.add({
        targets: fleck,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist + sh * 0.1, // gravity drift
        rotation: (Math.random() - 0.5) * Math.PI * 2,
        alpha: 0,
        duration: 380,
        delay: 30 + i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => fleck.destroy(),
      });
    }
  },
};

/** Armor-peel ripping downward shards — Sundered debuff */
const debuffSundered: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 220);
    shakeSprite(scene, sprite, 6);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;

    // Big horizontal rip slash flashes across the chest area
    const ripGfx = scene.add.graphics();
    ripGfx.setDepth(10001);
    const rip = { alpha: 1, sweep: 0 };
    const ripY = cy - sh * 0.05;
    const ripReach = sw * 0.7;
    const drawRip = () => {
      ripGfx.clear();
      const halfReach = ripReach * 0.5;
      const headX = cx - halfReach + ripReach * rip.sweep;
      // Outer tint slash
      ripGfx.lineStyle(7, tint, rip.alpha);
      ripGfx.lineBetween(cx - halfReach, ripY - 8, headX, ripY + 8);
      // Inner white core for readability
      ripGfx.lineStyle(2, 0xffffff, rip.alpha * 0.85);
      ripGfx.lineBetween(cx - halfReach, ripY - 8, headX, ripY + 8);
    };
    drawRip();
    scene.tweens.add({
      targets: rip,
      sweep: 1,
      duration: 110,
      ease: 'Power3.easeOut',
      onUpdate: drawRip,
      onComplete: () => {
        scene.tweens.add({
          targets: rip,
          alpha: 0,
          duration: 140,
          ease: 'Sine.easeIn',
          onUpdate: drawRip,
          onComplete: () => ripGfx.destroy(),
        });
      },
    });

    // Big armor plate fragments ripped outward then tumble down with gravity
    const plateCount = 7;
    for (let i = 0; i < plateCount; i++) {
      const plate = scene.add.graphics();
      plate.setDepth(10000);
      const pw = sh * 0.04 + Math.random() * sh * 0.03;
      const ph = sh * 0.06 + Math.random() * sh * 0.04;
      // Outer tinted plate
      plate.fillStyle(tint, 0.9);
      plate.fillTriangle(-pw, -ph * 0.5, pw, -ph * 0.5, pw * 0.4, ph);
      plate.fillTriangle(-pw, -ph * 0.5, pw * 0.4, ph, -pw * 0.6, ph * 0.7);
      // Inner highlight
      plate.fillStyle(0xffffff, 0.4);
      plate.fillTriangle(-pw * 0.4, -ph * 0.3, pw * 0.3, -ph * 0.3, 0, ph * 0.2);

      const sx = cx + (i / (plateCount - 1) - 0.5) * sw * 0.5;
      const sy = ripY + (Math.random() - 0.5) * sh * 0.06;
      plate.setPosition(sx, sy);
      plate.setRotation(Math.random() * Math.PI * 2);

      // Phase 1: ejected outward (60ms)
      const dirX = (Math.random() - 0.5) * 1.5;
      const ejectX = sx + dirX * sw * 0.18;
      const ejectY = sy - sh * 0.06;
      scene.tweens.add({
        targets: plate,
        x: ejectX,
        y: ejectY,
        duration: 90,
        delay: i * 12,
        ease: 'Power2.easeOut',
        onComplete: () => {
          // Phase 2: gravity tumble down past the sprite base
          scene.tweens.add({
            targets: plate,
            x: ejectX + dirX * sw * 0.15,
            y: sprite.y + sh * 0.15,
            rotation: plate.rotation + (Math.random() - 0.5) * Math.PI * 3,
            alpha: 0,
            duration: 380,
            ease: 'Power2.easeIn',
            onComplete: () => plate.destroy(),
          });
        },
      });
    }
  },
};

/** Downward drain wisps — Weakened debuff */
const debuffWeakened: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 280);
    shakeSprite(scene, sprite, 2);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;
    const baseY = sprite.y;

    // Sprite slumps — visible droop in scale + tiny Y nudge to sell exhaustion
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleY: bs * 0.94,
      duration: 200,
      yoyo: true,
      hold: 80,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Large central drain orb that bleeds energy downward — represents life being pulled out
    const orb = scene.add.graphics();
    orb.setDepth(10000);
    const orbData = { r: sh * 0.18, alpha: 0.85, sag: 0 };
    const drawOrb = () => {
      orb.clear();
      // Outer aura
      orb.fillStyle(tint, 0.4 * orbData.alpha);
      orb.fillCircle(cx, cy + orbData.sag, orbData.r * 1.4);
      // Inner core
      orb.fillStyle(tint, 0.85 * orbData.alpha);
      orb.fillCircle(cx, cy + orbData.sag, orbData.r);
      // Bright pulse center
      orb.fillStyle(0xffffff, 0.6 * orbData.alpha);
      orb.fillCircle(cx, cy + orbData.sag, orbData.r * 0.4);
    };
    drawOrb();
    scene.tweens.add({
      targets: orbData,
      r: sh * 0.06,
      alpha: 0,
      sag: sh * 0.25,
      duration: 480,
      ease: 'Power1.easeIn',
      onUpdate: drawOrb,
      onComplete: () => orb.destroy(),
    });

    // Big drooping wisps — life essence bleeding downward
    const wispCount = 7;
    for (let i = 0; i < wispCount; i++) {
      const wisp = scene.add.graphics();
      wisp.setDepth(10001);
      // Outer tinted wisp
      const ww = sh * 0.04 + Math.random() * sh * 0.02;
      const wh = sh * 0.07 + Math.random() * sh * 0.03;
      wisp.fillStyle(tint, 0.75);
      wisp.fillEllipse(0, 0, ww, wh);
      // Inner brighter core
      wisp.fillStyle(0xffffff, 0.5);
      wisp.fillEllipse(0, -wh * 0.1, ww * 0.45, wh * 0.55);

      const sx = cx + (Math.random() - 0.5) * sw * 0.6;
      const sy = cy - sh * 0.05 + Math.random() * sh * 0.1;
      wisp.setPosition(sx, sy);
      wisp.setScale(0.6);

      const fallY = baseY + sh * 0.15 + Math.random() * sh * 0.1;
      const drift = (Math.random() - 0.5) * sw * 0.18;
      scene.tweens.add({
        targets: wisp,
        y: fallY,
        x: sx + drift,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.2,
        duration: 480 + Math.random() * 100,
        delay: i * 35,
        ease: 'Sine.easeIn',
        onComplete: () => wisp.destroy(),
      });
    }

    // Dim grey ground puddle of drained essence beneath the sprite
    const puddle = scene.add.graphics();
    puddle.setDepth(9999);
    const pData = { alpha: 0, w: sw * 0.4, h: sh * 0.04 };
    scene.tweens.add({
      targets: pData,
      alpha: 0.5,
      duration: 200,
      yoyo: true,
      hold: 250,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        puddle.clear();
        puddle.fillStyle(tint, pData.alpha);
        puddle.fillEllipse(cx, baseY - 4, pData.w, pData.h);
      },
      onComplete: () => puddle.destroy(),
    });
  },
};

/** Ignite flare — Burn application (distinct from dot_burn tick) */
const debuffBurn: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 240);
    shakeSprite(scene, sprite, 5);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;
    const flareR = sh * 0.55;

    // Brief sprite scale punch — feels like the body flinches at ignition
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.05,
      scaleY: bs * 1.05,
      duration: 80,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Central ignite flare — radial whoosh, tint ring + white-hot core
    const flare = scene.add.graphics();
    flare.setDepth(10000);
    const fx = { r: flareR * 0.15, alpha: 1 };
    const drawFlare = () => {
      flare.clear();
      // Outer tint halo
      flare.fillStyle(tint, 0.55 * fx.alpha);
      flare.fillCircle(cx, cy, fx.r);
      // Mid-band golden
      flare.fillStyle(0xffaa22, 0.7 * fx.alpha);
      flare.fillCircle(cx, cy, fx.r * 0.65);
      // White-hot core
      flare.fillStyle(0xffffcc, 0.95 * fx.alpha);
      flare.fillCircle(cx, cy, fx.r * 0.32);
    };
    drawFlare();
    scene.tweens.add({
      targets: fx,
      r: flareR,
      alpha: 0,
      duration: 220,
      ease: 'Power2.easeOut',
      onUpdate: drawFlare,
      onComplete: () => flare.destroy(),
    });

    // Big upward flame wisps — layered tint + inner golden, varied sizes
    const flameCount = 9;
    for (let i = 0; i < flameCount; i++) {
      const flame = scene.add.graphics();
      flame.setDepth(10001);
      // Larger triangle — 14-22px tall, 10-14px wide
      const w = 5 + Math.random() * 2;
      const h = 14 + Math.random() * 8;
      // Outer tint layer
      flame.fillStyle(tint, 0.95);
      flame.fillTriangle(-w, h * 0.4, w, h * 0.4, 0, -h * 0.6);
      // Inner golden core
      flame.fillStyle(0xffcc33, 0.85);
      flame.fillTriangle(-w * 0.55, h * 0.25, w * 0.55, h * 0.25, 0, -h * 0.35);

      const sx = cx + (Math.random() - 0.5) * sw * 0.85;
      const sy = cy + sh * 0.05;
      flame.setPosition(sx, sy);
      const baseScale = 0.9 + Math.random() * 0.5;
      flame.setScale(baseScale);

      const rise = sh * 0.45 + Math.random() * sh * 0.2;
      scene.tweens.add({
        targets: flame,
        y: sy - rise,
        x: sx + (Math.random() - 0.5) * 25,
        alpha: 0,
        scaleX: baseScale * 0.4,
        scaleY: baseScale * 0.5,
        duration: 380 + Math.random() * 140,
        delay: i * 18,
        ease: 'Power2.easeOut',
        onComplete: () => flame.destroy(),
      });
    }

    // Ember sparks — small bright dots that arc upward and out
    for (let i = 0; i < 8; i++) {
      const spark = scene.add.graphics();
      spark.setDepth(10002);
      spark.fillStyle(0xffeeaa, 1);
      spark.fillCircle(0, 0, 1.5);
      spark.setPosition(cx, cy);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
      const dist = 35 + Math.random() * 30;
      scene.tweens.add({
        targets: spark,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        duration: 420 + Math.random() * 120,
        delay: 40 + i * 10,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  },
};

/** Slash mark flash — Bleed application (distinct from dot_bleed drip) */
const debuffBleed: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 220);
    shakeSprite(scene, sprite, 5);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;

    // Three big diagonal gashes carved across the sprite, drawn in sequence
    const gashes = [
      { offsetY: -sh * 0.15, length: sw * 0.55 },
      { offsetY: sh * 0.0,   length: sw * 0.65 },
      { offsetY: sh * 0.15,  length: sw * 0.5 },
    ];

    gashes.forEach((g, gi) => {
      const gfx = scene.add.graphics();
      gfx.setDepth(10000 + gi);
      const half = g.length * 0.5;
      // Diagonal angle (45-ish, slightly varied)
      const ang = Math.PI * 0.25 + (gi - 1) * 0.1;
      const dx = Math.cos(ang) * half;
      const dy = Math.sin(ang) * half;
      const ax = cx - dx;
      const ay = cy + g.offsetY - dy;
      const bx = cx + dx;
      const by = cy + g.offsetY + dy;

      const draw = { progress: 0, alpha: 1 };
      const render = () => {
        gfx.clear();
        const headX = ax + (bx - ax) * draw.progress;
        const headY = ay + (by - ay) * draw.progress;
        // Outer wide tinted slash
        gfx.lineStyle(8, tint, 0.95 * draw.alpha);
        gfx.lineBetween(ax, ay, headX, headY);
        // Inner bright core
        gfx.lineStyle(3, 0xffaaaa, 0.9 * draw.alpha);
        gfx.lineBetween(ax, ay, headX, headY);
      };

      // Phase 1: gash carves across (60ms, fast)
      scene.tweens.add({
        targets: draw,
        progress: 1,
        duration: 60,
        delay: gi * 50,
        ease: 'Power3.easeOut',
        onUpdate: render,
        onComplete: () => {
          // Phase 2: hold then fade
          scene.tweens.add({
            targets: draw,
            alpha: 0,
            duration: 220,
            delay: 80,
            ease: 'Sine.easeIn',
            onUpdate: render,
            onComplete: () => gfx.destroy(),
          });

          // Blood beads dripping from each gash mid-point
          for (let b = 0; b < 3; b++) {
            const bead = scene.add.graphics();
            bead.setDepth(10005);
            bead.fillStyle(tint, 0.95);
            bead.fillCircle(0, 0, 2.5);
            bead.fillTriangle(-2, 0, 2, 0, 0, 5);
            const beadX = cx + (Math.random() - 0.5) * g.length * 0.7;
            const beadY = cy + g.offsetY;
            bead.setPosition(beadX, beadY);
            scene.tweens.add({
              targets: bead,
              y: beadY + sh * 0.18 + Math.random() * sh * 0.1,
              alpha: 0,
              duration: 380,
              delay: 40 + b * 30,
              ease: 'Power1.easeIn',
              onComplete: () => bead.destroy(),
            });
          }
        },
      });
    });
  },
};

/** Green gas puff cloud — Poison application */
const debuffPoison: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 240);
    shakeSprite(scene, sprite, 2);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;

    // Central toxic eruption — large dense puff that expands rapidly
    const central = scene.add.graphics();
    central.setDepth(10001);
    const c = { r: sh * 0.1, alpha: 0.85 };
    const drawCentral = () => {
      central.clear();
      // Outer translucent halo
      central.fillStyle(tint, 0.35 * c.alpha);
      central.fillCircle(cx, cy, c.r * 1.3);
      // Mid body
      central.fillStyle(tint, 0.7 * c.alpha);
      central.fillCircle(cx, cy, c.r);
      // Bright center
      central.fillStyle(0xccff88, 0.6 * c.alpha);
      central.fillCircle(cx, cy, c.r * 0.4);
    };
    drawCentral();
    scene.tweens.add({
      targets: c,
      r: sh * 0.32,
      alpha: 0,
      duration: 380,
      ease: 'Power2.easeOut',
      onUpdate: drawCentral,
      onComplete: () => central.destroy(),
    });

    // Surrounding toxic puffs that drift up and outward — bigger and more numerous
    const puffCount = 9;
    for (let i = 0; i < puffCount; i++) {
      const puff = scene.add.graphics();
      puff.setDepth(10000);
      const baseR = sh * 0.05 + Math.random() * sh * 0.04;
      // Layered puff: outer dim + inner brighter (reads as cloud, not flat circle)
      puff.fillStyle(tint, 0.55);
      puff.fillCircle(0, 0, baseR);
      puff.fillStyle(tint, 0.85);
      puff.fillCircle(-baseR * 0.2, -baseR * 0.2, baseR * 0.6);
      puff.fillStyle(0xccff88, 0.5);
      puff.fillCircle(-baseR * 0.3, -baseR * 0.3, baseR * 0.3);

      const angle = (Math.PI * 2 * i) / puffCount + (Math.random() - 0.5) * 0.4;
      const startDist = sw * 0.05;
      const sx = cx + Math.cos(angle) * startDist;
      const sy = cy + Math.sin(angle) * startDist;
      puff.setPosition(sx, sy);
      puff.setScale(0.5);

      const driftDist = sw * 0.25 + Math.random() * sw * 0.15;
      const upward = sh * 0.15 + Math.random() * sh * 0.12;
      scene.tweens.add({
        targets: puff,
        x: sx + Math.cos(angle) * driftDist,
        y: sy + Math.sin(angle) * driftDist - upward,
        scaleX: 1.4,
        scaleY: 1.4,
        alpha: 0,
        duration: 500 + Math.random() * 150,
        delay: 30 + i * 22,
        ease: 'Power2.easeOut',
        onComplete: () => puff.destroy(),
      });
    }

    // Toxic bubble droplets popping up briefly — adds the "bubbling toxic" feel
    for (let i = 0; i < 5; i++) {
      const bubble = scene.add.graphics();
      bubble.setDepth(10002);
      const br = 2 + Math.random() * 2;
      bubble.lineStyle(1.5, 0xccff88, 0.95);
      bubble.strokeCircle(0, 0, br);
      bubble.fillStyle(tint, 0.5);
      bubble.fillCircle(0, 0, br - 0.5);
      const sx = cx + (Math.random() - 0.5) * sw * 0.5;
      const sy = cy + (Math.random() - 0.5) * sh * 0.15;
      bubble.setPosition(sx, sy);
      scene.tweens.add({
        targets: bubble,
        y: sy - sh * 0.12,
        alpha: 0,
        duration: 350,
        delay: 80 + i * 40,
        ease: 'Sine.easeOut',
        onComplete: () => bubble.destroy(),
      });
    }
  },
};

/** Dark dissolve mist — Decay application */
const debuffDecay: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 250);
    const cy = spriteCenterY(sprite);
    // Mist particles that expand and fade
    for (let i = 0; i < 5; i++) {
      const mist = scene.add.graphics();
      mist.fillStyle(tint, 0.4);
      mist.fillEllipse(0, 0, 10 + Math.random() * 8, 5 + Math.random() * 4);
      const sx = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.4;
      mist.setPosition(sx, cy);
      mist.setDepth(10000);
      const dir = Math.random() > 0.5 ? 1 : -1;
      scene.tweens.add({
        targets: mist,
        x: sx + dir * (15 + Math.random() * 15),
        y: cy + (Math.random() - 0.5) * 20,
        scaleX: 2,
        scaleY: 2,
        alpha: 0,
        duration: 500,
        delay: i * 40,
        ease: 'Power2.easeOut',
        onComplete: () => mist.destroy(),
      });
    }
    // Subtle shrink pulse
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 0.97,
      scaleY: bs * 0.97,
      duration: 120,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

// ---------------------------------------------------------------------------
// Per-buff application presets
// ---------------------------------------------------------------------------

/** Golden upward power surge — Empower buff */
const buffEmpower: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xffdd44, 280);

    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;
    const baseY = sprite.y;

    // Power-up scale punch
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.08,
      scaleY: bs * 1.08,
      duration: 140,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Central radiant flare — golden core that snaps in from below
    const flare = scene.add.graphics();
    flare.setDepth(10000);
    const fx = { r: sh * 0.1, alpha: 1 };
    const drawFlare = () => {
      flare.clear();
      // Outer golden halo
      flare.fillStyle(0xffaa22, 0.45 * fx.alpha);
      flare.fillCircle(cx, cy, fx.r * 1.3);
      // Mid golden body
      flare.fillStyle(0xffdd44, 0.75 * fx.alpha);
      flare.fillCircle(cx, cy, fx.r);
      // White-hot core
      flare.fillStyle(0xffffff, 0.9 * fx.alpha);
      flare.fillCircle(cx, cy, fx.r * 0.4);
    };
    drawFlare();
    scene.tweens.add({
      targets: fx,
      r: sh * 0.4,
      alpha: 0,
      duration: 320,
      ease: 'Power2.easeOut',
      onUpdate: drawFlare,
      onComplete: () => flare.destroy(),
    });

    // Upward radiant rays from the base — sells the "rising power" feel
    const rayCount = 6;
    for (let i = 0; i < rayCount; i++) {
      const ray = scene.add.graphics();
      ray.setDepth(10001);
      const rw = 4 + Math.random() * 2;
      const rh = sh * 0.55 + Math.random() * sh * 0.15;
      // Outer golden tapered beam
      ray.fillStyle(0xffdd44, 0.85);
      ray.fillTriangle(-rw, rh, rw, rh, 0, 0);
      // Inner white-hot beam
      ray.fillStyle(0xffffff, 0.7);
      ray.fillTriangle(-rw * 0.4, rh, rw * 0.4, rh, 0, rh * 0.1);

      const sx = cx + (i / (rayCount - 1) - 0.5) * sw * 0.65;
      ray.setPosition(sx, baseY);
      ray.setScale(1, 0);
      ray.setAlpha(0);

      scene.tweens.add({
        targets: ray,
        scaleY: 1,
        alpha: 1,
        duration: 100,
        delay: i * 18,
        ease: 'Power3.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: ray,
            alpha: 0,
            scaleY: 1.1,
            duration: 220,
            delay: 60,
            ease: 'Sine.easeIn',
            onComplete: () => ray.destroy(),
          });
        },
      });
    }

    // Ascending sparkles — bright dots rising from base, scattered, varied speeds
    const sparkCount = 12;
    for (let i = 0; i < sparkCount; i++) {
      const spark = scene.add.graphics();
      spark.setDepth(10002);
      // Diamond sparkle — bigger and more varied than rectangles
      const sz = 2 + Math.random() * 2;
      const color = i % 3 === 0 ? 0xffffff : 0xffdd44;
      spark.fillStyle(color, 1);
      spark.fillTriangle(-sz, 0, sz, 0, 0, -sz * 1.6);
      spark.fillTriangle(-sz, 0, sz, 0, 0, sz * 1.6);
      const sx = cx + (Math.random() - 0.5) * sw * 0.7;
      spark.setPosition(sx, baseY - sh * 0.05);
      const rise = sh * 0.6 + Math.random() * sh * 0.25;
      const drift = (Math.random() - 0.5) * sw * 0.15;
      scene.tweens.add({
        targets: spark,
        y: baseY - rise,
        x: sx + drift,
        alpha: 0,
        rotation: (Math.random() - 0.5) * Math.PI,
        duration: 460 + Math.random() * 140,
        delay: i * 18,
        ease: 'Sine.easeOut',
        onComplete: () => spark.destroy(),
      });
    }

    // Ground impact ring — golden disc expanding outward at the feet
    const ring = scene.add.graphics();
    ring.setDepth(9999);
    const ringData = { r: sw * 0.1, alpha: 0.85 };
    const drawRing = () => {
      ring.clear();
      ring.lineStyle(4, 0xffdd44, ringData.alpha);
      ring.strokeEllipse(cx, baseY - 4, ringData.r * 2, ringData.r * 0.5);
      ring.lineStyle(2, 0xffffff, ringData.alpha * 0.7);
      ring.strokeEllipse(cx, baseY - 4, ringData.r * 1.5, ringData.r * 0.4);
    };
    drawRing();
    scene.tweens.add({
      targets: ringData,
      r: sw * 0.45,
      alpha: 0,
      duration: 350,
      ease: 'Power2.easeOut',
      onUpdate: drawRing,
      onComplete: () => ring.destroy(),
    });
  },
};

/** Red pulsing aggressive aura — Fury buff */
const buffFury: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xff4444, 250);
    const cy = spriteCenterY(sprite);
    // Aggressive outward burst particles
    for (let i = 0; i < 6; i++) {
      const spark = scene.add.graphics();
      spark.fillStyle(0xff4444, 0.85);
      spark.fillTriangle(-3, 0, 3, 0, 0, -5);
      spark.setPosition(sprite.x, cy);
      spark.setDepth(10000);
      const angle = (Math.PI * 2 * i) / 6;
      const dist = 20 + Math.random() * 15;
      scene.tweens.add({
        targets: spark,
        x: sprite.x + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.5,
        rotation: angle,
        duration: 300,
        delay: i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
    // Scale pulse — bigger than normal
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.08,
      scaleY: bs * 1.08,
      duration: 100,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Swirling wind lines rushing upward — Tailwind buff */
const buffTailwind: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0x99ddcc, 200);
    const baseY = sprite.y;
    const cx = sprite.x;
    for (let i = 0; i < 5; i++) {
      const line = scene.add.graphics();
      line.lineStyle(2, 0x99ddcc, 0.7);
      // Curved wind line
      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.6;
      line.lineBetween(startX, baseY, startX + 8, baseY - 15);
      line.setDepth(10000);
      scene.tweens.add({
        targets: line,
        y: -sprite.displayHeight * 0.7,
        alpha: 0,
        duration: 350 + Math.random() * 100,
        delay: i * 50,
        ease: 'Power2.easeOut',
        onComplete: () => line.destroy(),
      });
    }
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Green coating drip/sheen — Venomcoat buff */
const buffVenomcoat: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0x88cc44, 250);
    const cy = spriteCenterY(sprite);
    // Green droplets sliding down the sprite
    for (let i = 0; i < 5; i++) {
      const drop = scene.add.graphics();
      drop.fillStyle(0x88cc44, 0.7);
      drop.fillCircle(0, 0, 2.5);
      const sx = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.4;
      drop.setPosition(sx, cy - sprite.displayHeight * 0.25);
      drop.setDepth(10000);
      scene.tweens.add({
        targets: drop,
        y: cy + sprite.displayHeight * 0.3,
        alpha: 0,
        scale: 0.4,
        duration: 400 + Math.random() * 100,
        delay: i * 40,
        ease: 'Power1.easeIn',
        onComplete: () => drop.destroy(),
      });
    }
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Metallic plate flash — Iron Wall buff */
const buffIronWall: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xaabbcc, 250);
    const cy = spriteCenterY(sprite);
    // Brief rectangular shield flash overlay
    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    gfx.fillStyle(0xccccdd, 0.3);
    const w = sprite.displayWidth * 0.6;
    const h = sprite.displayHeight * 0.7;
    gfx.fillRoundedRect(sprite.x - w / 2, cy - h / 2, w, h, 6);
    gfx.lineStyle(2, 0xeeeeff, 0.6);
    gfx.strokeRoundedRect(sprite.x - w / 2, cy - h / 2, w, h, 6);
    gfx.setAlpha(0);
    scene.tweens.add({
      targets: gfx,
      alpha: 1,
      duration: 80,
      yoyo: true,
      hold: 150,
      ease: 'Sine.easeIn',
      onComplete: () => gfx.destroy(),
    });
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.04,
      scaleY: bs * 1.04,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Steel reinforcement shimmer — Brace buff */
const buffBrace: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xc0c0d0, 250);
    const cy = spriteCenterY(sprite);
    // Small metallic sparks rising from base
    for (let i = 0; i < 4; i++) {
      const spark = scene.add.graphics();
      const color = i % 2 === 0 ? 0xddddee : 0xffffff;
      spark.fillStyle(color, 0.8);
      spark.fillRect(-1.5, -1.5, 3, 3);
      const sx = sprite.x + (Math.random() - 0.5) * sprite.displayWidth * 0.4;
      spark.setPosition(sx, sprite.y);
      spark.setDepth(10000);
      scene.tweens.add({
        targets: spark,
        y: cy - Math.random() * 15,
        alpha: 0,
        duration: 350,
        delay: i * 35,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 80,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Arcane convergence — 6 sparkles spiral INWARD to the target, flash on arrival. Catalyze buff. */
const buffCatalyze: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xcc88ff, 280);
    const cy = spriteCenterY(sprite);
    // Converging sparkles from a wide ring inward
    for (let i = 0; i < 8; i++) {
      const spark = scene.add.graphics();
      const color = i % 3 === 0 ? 0xffffff : 0xcc88ff;
      spark.fillStyle(color, 0.9);
      // Diamond shape — arcane/crystalline feel
      spark.fillTriangle(0, -4, 3, 0, 0, 4);
      spark.fillTriangle(0, -4, -3, 0, 0, 4);
      spark.setDepth(10000);
      const angle = (Math.PI * 2 * i) / 8;
      const dist = 35 + Math.random() * 10;
      const startX = sprite.x + Math.cos(angle) * dist;
      const startY = cy + Math.sin(angle) * dist;
      spark.setPosition(startX, startY);
      scene.tweens.add({
        targets: spark,
        x: sprite.x,
        y: cy,
        alpha: 0,
        rotation: Math.PI,
        duration: 250,
        delay: i * 20,
        ease: 'Power3.easeIn',
        onComplete: () => spark.destroy(),
      });
    }
    // Flash at convergence point
    scene.time.delayedCall(220, () => {
      const flash = scene.add.graphics();
      flash.fillStyle(0xffffff, 0.8);
      flash.fillCircle(0, 0, 8);
      flash.setPosition(sprite.x, cy);
      flash.setDepth(10001);
      scene.tweens.add({
        targets: flash,
        alpha: 0,
        scale: 2.5,
        duration: 200,
        ease: 'Power2.easeOut',
        onComplete: () => flash.destroy(),
      });
    });
    // Tight scale pulse
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.05,
      scaleY: bs * 1.05,
      duration: 100,
      delay: 200,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Dark threads weaving upward — necromantic energy stitched into the target. Soulstitch buff. */
const buffSoulstitch: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0x8844cc, 300);
    const baseY = sprite.y;
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    // Dark thread lines crawling up the sprite
    for (let i = 0; i < 5; i++) {
      const thread = scene.add.graphics();
      thread.lineStyle(2, i % 2 === 0 ? 0x8844cc : 0x553399, 0.8);
      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.5;
      // Sinuous thread path
      const midX = startX + (Math.random() - 0.5) * 12;
      thread.lineBetween(startX, baseY, midX, cy);
      thread.setPosition(0, 0);
      thread.setDepth(10000);
      thread.setAlpha(0);
      scene.tweens.add({
        targets: thread,
        alpha: 0.8,
        duration: 120,
        delay: i * 50,
        ease: 'Sine.easeIn',
      });
      scene.tweens.add({
        targets: thread,
        alpha: 0,
        duration: 250,
        delay: i * 50 + 180,
        ease: 'Power2.easeOut',
        onComplete: () => thread.destroy(),
      });
    }
    // Small dark wisps rising from sprite base
    for (let i = 0; i < 4; i++) {
      const wisp = scene.add.graphics();
      wisp.fillStyle(0x8844cc, 0.7);
      wisp.fillCircle(0, 0, 2.5);
      const sx = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.4;
      wisp.setPosition(sx, baseY);
      wisp.setDepth(10000);
      scene.tweens.add({
        targets: wisp,
        y: cy - 15 - Math.random() * 20,
        x: sx + (Math.random() - 0.5) * 10,
        alpha: 0,
        duration: 400,
        delay: 80 + i * 40,
        ease: 'Power1.easeOut',
        onComplete: () => wisp.destroy(),
      });
    }
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.04,
      scaleY: bs * 1.04,
      duration: 100,
      delay: 80,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Expanding power rings — amplification surge. Intensify buff. */
const buffIntensify: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xff9944, 280);
    const cy = spriteCenterY(sprite);
    // Two concentric rings expanding outward
    for (let ring = 0; ring < 2; ring++) {
      const circle = scene.add.graphics();
      circle.lineStyle(3 - ring, ring === 0 ? 0xff9944 : 0xffcc66, 0.9);
      circle.strokeCircle(0, 0, 4);
      circle.setPosition(sprite.x, cy);
      circle.setDepth(10000);
      scene.tweens.add({
        targets: circle,
        scale: 6 + ring * 3,
        alpha: 0,
        duration: 350,
        delay: ring * 80,
        ease: 'Power2.easeOut',
        onComplete: () => circle.destroy(),
      });
    }
    // Hot sparks radiating outward
    for (let i = 0; i < 6; i++) {
      const spark = scene.add.graphics();
      spark.fillStyle(i % 2 === 0 ? 0xff9944 : 0xffffff, 0.85);
      spark.fillRect(-1.5, -3, 3, 6);
      spark.setPosition(sprite.x, cy);
      spark.setDepth(10000);
      const angle = (Math.PI * 2 * i) / 6;
      const dist = 25 + Math.random() * 10;
      scene.tweens.add({
        targets: spark,
        x: sprite.x + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        rotation: angle,
        duration: 280,
        delay: 40 + i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
    // Dramatic scale pulse
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.08,
      scaleY: bs * 1.08,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Temporal ripple sweep — a wave of light rolls bottom-to-top. Extend buff. */
const buffExtend: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xffdd88, 250);
    const baseY = sprite.y;
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const topY = sprite.y - sprite.displayHeight;
    // Horizontal shimmer line sweeping upward
    const sweep = scene.add.graphics();
    sweep.lineStyle(2, 0xffdd88, 0.8);
    const halfW = sprite.displayWidth * 0.4;
    sweep.lineBetween(-halfW, 0, halfW, 0);
    sweep.setPosition(cx, baseY);
    sweep.setDepth(10000);
    scene.tweens.add({
      targets: sweep,
      y: topY,
      alpha: 0,
      duration: 350,
      ease: 'Sine.easeInOut',
      onComplete: () => sweep.destroy(),
    });
    // Trailing sparkles behind the sweep
    for (let i = 0; i < 5; i++) {
      scene.time.delayedCall(i * 60, () => {
        const sparkle = scene.add.graphics();
        sparkle.fillStyle(i % 2 === 0 ? 0xffdd88 : 0xffffff, 0.7);
        sparkle.fillCircle(0, 0, 2);
        const sx = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.5;
        const sy = baseY - ((baseY - topY) * (i / 5));
        sparkle.setPosition(sx, sy);
        sparkle.setDepth(10000);
        scene.tweens.add({
          targets: sparkle,
          y: sy - 12,
          alpha: 0,
          duration: 300,
          ease: 'Power2.easeOut',
          onComplete: () => sparkle.destroy(),
        });
      });
    }
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 100,
      delay: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Cosmic shield — descending star particles settling into a protective ring. Ward buff. */
const buffWard: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0x6688dd, 300);
    const cy = spriteCenterY(sprite);
    const cx = sprite.x;
    // Star particles descending from above and settling at center-mass
    for (let i = 0; i < 7; i++) {
      const star = scene.add.graphics();
      const color = [0x6688dd, 0x8866cc, 0xffffff][i % 3];
      star.fillStyle(color, 0.85);
      // 4-pointed star shape
      star.fillTriangle(0, -4, 2, 0, 0, 4);
      star.fillTriangle(0, -4, -2, 0, 0, 4);
      star.fillTriangle(-4, 0, 0, 2, 4, 0);
      star.fillTriangle(-4, 0, 0, -2, 4, 0);
      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.8;
      const startY = cy - sprite.displayHeight * 0.6 - Math.random() * 20;
      star.setPosition(startX, startY);
      star.setDepth(10000);
      // Settle into a ring around center
      const angle = (Math.PI * 2 * i) / 7;
      const ringDist = sprite.displayWidth * 0.35;
      scene.tweens.add({
        targets: star,
        x: cx + Math.cos(angle) * ringDist,
        y: cy + Math.sin(angle) * ringDist * 0.4,
        alpha: 0.5,
        duration: 250,
        delay: i * 30,
        ease: 'Power2.easeOut',
      });
      // Then fade out
      scene.tweens.add({
        targets: star,
        alpha: 0,
        scale: 0.3,
        duration: 200,
        delay: i * 30 + 280,
        ease: 'Power2.easeIn',
        onComplete: () => star.destroy(),
      });
    }
    // Brief protective ring flash
    scene.time.delayedCall(200, () => {
      const ring = scene.add.graphics();
      ring.lineStyle(2, 0x8899dd, 0.7);
      ring.strokeEllipse(0, 0, sprite.displayWidth * 0.7, sprite.displayWidth * 0.3);
      ring.setPosition(cx, cy + sprite.displayHeight * 0.15);
      ring.setDepth(10000);
      scene.tweens.add({
        targets: ring,
        alpha: 0,
        scale: 1.3,
        duration: 250,
        ease: 'Power2.easeOut',
        onComplete: () => ring.destroy(),
      });
    });
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.04,
      scaleY: bs * 1.04,
      duration: 100,
      delay: 150,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

// ---------------------------------------------------------------------------
// Reactive combat moment presets
// ---------------------------------------------------------------------------

/**
 * Nature regen tick — petal bloom + drifting leaves + gentle breath pulse.
 * No shake (HoT is passive, not an impact). Shapes are leaves and a 5-petal
 * bloom so it reads as regrowth, not a generic green flash.
 */
const regenTick: AnimationPreset = {
  play(scene, sprite, _tint) {
    const leafColor = 0x66dd77;
    const accentColor = 0xaaff99;

    flashSpriteTint(scene, sprite, 0x88ff88, 220);

    // Gentle breath pulse — the unit subtly inhales life
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 180,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(bs),
    });

    const cy = spriteCenterY(sprite);
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;

    // Center bloom — 5-petal flower expands and fades
    const bloom = scene.add.graphics();
    bloom.setPosition(sprite.x, cy);
    bloom.setDepth(10000);
    const drawBloom = (radius: number, alpha: number) => {
      bloom.clear();
      for (let p = 0; p < 5; p++) {
        const angle = (Math.PI * 2 * p) / 5 - Math.PI / 2;
        const px = Math.cos(angle) * radius * 0.6;
        const py = Math.sin(angle) * radius * 0.6;
        bloom.fillStyle(leafColor, alpha * 0.85);
        bloom.fillEllipse(px, py, radius * 0.55, radius * 0.95);
      }
      bloom.fillStyle(0xffffff, alpha);
      bloom.fillCircle(0, 0, radius * 0.32);
    };
    const bloomState = { r: 1, a: 1 };
    scene.tweens.add({
      targets: bloomState,
      r: sh * 0.22,
      a: 0,
      duration: 380,
      ease: 'Sine.easeOut',
      onUpdate: () => drawBloom(bloomState.r, bloomState.a),
      onComplete: () => bloom.destroy(),
    });

    // Drifting leaves — small leaf shapes rise with sway and rotation
    const leafCount = 6;
    for (let i = 0; i < leafCount; i++) {
      const leaf = scene.add.graphics();
      leaf.fillStyle(leafColor, 0.9);
      leaf.fillEllipse(0, 0, 5, 10);
      leaf.fillStyle(accentColor, 0.95);
      leaf.fillEllipse(0, -1, 2.2, 6);

      const startX = sprite.x + (Math.random() - 0.5) * sw * 0.55;
      const startY = cy + sh * 0.18;
      leaf.setPosition(startX, startY);
      leaf.setDepth(10000);
      leaf.setRotation((Math.random() - 0.5) * 0.7);

      const driftX = (Math.random() - 0.5) * 36;
      scene.tweens.add({
        targets: leaf,
        x: startX + driftX,
        y: cy - sh * 0.22 - Math.random() * 22,
        rotation: leaf.rotation + (Math.random() - 0.5) * 1.6,
        alpha: 0,
        duration: 540 + Math.random() * 140,
        delay: i * 45,
        ease: 'Sine.easeOut',
        onComplete: () => leaf.destroy(),
      });
    }
  },
};

/** Bright white/gold camera flash + sprite flash — critical hit emphasis */
const critFlash: AnimationPreset = {
  play(scene, sprite, tint) {
    // Sharp white flash on sprite
    flashSpriteTint(scene, sprite, 0xffffff, 120);
    // Brief golden camera flash
    scene.cameras.main.flash(80, 255, 220, 100, false);
    // Extra shake on top of the ability's shake
    shakeSprite(scene, sprite, 3);
    // Tiny white starburst particles
    const cy = spriteCenterY(sprite);
    for (let i = 0; i < 4; i++) {
      const spark = scene.add.graphics();
      spark.fillStyle(0xffffff, 0.9);
      spark.fillCircle(0, 0, 2);
      spark.setPosition(sprite.x, cy);
      spark.setDepth(10001);
      const angle = (Math.PI * 2 * i) / 4 + Math.PI / 4;
      scene.tweens.add({
        targets: spark,
        x: sprite.x + Math.cos(angle) * 20,
        y: cy + Math.sin(angle) * 20,
        alpha: 0,
        duration: 200,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  },
};

/** Type advantage emphasis — bright tinted burst ring */
const superEffective: AnimationPreset = {
  play(scene, sprite, tint) {
    const cy = spriteCenterY(sprite);
    // Expanding colored ring
    const ring = scene.add.graphics();
    ring.setDepth(10001);
    const ringData = { radius: 5, alpha: 0.7 };
    scene.tweens.add({
      targets: ringData,
      radius: sprite.displayHeight * 0.45,
      alpha: 0,
      duration: 250,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(4, tint, ringData.alpha);
        ring.strokeCircle(sprite.x, cy, ringData.radius);
      },
      onComplete: () => ring.destroy(),
    });
    // Subtle camera flash in the element's color
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(60, r, g, b, false);
  },
};

/** Muted dampened visual — damage resisted by type */
const resistFlash: AnimationPreset = {
  play(scene, sprite, tint) {
    // Brief gray tint — visually "dampened"
    flashSpriteTint(scene, sprite, 0x888888, 150);
    const cy = spriteCenterY(sprite);
    // Small gray deflection particles angling away
    for (let i = 0; i < 3; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(0xaaaaaa, 0.6);
      particle.fillCircle(0, 0, 2);
      particle.setPosition(sprite.x, cy);
      particle.setDepth(10000);
      const angle = Math.PI * 0.5 + (Math.random() - 0.5) * 1.5; // roughly upward
      scene.tweens.add({
        targets: particle,
        x: sprite.x + Math.cos(angle) * 18,
        y: cy + Math.sin(angle) * -18,
        alpha: 0,
        duration: 250,
        delay: i * 30,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  },
};

/**
 * Dye Bloom — violet luminous flower-bloom unfurling outward from the target,
 * with a white-violet core flare and stain-drips falling after. Distinct
 * shape language from `starburst` (pointed star), `vortex` (inward spiral),
 * and `spore_cloud` (drifting motes). Reads as "she pulled the dye outward
 * and it's blooming on us." Heavy magic AOE feel.
 */
const dyeBloom: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const size = sprite.displayHeight * 0.55;

    // White-violet core flare — fast, peaks early
    const core = scene.add.graphics();
    core.fillStyle(0xffffff, 0.95);
    core.fillCircle(0, 0, size * 0.2);
    core.setPosition(cx, cy);
    core.setDepth(10001);
    core.setBlendMode(Phaser.BlendModes.ADD);
    core.setScale(0.2);
    scene.tweens.add({
      targets: core,
      scale: 1.1,
      alpha: 0,
      duration: 200,
      ease: 'Sine.easeOut',
      onComplete: () => core.destroy(),
    });

    // 6 ellipse "petals" unfurl outward in a bloom — each is a tinted ellipse
    // pointing outward with a luminous inner streak
    const petalCount = 6;
    for (let i = 0; i < petalCount; i++) {
      const angle = (i / petalCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.18;
      const petalLen = size * 0.55;
      const petalWidth = size * 0.22;

      const petal = scene.add.graphics();
      petal.fillStyle(tint, 0.9);
      petal.fillEllipse(petalLen * 0.5, 0, petalLen, petalWidth);
      petal.fillStyle(0xffffff, 0.55);
      petal.fillEllipse(petalLen * 0.5, 0, petalLen * 0.85, petalWidth * 0.35);
      petal.setPosition(cx, cy);
      petal.rotation = angle;
      petal.setScale(0.12);
      petal.setDepth(10000);
      petal.setBlendMode(Phaser.BlendModes.ADD);

      scene.tweens.add({
        targets: petal,
        scale: 1.0,
        alpha: 0,
        duration: 320,
        ease: 'Cubic.easeOut',
        onComplete: () => petal.destroy(),
      });
    }

    // 5 stain-drips fall through the bloom — concrete dye identity vs. magic
    for (let i = 0; i < 5; i++) {
      const drip = scene.add.graphics();
      drip.fillStyle(tint, 0.92);
      drip.fillEllipse(0, 0, 6, 11);

      const startX = cx + (Math.random() - 0.5) * size * 0.7;
      const startY = cy + (Math.random() - 0.5) * 8;
      drip.setPosition(startX, startY);
      drip.setDepth(10002);
      drip.alpha = 0;

      scene.tweens.add({
        targets: drip,
        y: startY + 28 + Math.random() * 22,
        alpha: { from: 0, to: 0.9 },
        duration: 220,
        delay: 140 + i * 25,
        ease: 'Quad.easeIn',
        onComplete: () => {
          scene.tweens.add({
            targets: drip,
            alpha: 0,
            duration: 130,
            onComplete: () => drip.destroy(),
          });
        },
      });
    }

    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(110, r, g, b, false);
    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 220);
  },
};

/** Dye Stain — single-target despair-applier (stained_mark / saturated_despair).
 *  A small bloom appears on the target's chest, then the stain spreads outward
 *  and runs down in three thick drips. Reads as "the dye got into them" — the
 *  Stained Keeper's chapter signature. Distinct from dye_bloom (radial petal
 *  AOE) by being centered + downward-bleeding rather than radiating outward. */
const dyeStain: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const size = sprite.displayHeight * 0.42;

    // Center stain — irregular violet blob blooms on chest, then drips
    const stain = scene.add.graphics();
    stain.setPosition(cx, cy);
    stain.setDepth(10000);
    stain.setBlendMode(Phaser.BlendModes.NORMAL);

    // Build stain via 5 overlapping ellipses for an organic, irregular shape
    const blobs: Array<{ ox: number; oy: number; rx: number; ry: number }> = [];
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + Math.random() * 0.4;
      blobs.push({
        ox: Math.cos(a) * size * 0.18,
        oy: Math.sin(a) * size * 0.14,
        rx: size * (0.32 + Math.random() * 0.18),
        ry: size * (0.28 + Math.random() * 0.18),
      });
    }
    const draw = { scale: 0.15 };
    scene.tweens.add({
      targets: draw,
      scale: 1.0,
      duration: 220,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        stain.clear();
        stain.fillStyle(tint, 0.85);
        for (const b of blobs) {
          stain.fillEllipse(b.ox, b.oy, b.rx * draw.scale, b.ry * draw.scale);
        }
        // White-violet inner core for luminance
        stain.fillStyle(0xffeeff, 0.4);
        stain.fillEllipse(0, 0, size * 0.4 * draw.scale, size * 0.32 * draw.scale);
      },
      onComplete: () => {
        scene.tweens.add({
          targets: stain,
          alpha: 0,
          duration: 280,
          delay: 80,
          ease: 'Sine.easeIn',
          onComplete: () => stain.destroy(),
        });
      },
    });

    // Three thick drips run downward off the stain — this is the dye reading
    // as a fluid, not a generic magic flash
    for (let i = 0; i < 3; i++) {
      const drip = scene.add.graphics();
      drip.fillStyle(tint, 0.95);
      drip.fillEllipse(0, 0, 7, 14);
      drip.fillStyle(0xffffff, 0.35);
      drip.fillEllipse(-1, -2, 3, 7);

      const startX = cx + (i - 1) * size * 0.32 + (Math.random() - 0.5) * 6;
      const startY = cy + size * 0.05;
      drip.setPosition(startX, startY);
      drip.setDepth(10001);
      drip.alpha = 0;

      scene.tweens.add({
        targets: drip,
        y: startY + 30 + Math.random() * 18,
        alpha: { from: 0, to: 0.95 },
        scaleY: 1.6,
        duration: 280,
        delay: 160 + i * 40,
        ease: 'Quad.easeIn',
        onComplete: () => {
          scene.tweens.add({
            targets: drip,
            alpha: 0,
            duration: 140,
            onComplete: () => drip.destroy(),
          });
        },
      });
    }

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 200);
  },
};

/** Mordant Lash — dye-tendril whip striking the target. A thick tendril ropes
 *  in from above-side, snaps across the sprite, leaves a glistening violet
 *  trail. Distinct from crescent_sweep (clean blade arc) and shadow_burst
 *  (radial implosion). The Hollow + Steeped Drinker's signature attack —
 *  the dye fights back with shape. */
const mordantLash: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const size = sprite.displayHeight * 0.6;

    // Tendril path — from above-left to lower-right, slight curve
    const fromX = cx - size * 0.95;
    const fromY = cy - size * 0.55;
    const toX = cx + size * 0.85;
    const toY = cy + size * 0.25;
    const ctrlX = cx + size * 0.05;
    const ctrlY = cy - size * 0.25; // bows upward

    const tendril = scene.add.graphics();
    tendril.setDepth(10000);
    tendril.setBlendMode(Phaser.BlendModes.NORMAL);

    const sweep = { t: 0 };
    const SEGMENTS = 18;
    scene.tweens.add({
      targets: sweep,
      t: 1,
      duration: 180,
      ease: 'Cubic.easeIn',
      onUpdate: () => {
        tendril.clear();
        // Outer rope (thick violet)
        tendril.lineStyle(8, tint, 0.92);
        for (let i = 0; i < SEGMENTS - 1; i++) {
          const t1 = (i / SEGMENTS) * sweep.t;
          const t2 = ((i + 1) / SEGMENTS) * sweep.t;
          const x1 = (1 - t1) * (1 - t1) * fromX + 2 * (1 - t1) * t1 * ctrlX + t1 * t1 * toX;
          const y1 = (1 - t1) * (1 - t1) * fromY + 2 * (1 - t1) * t1 * ctrlY + t1 * t1 * toY;
          const x2 = (1 - t2) * (1 - t2) * fromX + 2 * (1 - t2) * t2 * ctrlX + t2 * t2 * toX;
          const y2 = (1 - t2) * (1 - t2) * fromY + 2 * (1 - t2) * t2 * ctrlY + t2 * t2 * toY;
          tendril.lineBetween(x1, y1, x2, y2);
        }
        // Inner highlight (white-violet)
        tendril.lineStyle(3, 0xffeeff, 0.85);
        for (let i = 0; i < SEGMENTS - 1; i++) {
          const t1 = (i / SEGMENTS) * sweep.t;
          const t2 = ((i + 1) / SEGMENTS) * sweep.t;
          const x1 = (1 - t1) * (1 - t1) * fromX + 2 * (1 - t1) * t1 * ctrlX + t1 * t1 * toX;
          const y1 = (1 - t1) * (1 - t1) * fromY + 2 * (1 - t1) * t1 * ctrlY + t1 * t1 * toY;
          const x2 = (1 - t2) * (1 - t2) * fromX + 2 * (1 - t2) * t2 * ctrlX + t2 * t2 * toX;
          const y2 = (1 - t2) * (1 - t2) * fromY + 2 * (1 - t2) * t2 * ctrlY + t2 * t2 * toY;
          tendril.lineBetween(x1, y1, x2, y2);
        }
      },
    });

    // After full sweep, fade and drip
    scene.time.delayedCall(180, () => {
      scene.tweens.add({
        targets: tendril,
        alpha: 0,
        duration: 220,
        ease: 'Sine.easeIn',
        onComplete: () => tendril.destroy(),
      });

      // 4 drips fall from where the tendril crossed
      for (let i = 0; i < 4; i++) {
        const t = 0.25 + i * 0.2;
        const dx = (1 - t) * (1 - t) * fromX + 2 * (1 - t) * t * ctrlX + t * t * toX;
        const dy = (1 - t) * (1 - t) * fromY + 2 * (1 - t) * t * ctrlY + t * t * toY;

        const drip = scene.add.graphics();
        drip.fillStyle(tint, 0.9);
        drip.fillEllipse(0, 0, 5, 10);
        drip.setPosition(dx, dy);
        drip.setDepth(10001);

        scene.tweens.add({
          targets: drip,
          y: dy + 24 + Math.random() * 16,
          alpha: 0,
          duration: 240,
          delay: i * 30,
          ease: 'Quad.easeIn',
          onComplete: () => drip.destroy(),
        });
      }
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 200);
  },
};

/** Mordant Spew — Hollow's party-wide dye spread. A central column of viscous
 *  violet erupts upward, then bursts outward as 8 radial spatter-blobs that
 *  fall and drip. Distinct from generic burst (no clean particles), shadow_burst
 *  (radial implosion), and rain_down (linear teardrops). Sells "the dye is
 *  POURING off this thing onto the party." */
const mordantSpew: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const size = sprite.displayHeight * 0.55;

    // Central upward column — fluid jets up
    const column = scene.add.graphics();
    column.setPosition(cx, cy + size * 0.35);
    column.setDepth(10000);
    column.setBlendMode(Phaser.BlendModes.NORMAL);

    const colData = { h: 0, alpha: 0.9 };
    scene.tweens.add({
      targets: colData,
      h: size * 0.85,
      alpha: 0,
      duration: 280,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        column.clear();
        column.fillStyle(tint, colData.alpha);
        // Tapered teardrop column
        const w = size * 0.22 * (1 - colData.h / (size * 0.85) * 0.5);
        column.fillEllipse(0, -colData.h * 0.5, w, colData.h);
        // White-hot core
        column.fillStyle(0xffeeff, colData.alpha * 0.55);
        column.fillEllipse(0, -colData.h * 0.5, w * 0.45, colData.h * 0.85);
      },
      onComplete: () => column.destroy(),
    });

    // 8 radial spatter blobs erupt from peak, arc out, fall, drip
    const SPATTERS = 8;
    for (let i = 0; i < SPATTERS; i++) {
      const angle = (i / SPATTERS) * Math.PI - Math.PI; // upper half-circle (-PI to 0)
      const dist = size * (0.7 + Math.random() * 0.35);
      const targetX = cx + Math.cos(angle) * dist;
      const peakY = cy - size * 0.45 + Math.sin(angle) * dist * 0.55;

      const blob = scene.add.graphics();
      blob.fillStyle(tint, 0.95);
      blob.fillCircle(0, 0, 5 + Math.random() * 2.5);
      blob.fillStyle(0xffffff, 0.4);
      blob.fillCircle(-1, -1, 2);
      blob.setPosition(cx, cy - size * 0.05);
      blob.setDepth(10001);
      blob.alpha = 0;

      // Arc out to peak
      scene.tweens.add({
        targets: blob,
        x: targetX,
        y: peakY,
        alpha: { from: 0, to: 1 },
        duration: 200,
        delay: 120 + i * 18,
        ease: 'Quad.easeOut',
        onComplete: () => {
          // Fall + fade
          scene.tweens.add({
            targets: blob,
            y: peakY + 50 + Math.random() * 30,
            alpha: 0,
            scaleY: 1.8,
            duration: 280,
            ease: 'Quad.easeIn',
            onComplete: () => blob.destroy(),
          });
        },
      });
    }

    // Camera flash to sell the AoE moment
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(120, r, g, b, false);
    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 240);
  },
};

/** Dye Drink — Velara's self-cast (feed_dye). Violet fluid is sucked INTO the
 *  caster from below and the sides; her silhouette deepens in saturation;
 *  brief surge pulse + caster glow. Sells "she's drinking the dye into
 *  herself." Distinct from buff_shimmer (small sparkles) by the inward
 *  motion and the deep-stain hold on the sprite. */
const dyeDrink: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sw = sprite.displayWidth;
    const sh = sprite.displayHeight;

    // 6 dye droplets converge inward from below and sides — pulled into caster
    for (let i = 0; i < 6; i++) {
      const drop = scene.add.graphics();
      drop.fillStyle(tint, 0.95);
      drop.fillEllipse(0, 0, 7, 12);
      drop.fillStyle(0xffeeff, 0.45);
      drop.fillEllipse(-1, -2, 3, 5);

      // Spawn around the lower half of the sprite
      const angle = Math.PI * (0.15 + (i / 5) * 0.7); // PI*0.15 .. PI*0.85 (bottom arc)
      const r = sw * 0.55;
      const startX = cx + Math.cos(angle) * r;
      const startY = cy + Math.sin(angle) * r * 0.5 + sh * 0.18;
      drop.setPosition(startX, startY);
      drop.setDepth(10000);

      scene.tweens.add({
        targets: drop,
        x: cx,
        y: cy,
        alpha: { from: 0.95, to: 0 },
        scale: 0.5,
        duration: 320,
        delay: i * 35,
        ease: 'Cubic.easeIn',
        onComplete: () => drop.destroy(),
      });
    }

    // Deep stain hold — full sprite tinted violet for a longer beat than a
    // typical buff flash, sells the saturation
    flashSpriteTint(scene, sprite, tint, 420);

    // Caster body pulse — slight scale-up as if absorbing
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.06,
      scaleY: bs * 1.06,
      duration: 220,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    // Final inward burst flash — the dye lands, settles
    scene.time.delayedCall(280, () => {
      const burst = scene.add.graphics();
      burst.fillStyle(tint, 0.7);
      burst.fillCircle(0, 0, sw * 0.18);
      burst.fillStyle(0xffeeff, 0.55);
      burst.fillCircle(0, 0, sw * 0.08);
      burst.setPosition(cx, cy);
      burst.setDepth(10001);
      burst.setBlendMode(Phaser.BlendModes.ADD);
      burst.setScale(0.4);
      scene.tweens.add({
        targets: burst,
        scale: 1.3,
        alpha: 0,
        duration: 260,
        ease: 'Cubic.easeOut',
        onComplete: () => burst.destroy(),
      });
    });
  },
};

/** Guardian raises a tower-shield in front of themselves — Intercept buff cast.
 *  Reads as "I am now a wall." Steel-gold tint, shield silhouette grows in front
 *  of the sprite, brace-pulse on the sprite, sparks at the rim. */
const interceptGuard: AnimationPreset = {
  play(scene, sprite, tint) {
    const cy = spriteCenterY(sprite);
    const w = sprite.displayWidth * 0.7;
    const h = sprite.displayHeight * 0.85;
    const gfx = scene.add.graphics();
    gfx.setDepth(10000);

    const draw = { rise: 0, fade: 1 };

    // Phase 1: shield rises from below (140ms)
    scene.tweens.add({
      targets: draw,
      rise: 1,
      duration: 140,
      ease: 'Back.easeOut',
      onUpdate: () => {
        gfx.clear();
        const top = cy - (h / 2) * draw.rise;
        const bot = cy + (h / 2) * draw.rise;
        const curW = w * draw.rise;
        // Outer steel plate (translucent fill)
        gfx.fillStyle(0xccddee, 0.25 * draw.fade);
        gfx.fillRoundedRect(sprite.x - curW / 2, top, curW, bot - top, 8);
        // Bold gold rim
        gfx.lineStyle(5, tint, 0.9 * draw.fade);
        gfx.strokeRoundedRect(sprite.x - curW / 2, top, curW, bot - top, 8);
        // Inner highlight rim
        gfx.lineStyle(2, 0xffffff, 0.7 * draw.fade);
        gfx.strokeRoundedRect(sprite.x - curW / 2 + 4, top + 4, Math.max(0, curW - 8), Math.max(0, (bot - top) - 8), 6);
        // Center boss (round metal stud)
        if (draw.rise > 0.6) {
          gfx.fillStyle(tint, 0.9 * draw.fade);
          gfx.fillCircle(sprite.x, cy, 6);
          gfx.fillStyle(0xffffff, 0.8 * draw.fade);
          gfx.fillCircle(sprite.x - 1, cy - 1, 2);
        }
      },
      onComplete: () => {
        // Phase 2: hold then fade (260ms)
        scene.tweens.add({
          targets: draw,
          fade: 0,
          duration: 260,
          delay: 80,
          ease: 'Sine.easeIn',
          onUpdate: () => {
            gfx.clear();
            const top = cy - h / 2;
            const bot = cy + h / 2;
            gfx.fillStyle(0xccddee, 0.25 * draw.fade);
            gfx.fillRoundedRect(sprite.x - w / 2, top, w, bot - top, 8);
            gfx.lineStyle(5, tint, 0.9 * draw.fade);
            gfx.strokeRoundedRect(sprite.x - w / 2, top, w, bot - top, 8);
            gfx.lineStyle(2, 0xffffff, 0.7 * draw.fade);
            gfx.strokeRoundedRect(sprite.x - w / 2 + 4, top + 4, w - 8, (bot - top) - 8, 6);
            gfx.fillStyle(tint, 0.9 * draw.fade);
            gfx.fillCircle(sprite.x, cy, 6);
            gfx.fillStyle(0xffffff, 0.8 * draw.fade);
            gfx.fillCircle(sprite.x - 1, cy - 1, 2);
          },
          onComplete: () => gfx.destroy(),
        });
      },
    });

    // Gold sparks at shield rim (4 corners)
    const corners = [
      { x: -w / 2, y: -h / 2 }, { x: w / 2, y: -h / 2 },
      { x: -w / 2, y: h / 2 }, { x: w / 2, y: h / 2 },
    ];
    corners.forEach((c, i) => {
      scene.time.delayedCall(80 + i * 25, () => {
        const spark = scene.add.graphics();
        spark.fillStyle(0xffffff, 0.9);
        spark.fillCircle(0, 0, 3);
        spark.fillStyle(tint, 0.7);
        spark.fillCircle(0, 0, 5);
        spark.setPosition(sprite.x + c.x, cy + c.y);
        spark.setDepth(10001);
        scene.tweens.add({
          targets: spark,
          alpha: 0,
          scaleX: 1.8,
          scaleY: 1.8,
          duration: 220,
          ease: 'Power2.easeOut',
          onComplete: () => spark.destroy(),
        });
      });
    });

    // Brace-pulse on sprite
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.05,
      scaleY: bs * 1.05,
      duration: 110,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    flashSpriteTint(scene, sprite, tint, 280);
  },
};

/** Intercept TRIGGERS — guardian braces and takes the hit. Played on the guardian
 *  the instant before the redirected damage event lands. Sprite snap-braces,
 *  big shield-block flash, deflection arcs radiate. The "I jumped in front" cue. */
const interceptBlock: AnimationPreset = {
  play(scene, sprite, tint) {
    const cy = spriteCenterY(sprite);

    // Quick brace: drop slightly + scale-pulse (set feet)
    const origY = sprite.y;
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      y: origY + 4,
      scaleX: bs * 1.06,
      scaleY: bs * 0.96,
      duration: 70,
      yoyo: true,
      ease: 'Power3.easeOut',
      onComplete: () => { sprite.y = origY; sprite.setScale(bs); },
    });

    // Big shield-disc flash in front of sprite
    const shieldGfx = scene.add.graphics();
    shieldGfx.setDepth(10000);
    const r0 = sprite.displayHeight * 0.18;
    const r1 = sprite.displayHeight * 0.42;
    const flash = { p: 0 };
    scene.tweens.add({
      targets: flash,
      p: 1,
      duration: 200,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        shieldGfx.clear();
        const r = r0 + (r1 - r0) * flash.p;
        const a = 1 - flash.p;
        // Outer gold ring
        shieldGfx.lineStyle(6, tint, a);
        shieldGfx.strokeCircle(sprite.x, cy, r);
        // Inner steel ring
        shieldGfx.lineStyle(3, 0xccddee, a * 0.9);
        shieldGfx.strokeCircle(sprite.x, cy, r * 0.78);
        // Bright center pop
        if (flash.p < 0.5) {
          shieldGfx.fillStyle(0xffffff, (0.5 - flash.p) * 1.6);
          shieldGfx.fillCircle(sprite.x, cy, r * 0.35);
        }
      },
      onComplete: () => shieldGfx.destroy(),
    });

    // Deflection arcs radiating outward — 6 short curved lines
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6 + Math.random() * 0.2;
      const arcGfx = scene.add.graphics();
      arcGfx.setDepth(10001);
      const arc = { t: 0 };
      const r = r1 * 0.85;
      scene.tweens.add({
        targets: arc,
        t: 1,
        duration: 220,
        delay: 30,
        ease: 'Power2.easeOut',
        onUpdate: () => {
          arcGfx.clear();
          const a = 1 - arc.t;
          const startR = r * (0.6 + arc.t * 0.5);
          const endR = startR + 14;
          const sx = sprite.x + Math.cos(angle) * startR;
          const sy = cy + Math.sin(angle) * startR;
          const ex = sprite.x + Math.cos(angle) * endR;
          const ey = cy + Math.sin(angle) * endR;
          arcGfx.lineStyle(3, 0xffffff, a);
          arcGfx.beginPath();
          arcGfx.moveTo(sx, sy);
          arcGfx.lineTo(ex, ey);
          arcGfx.strokePath();
          arcGfx.lineStyle(2, tint, a * 0.9);
          arcGfx.beginPath();
          arcGfx.moveTo(sx, sy);
          arcGfx.lineTo(ex, ey);
          arcGfx.strokePath();
        },
        onComplete: () => arcGfx.destroy(),
      });
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, 0xffffff, 90);
    scene.cameras.main.shake(60, 0.004);
  },
};

/** Heavy arrow/bolt slams into target — visible shaft sticks, fletching, dust kick.
 *  Replaces undersized `pierce` for power-shot tier ranged attacks. */
const arrowThud: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.55;
    // Shaft angle: comes downward from above-left toward center
    const angle = -Math.PI * 0.18; // ~ -32° from horizontal
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    // Tail (start) and head (end) of the shaft — head is at sprite center
    const headX = cx;
    const headY = cy;
    const tailX = cx - dx * len;
    const tailY = cy - dy * len;

    const gfx = scene.add.graphics();
    gfx.setDepth(10000);

    const draw = { extend: 0, fade: 1 };

    // Phase 1: shaft drives in (50ms — fast)
    scene.tweens.add({
      targets: draw,
      extend: 1,
      duration: 50,
      ease: 'Power3.easeIn',
      onUpdate: () => {
        gfx.clear();
        const tx = headX - dx * len * draw.extend;
        const ty = headY - dy * len * draw.extend;
        // Dark outline
        gfx.lineStyle(7, 0x222222, draw.fade);
        gfx.beginPath();
        gfx.moveTo(tx, ty);
        gfx.lineTo(headX, headY);
        gfx.strokePath();
        // Inner shaft
        gfx.lineStyle(4, tint, draw.fade);
        gfx.beginPath();
        gfx.moveTo(tx, ty);
        gfx.lineTo(headX, headY);
        gfx.strokePath();
      },
      onComplete: () => {
        // Phase 2: hold visible shaft + fletching (140ms)
        scene.tweens.add({
          targets: draw,
          fade: 1,
          duration: 140,
          onUpdate: () => {
            gfx.clear();
            // Shaft (full length)
            gfx.lineStyle(7, 0x222222, draw.fade);
            gfx.beginPath();
            gfx.moveTo(tailX, tailY);
            gfx.lineTo(headX, headY);
            gfx.strokePath();
            gfx.lineStyle(4, tint, draw.fade);
            gfx.beginPath();
            gfx.moveTo(tailX, tailY);
            gfx.lineTo(headX, headY);
            gfx.strokePath();
            // Arrowhead triangle at impact point
            const ah = 7;
            const perpX = -dy;
            const perpY = dx;
            gfx.fillStyle(0x222222, draw.fade);
            gfx.fillTriangle(
              headX + dx * ah, headY + dy * ah,
              headX + perpX * 4, headY + perpY * 4,
              headX - perpX * 4, headY - perpY * 4,
            );
            gfx.fillStyle(tint, draw.fade);
            gfx.fillTriangle(
              headX + dx * (ah - 2), headY + dy * (ah - 2),
              headX + perpX * 2.5, headY + perpY * 2.5,
              headX - perpX * 2.5, headY - perpY * 2.5,
            );
            // Fletching (3 small triangles at tail)
            const flX = tailX;
            const flY = tailY;
            gfx.fillStyle(0xeeeeee, draw.fade * 0.9);
            gfx.fillTriangle(
              flX, flY,
              flX + dx * 8 + perpX * 5, flY + dy * 8 + perpY * 5,
              flX + dx * 8 - perpX * 5, flY + dy * 8 - perpY * 5,
            );
            gfx.fillStyle(tint, draw.fade * 0.7);
            gfx.fillTriangle(
              flX + dx * 2, flY + dy * 2,
              flX + dx * 8 + perpX * 3, flY + dy * 8 + perpY * 3,
              flX + dx * 8 - perpX * 3, flY + dy * 8 - perpY * 3,
            );
          },
          onComplete: () => {
            // Phase 3: fade out (120ms)
            scene.tweens.add({
              targets: draw,
              fade: 0,
              duration: 120,
              ease: 'Power2.easeIn',
              onUpdate: () => {
                gfx.clear();
                gfx.lineStyle(7, 0x222222, draw.fade);
                gfx.beginPath();
                gfx.moveTo(tailX, tailY);
                gfx.lineTo(headX, headY);
                gfx.strokePath();
                gfx.lineStyle(4, tint, draw.fade);
                gfx.beginPath();
                gfx.moveTo(tailX, tailY);
                gfx.lineTo(headX, headY);
                gfx.strokePath();
              },
              onComplete: () => gfx.destroy(),
            });
          },
        });
      },
    });

    // Dust puff at impact (4 small particles spreading downward)
    for (let i = 0; i < 5; i++) {
      const dust = scene.add.graphics();
      dust.fillStyle(0xccbb99, 0.8);
      dust.fillCircle(0, 0, 2 + Math.random() * 2);
      dust.setPosition(cx + (Math.random() - 0.5) * 8, cy + (Math.random() - 0.5) * 8);
      dust.setDepth(9999);
      const dustAngle = Math.PI * (0.7 + Math.random() * 0.6); // downward spread
      const dist = 14 + Math.random() * 10;
      scene.tweens.add({
        targets: dust,
        x: dust.x + Math.cos(dustAngle) * dist,
        y: dust.y + Math.sin(dustAngle) * dist,
        alpha: 0,
        duration: 280,
        delay: 30,
        ease: 'Power2.easeOut',
        onComplete: () => dust.destroy(),
      });
    }

    // Heavy backward nudge — arrow pushes the target
    const origX = sprite.x;
    const origY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      x: origX + dx * 6,
      y: origY + dy * 4,
      duration: 60,
      yoyo: true,
      ease: 'Power2.easeOut',
      onComplete: () => { sprite.x = origX; sprite.y = origY; },
    });

    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 140);
    scene.cameras.main.shake(70, 0.005);
  },
};

/** Ricochet bolt — kinetic chain projectile. Incoming bolt drives in from lower-left,
 *  small angular spark burst at impact, then a deflection trail streaks out at a steeper
 *  upward angle to sell the bounce. Lighter weight than arrowThud (no fletching, no big
 *  dust kick) — Ricochet is a mid-cost AoE chain, not a heavy nuke. The chain story is
 *  told by the engine firing this preset on each hop target sequentially. */
const boltRicochet: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.45;

    // Incoming: from lower-left, traveling up-and-right
    const inAng = -Math.PI * 0.18;
    const idx = Math.cos(inAng);
    const idy = Math.sin(inAng);

    // Outgoing: deflects up-and-right at a steeper angle (sells the bounce)
    const outAng = -Math.PI * 0.42;
    const odx = Math.cos(outAng);
    const ody = Math.sin(outAng);

    const headX = cx;
    const headY = cy;
    const tailX = cx - idx * len;
    const tailY = cy - idy * len;

    // Incoming bolt graphics
    const inGfx = scene.add.graphics();
    inGfx.setDepth(10000);
    const inState = { extend: 0, fade: 1 };

    scene.tweens.add({
      targets: inState,
      extend: 1,
      duration: 60,
      ease: 'Power3.easeIn',
      onUpdate: () => {
        inGfx.clear();
        const tx = headX - idx * len * inState.extend;
        const ty = headY - idy * len * inState.extend;
        inGfx.lineStyle(6, 0x222222, inState.fade);
        inGfx.beginPath();
        inGfx.moveTo(tx, ty);
        inGfx.lineTo(headX, headY);
        inGfx.strokePath();
        inGfx.lineStyle(3, tint, inState.fade);
        inGfx.beginPath();
        inGfx.moveTo(tx, ty);
        inGfx.lineTo(headX, headY);
        inGfx.strokePath();
      },
      onComplete: () => {
        // Impact spark — 5 short angular lines radiating outward
        const sparkGfx = scene.add.graphics();
        sparkGfx.setDepth(10002);
        const spark = { fade: 1 };
        scene.tweens.add({
          targets: spark,
          fade: 0,
          duration: 180,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            sparkGfx.clear();
            for (let i = 0; i < 5; i++) {
              const a = (i / 5) * Math.PI * 2 + 0.4;
              const r1 = 3;
              const r2 = 16;
              sparkGfx.lineStyle(3, 0xffffff, spark.fade);
              sparkGfx.beginPath();
              sparkGfx.moveTo(headX + Math.cos(a) * r1, headY + Math.sin(a) * r1);
              sparkGfx.lineTo(headX + Math.cos(a) * r2, headY + Math.sin(a) * r2);
              sparkGfx.strokePath();
              sparkGfx.lineStyle(1.5, tint, spark.fade);
              sparkGfx.beginPath();
              sparkGfx.moveTo(headX + Math.cos(a) * r1, headY + Math.sin(a) * r1);
              sparkGfx.lineTo(headX + Math.cos(a) * r2, headY + Math.sin(a) * r2);
              sparkGfx.strokePath();
            }
          },
          onComplete: () => sparkGfx.destroy(),
        });

        // Outgoing deflection trail — ghosts away from contact point
        const outGfx = scene.add.graphics();
        outGfx.setDepth(9999);
        const outState = { extend: 0, fade: 1 };
        scene.tweens.add({
          targets: outState,
          extend: 1,
          duration: 160,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            outGfx.clear();
            const ex = headX + odx * len * outState.extend;
            const ey = headY + ody * len * outState.extend;
            outGfx.lineStyle(5, 0x222222, outState.fade * 0.7);
            outGfx.beginPath();
            outGfx.moveTo(headX, headY);
            outGfx.lineTo(ex, ey);
            outGfx.strokePath();
            outGfx.lineStyle(2.5, tint, outState.fade * 0.85);
            outGfx.beginPath();
            outGfx.moveTo(headX, headY);
            outGfx.lineTo(ex, ey);
            outGfx.strokePath();
          },
          onComplete: () => {
            scene.tweens.add({
              targets: outState,
              fade: 0,
              duration: 80,
              ease: 'Power2.easeIn',
              onUpdate: () => {
                outGfx.clear();
                const ex = headX + odx * len;
                const ey = headY + ody * len;
                outGfx.lineStyle(5, 0x222222, outState.fade * 0.7);
                outGfx.beginPath();
                outGfx.moveTo(headX, headY);
                outGfx.lineTo(ex, ey);
                outGfx.strokePath();
                outGfx.lineStyle(2.5, tint, outState.fade * 0.85);
                outGfx.beginPath();
                outGfx.moveTo(headX, headY);
                outGfx.lineTo(ex, ey);
                outGfx.strokePath();
              },
              onComplete: () => outGfx.destroy(),
            });
          },
        });

        // Incoming bolt fades alongside the spark
        scene.tweens.add({
          targets: inState,
          fade: 0,
          duration: 180,
          ease: 'Power2.easeIn',
          onUpdate: () => {
            inGfx.clear();
            inGfx.lineStyle(6, 0x222222, inState.fade);
            inGfx.beginPath();
            inGfx.moveTo(tailX, tailY);
            inGfx.lineTo(headX, headY);
            inGfx.strokePath();
            inGfx.lineStyle(3, tint, inState.fade);
            inGfx.beginPath();
            inGfx.moveTo(tailX, tailY);
            inGfx.lineTo(headX, headY);
            inGfx.strokePath();
          },
          onComplete: () => inGfx.destroy(),
        });
      },
    });

    shakeSprite(scene, sprite, 5);
    flashSpriteTint(scene, sprite, tint, 90);
    scene.cameras.main.shake(50, 0.003);
  },
};

// ---------------------------------------------------------------------------
// Axecraft (greataxe — heavy slams + fissures + execute + Break contributor)
// ---------------------------------------------------------------------------

/** Heavy diagonal greataxe chop — top-right to bottom-left arc with debris kick.
 *  Distinct from `slash` (thin/diagonal) and `crescent_sweep` (horizontal) — this
 *  reads as a vertical-leaning chopping blade. Used by Hew. */
const axeChop: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayHeight * 0.7;

    // Diagonal blade arc — drawn top-right to bottom-left with thick stroke
    const gfx = scene.add.graphics();
    gfx.setDepth(10000);

    const arc = { progress: 0 };
    scene.tweens.add({
      targets: arc,
      progress: 1,
      duration: 130,
      ease: 'Power3.easeOut',
      onUpdate: () => {
        gfx.clear();
        // Wide arc sweeping from upper-right to lower-left (greataxe overhead chop)
        const startX = cx + reach * 0.5;
        const startY = cy - reach * 0.45;
        const endX = cx - reach * 0.4;
        const endY = cy + reach * 0.4;
        const px = startX + (endX - startX) * arc.progress;
        const py = startY + (endY - startY) * arc.progress;
        // Trailing colored stroke
        gfx.lineStyle(9, tint, 0.9);
        gfx.lineBetween(startX, startY, px, py);
        // Bright inner core
        gfx.lineStyle(3, 0xffffff, 0.85);
        gfx.lineBetween(startX, startY, px, py);
      },
      onComplete: () => {
        scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 130,
          ease: 'Power2.easeIn',
          onComplete: () => gfx.destroy(),
        });
      },
    });

    // Rock-chip debris flying outward from impact point (lower-left)
    const impactX = cx - reach * 0.3;
    const impactY = sprite.y - sprite.displayHeight * 0.05;
    for (let i = 0; i < 4; i++) {
      const chip = scene.add.graphics();
      const chipSize = 3 + Math.random() * 2;
      chip.fillStyle(0xa08570, 0.95);
      // Small irregular triangle — reads as rock chip, not particle dot
      chip.beginPath();
      chip.moveTo(-chipSize, 0);
      chip.lineTo(0, -chipSize * 1.2);
      chip.lineTo(chipSize * 0.8, chipSize * 0.3);
      chip.closePath();
      chip.fillPath();
      chip.setPosition(impactX, impactY);
      chip.setDepth(10001);

      const angle = -Math.PI * 0.15 + (Math.random() - 0.5) * Math.PI * 0.6;
      const dist = 30 + Math.random() * 20;
      scene.tweens.add({
        targets: chip,
        x: impactX + Math.cos(angle) * dist,
        y: impactY + Math.sin(angle) * dist,
        alpha: 0,
        rotation: (Math.random() - 0.5) * 2,
        duration: 280,
        delay: 60 + i * 18,
        ease: 'Power2.easeOut',
        onComplete: () => chip.destroy(),
      });
    }

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 160);
    scene.cameras.main.shake(60, 0.004);
  },
};

/** Vertical executioner's drop — overhead axe falls straight down with red splash
 *  and ground-crack burst beneath the target. Used by Headsman. */
const axeExecute: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const baseY = sprite.y;
    const h = sprite.displayHeight;

    // Vertical drop line — heavy stroke from above sprite to center
    const dropGfx = scene.add.graphics();
    dropGfx.setDepth(10000);
    const drop = { progress: 0 };
    const startY = cy - h * 0.6;
    const endY = cy + h * 0.05;
    scene.tweens.add({
      targets: drop,
      progress: 1,
      duration: 110,
      ease: 'Power3.easeIn',
      onUpdate: () => {
        dropGfx.clear();
        const tipY = startY + (endY - startY) * drop.progress;
        dropGfx.lineStyle(11, tint, 0.92);
        dropGfx.lineBetween(cx, startY, cx, tipY);
        dropGfx.lineStyle(4, 0xffffff, 0.85);
        dropGfx.lineBetween(cx, startY, cx, tipY);
      },
      onComplete: () => {
        scene.tweens.add({
          targets: dropGfx,
          alpha: 0,
          duration: 140,
          ease: 'Power2.easeIn',
          onComplete: () => dropGfx.destroy(),
        });

        // On impact: crimson splash particles + ground cracks
        // Splash: 4 dark-red drops fanning down-and-outward from impact
        for (let i = 0; i < 4; i++) {
          const splash = scene.add.graphics();
          const sz = 3 + Math.random() * 2;
          splash.fillStyle(0xaa1111, 0.9);
          splash.fillCircle(0, 0, sz);
          splash.setPosition(cx, cy);
          splash.setDepth(10001);
          const angle = Math.PI * 0.5 + (i - 1.5) * 0.45;
          const dist = h * 0.4;
          scene.tweens.add({
            targets: splash,
            x: cx + Math.cos(angle) * dist,
            y: cy + Math.sin(angle) * dist + 10,
            alpha: 0,
            duration: 320,
            ease: 'Power2.easeOut',
            onComplete: () => splash.destroy(),
          });
        }

        // 4 jagged ground-crack lines fanning beneath base
        for (let i = 0; i < 4; i++) {
          const crackGfx = scene.add.graphics();
          crackGfx.setDepth(10000);
          const angle = Math.PI * 0.55 + (i - 1.5) * 0.4;
          const length = h * (0.3 + Math.random() * 0.15);
          // Jagged 3-segment line
          const segments = 3;
          const points: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
          for (let s = 1; s <= segments; s++) {
            const t = s / segments;
            const baseX = Math.cos(angle) * length * t;
            const baseYoff = Math.sin(angle) * length * t;
            const jitter = (Math.random() - 0.5) * 8;
            points.push({ x: baseX + jitter, y: baseYoff });
          }
          crackGfx.lineStyle(3, 0x000000, 0.85);
          crackGfx.beginPath();
          crackGfx.moveTo(points[0].x, points[0].y);
          for (let s = 1; s < points.length; s++) {
            crackGfx.lineTo(points[s].x, points[s].y);
          }
          crackGfx.strokePath();
          crackGfx.setPosition(cx, baseY - 4);
          crackGfx.setScale(0.1);
          scene.tweens.add({
            targets: crackGfx,
            scale: 1,
            duration: 150,
            ease: 'Power2.easeOut',
            onComplete: () => {
              scene.tweens.add({
                targets: crackGfx,
                alpha: 0,
                duration: 220,
                delay: 100,
                ease: 'Power2.easeIn',
                onComplete: () => crackGfx.destroy(),
              });
            },
          });
        }
      },
    });

    shakeSprite(scene, sprite, 10);
    flashSpriteTint(scene, sprite, tint, 220);
    scene.cameras.main.shake(140, 0.013);
  },
};

/** Earthrend — radial fissures fan out from the target's base, splitting outward
 *  with small magma flashes at the cracks' ends. Used by Earthrend. Distinct from
 *  ground_eruption (vertical spikes) and ground_rumble (just shake). */
const earthFissures: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y - 4;
    const reach = sprite.displayHeight * 0.55;

    // 6 radiating jagged crack lines, fanning across the lower half of the frame
    const crackCount = 6;
    for (let i = 0; i < crackCount; i++) {
      const crackGfx = scene.add.graphics();
      crackGfx.setDepth(10000);

      // Spread the cracks in a radial fan — favor lower hemisphere
      const angle = Math.PI + (i - (crackCount - 1) / 2) * (Math.PI / (crackCount + 1));
      const length = reach * (0.7 + Math.random() * 0.4);

      // Jagged 4-segment polyline with random kinks
      const segs = 4;
      const points: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
      for (let s = 1; s <= segs; s++) {
        const t = s / segs;
        const px = Math.cos(angle) * length * t;
        const py = Math.sin(angle) * length * t;
        const jitter = (Math.random() - 0.5) * 10;
        // Perpendicular jitter so the crack zigzags
        const perpX = -Math.sin(angle) * jitter;
        const perpY = Math.cos(angle) * jitter;
        points.push({ x: px + perpX, y: py + perpY });
      }

      // Dark crack stroke
      crackGfx.lineStyle(3, 0x000000, 0.9);
      crackGfx.beginPath();
      crackGfx.moveTo(points[0].x, points[0].y);
      for (let s = 1; s < points.length; s++) {
        crackGfx.lineTo(points[s].x, points[s].y);
      }
      crackGfx.strokePath();

      // Magma core highlight inside the crack — tint color over black
      crackGfx.lineStyle(1.5, tint, 0.85);
      crackGfx.beginPath();
      crackGfx.moveTo(points[0].x, points[0].y);
      for (let s = 1; s < points.length; s++) {
        crackGfx.lineTo(points[s].x, points[s].y);
      }
      crackGfx.strokePath();

      crackGfx.setPosition(cx, baseY);
      crackGfx.setScale(0.05);

      // Stagger eruption — cracks split outward in sequence
      scene.tweens.add({
        targets: crackGfx,
        scale: 1,
        duration: 170,
        delay: i * 25,
        ease: 'Power2.easeOut',
        onComplete: () => {
          // Magma pop at the crack's end
          const endX = cx + points[points.length - 1].x;
          const endY = baseY + points[points.length - 1].y;
          const pop = scene.add.graphics();
          pop.setDepth(10001);
          pop.fillStyle(0xff5522, 0.95);
          pop.fillCircle(0, 0, 4);
          pop.fillStyle(0xffee88, 0.9);
          pop.fillCircle(0, 0, 2);
          pop.setPosition(endX, endY);
          pop.setScale(0.3);
          scene.tweens.add({
            targets: pop,
            scale: 1.4,
            alpha: 0,
            duration: 240,
            ease: 'Power2.easeOut',
            onComplete: () => pop.destroy(),
          });

          // Fade the crack line itself
          scene.tweens.add({
            targets: crackGfx,
            alpha: 0,
            duration: 320,
            delay: 200,
            ease: 'Power2.easeIn',
            onComplete: () => crackGfx.destroy(),
          });
        },
      });
    }

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 200);
    scene.cameras.main.shake(160, 0.010);
  },
};

/** Faultline peak release. Massive vertical eruption of jagged stone shards
 *  thrust upward with a magma flash at base + screen flash. The biggest preset in
 *  the axecraft corpus. Used by Faultline main hit. */
const faultline: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y;
    const h = sprite.displayHeight;

    // Camera flash in tint — magma core feel
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(220, r, g, b, false);

    // Bright magma core flash at the base — single big bright disc that fades fast
    const core = scene.add.graphics();
    core.setDepth(10000);
    core.fillStyle(0xffee88, 0.95);
    core.fillCircle(0, 0, h * 0.18);
    core.fillStyle(0xff5522, 0.85);
    core.fillCircle(0, 0, h * 0.12);
    core.setPosition(cx, baseY - 6);
    core.setScale(0.2);
    scene.tweens.add({
      targets: core,
      scale: 1.3,
      alpha: 0,
      duration: 320,
      ease: 'Power2.easeOut',
      onComplete: () => core.destroy(),
    });

    // 7 jagged stone shards thrust upward in a tall starburst
    const shardCount = 7;
    for (let i = 0; i < shardCount; i++) {
      const shard = scene.add.graphics();
      shard.setDepth(10000);

      // Spread shards in upper hemisphere (-PI to 0, weighted toward straight up)
      const t = i / (shardCount - 1);
      const angle = -Math.PI * 0.85 + t * Math.PI * 0.7; // -153° to -27°
      const length = h * (0.5 + Math.random() * 0.45);
      const halfW = 5 + Math.random() * 4;

      // Jagged stone shard — quadrilateral pointing along angle
      const tipX = Math.cos(angle) * length;
      const tipY = Math.sin(angle) * length;
      const baseLeftX = Math.cos(angle + Math.PI / 2) * halfW;
      const baseLeftY = Math.sin(angle + Math.PI / 2) * halfW;
      const baseRightX = -baseLeftX;
      const baseRightY = -baseLeftY;

      // Outline shadow
      shard.lineStyle(2, 0x000000, 0.85);
      shard.fillStyle(0x6e5238, 0.95); // dark earth-brown shard body
      shard.beginPath();
      shard.moveTo(baseLeftX, baseLeftY);
      shard.lineTo(tipX, tipY);
      shard.lineTo(baseRightX, baseRightY);
      shard.closePath();
      shard.fillPath();
      shard.strokePath();

      // Tan highlight along one edge
      shard.lineStyle(2, 0xc89a6c, 0.85);
      shard.lineBetween(baseLeftX * 0.5, baseLeftY * 0.5, tipX * 0.85, tipY * 0.85);

      // Magma seam where it cracked from the ground (tint-colored)
      shard.lineStyle(1.5, tint, 0.9);
      shard.lineBetween(0, 0, tipX * 0.4, tipY * 0.4);

      shard.setPosition(cx, baseY - 4);
      shard.setScale(1, 0); // start flat, scale outward

      // Erupt sequence
      scene.tweens.add({
        targets: shard,
        scaleY: 1,
        duration: 130,
        delay: i * 22,
        ease: 'Back.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: shard,
            alpha: 0,
            scaleY: 0.6,
            duration: 320,
            delay: 180,
            ease: 'Power2.easeIn',
            onComplete: () => shard.destroy(),
          });
        },
      });
    }

    // 8 debris chunks ringing outward from base
    for (let i = 0; i < 8; i++) {
      const debris = scene.add.graphics();
      debris.fillStyle(0x8a6648, 0.85);
      const size = 3 + Math.random() * 2;
      debris.fillCircle(0, 0, size);
      debris.setPosition(cx, baseY);
      debris.setDepth(10001);

      const angle = (Math.PI * 2 * i) / 8 - Math.PI * 0.5;
      const dist = h * 0.45 + Math.random() * 25;
      scene.tweens.add({
        targets: debris,
        x: cx + Math.cos(angle) * dist,
        y: baseY + Math.sin(angle) * dist * 0.5 - 12,
        alpha: 0,
        duration: 420,
        delay: 60 + i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => debris.destroy(),
      });
    }

    shakeSprite(scene, sprite, 12);
    flashSpriteTint(scene, sprite, tint, 260);
    scene.cameras.main.shake(280, 0.022);
  },
};

/** Earth Charge — cast windup played on the CASTER. Ground rings expand from feet,
 *  small dust puffs, glow ramps. Mirrors waterGather but with earth/rumble feel. */
const earthGather: AnimationPreset = {
  play(scene, sprite, tint) {
    const h = sprite.displayHeight;
    const cx = sprite.x;
    const footY = sprite.y;

    // PostFX glow ramp — telegraphs the incoming slam
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 0, 0, false, 0.08, 8);
      scene.tweens.add({
        targets: glow,
        outerStrength: 0.25,
        duration: 500,
        ease: 'Sine.easeIn',
        onComplete: () => {
          scene.time.delayedCall(200, () => {
            if (sprite.postFX) sprite.postFX.remove(glow);
            if ('restoreVisual' in scene) {
              (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
            }
          });
        },
      });
    }

    // 3 ground crack rings expanding outward from feet
    const ringBaseW = h * 0.26;
    const ringBaseH = h * 0.07;
    for (let i = 0; i < 3; i++) {
      scene.time.delayedCall(i * 180, () => {
        const ring = scene.add.graphics();
        ring.setDepth(10000);
        ring.lineStyle(3, tint, 0.85);
        ring.strokeEllipse(0, 0, ringBaseW, ringBaseH);
        ring.setPosition(cx, footY - 4);
        scene.tweens.add({
          targets: ring,
          scaleX: 3.0,
          scaleY: 3.0,
          alpha: 0,
          duration: 460,
          ease: 'Sine.easeOut',
          onComplete: () => ring.destroy(),
        });
      });
    }

    // Dust puffs around feet — small earth chunks rising and fading
    for (let i = 0; i < 5; i++) {
      const delay = 100 + i * 80;
      scene.time.delayedCall(delay, () => {
        const spread = h * 0.4;
        const startX = cx + (Math.random() - 0.5) * spread;
        const startY = footY - 4;
        const riseDist = h * (0.2 + Math.random() * 0.15);
        const size = 3 + Math.random() * 2;

        const puff = scene.add.graphics();
        puff.setDepth(10000);
        puff.fillStyle(0x886633, 0.8);
        puff.fillCircle(0, 0, size);
        puff.fillStyle(0xc89a6c, 0.6);
        puff.fillCircle(-size * 0.3, -size * 0.3, size * 0.5);
        puff.setPosition(startX, startY);

        scene.tweens.add({
          targets: puff,
          y: startY - riseDist,
          alpha: 0,
          duration: 380,
          ease: 'Sine.easeOut',
          onComplete: () => puff.destroy(),
        });
      });
    }

    // Subtle scale pulse — feels weighty as the caster gathers force
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    sprite.setScale(bs);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.05,
      scaleY: bs * 1.05,
      duration: 280,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });

    flashSpriteTint(scene, sprite, tint, 500);
  },
};

/** Aftershock pulse, small repeating earth burst at target's base. Brief and
 *  readable since this fires multiple times. Generic preset usable by any
 *  aftershock-class trigger (Faultline, future affixes/skills). */
const aftershockPulse: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y - 4;
    const h = sprite.displayHeight;

    // Single bright pulse ring at base
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    ring.lineStyle(4, tint, 0.9);
    ring.strokeEllipse(0, 0, h * 0.32, h * 0.09);
    ring.setPosition(cx, baseY);
    ring.setScale(0.3);
    scene.tweens.add({
      targets: ring,
      scale: 1.4,
      alpha: 0,
      duration: 240,
      ease: 'Power2.easeOut',
      onComplete: () => ring.destroy(),
    });

    // 3 small pebble pops radiating up-and-out from base
    for (let i = 0; i < 3; i++) {
      const pebble = scene.add.graphics();
      const sz = 2.5 + Math.random() * 1.5;
      pebble.fillStyle(0x886633, 0.9);
      pebble.fillCircle(0, 0, sz);
      pebble.fillStyle(0xff5522, 0.85);
      pebble.fillCircle(0, 0, sz * 0.5);
      pebble.setPosition(cx, baseY);
      pebble.setDepth(10001);

      const angle = -Math.PI * 0.75 + (i - 1) * 0.5;
      const dist = h * 0.18 + Math.random() * 8;
      scene.tweens.add({
        targets: pebble,
        x: cx + Math.cos(angle) * dist,
        y: baseY + Math.sin(angle) * dist - 6,
        alpha: 0,
        duration: 220,
        delay: 30,
        ease: 'Power2.easeOut',
        onComplete: () => pebble.destroy(),
      });
    }

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 110);
  },
};

/** Reality tears open across the target. A jagged crack draws on, dark motes
 *  eject outward like splatter, then the wound snaps closed. For the_rift's
 *  burst-summon move (Rupture). Distinct from summon_rise (gentle ground-up).
 *  Tuned to feel violent at combat scale — wider crack, white-hot core flash,
 *  16 motes erupting upward and outward, camera flash at the rip. */
const voidRupture: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const halfW = sprite.displayWidth * 0.7;
    const jagH = sprite.displayHeight * 0.07;

    // Build jagged zigzag points across the sprite width
    const segments = 9;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const px = cx - halfW + halfW * 2 * t;
      const py = cy + (i % 2 === 0 ? -jagH : jagH);
      points.push({ x: px, y: py });
    }

    // Crack draws across (0-160ms)
    const crack = scene.add.graphics();
    crack.setDepth(10000);
    const drawState = { progress: 0 };
    scene.tweens.add({
      targets: drawState,
      progress: 1,
      duration: 160,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        crack.clear();
        const total = segments;
        const cur = Math.min(total, Math.floor(drawState.progress * total));
        const frac = drawState.progress * total - cur;
        // Outer halo so the crack reads at combat scale
        crack.lineStyle(16, tint, 0.45);
        crack.beginPath();
        crack.moveTo(points[0].x, points[0].y);
        for (let i = 1; i <= cur; i++) crack.lineTo(points[i].x, points[i].y);
        if (cur < total && frac > 0) {
          const a = points[cur];
          const b = points[cur + 1];
          crack.lineTo(a.x + (b.x - a.x) * frac, a.y + (b.y - a.y) * frac);
        }
        crack.strokePath();
        // Tint body
        crack.lineStyle(10, tint, 1);
        crack.beginPath();
        crack.moveTo(points[0].x, points[0].y);
        for (let i = 1; i <= cur; i++) crack.lineTo(points[i].x, points[i].y);
        if (cur < total && frac > 0) {
          const a = points[cur];
          const b = points[cur + 1];
          crack.lineTo(a.x + (b.x - a.x) * frac, a.y + (b.y - a.y) * frac);
        }
        crack.strokePath();
        // White-hot inner core
        crack.lineStyle(4, 0xffffff, 0.95);
        crack.beginPath();
        crack.moveTo(points[0].x, points[0].y);
        for (let i = 1; i <= cur; i++) crack.lineTo(points[i].x, points[i].y);
        if (cur < total && frac > 0) {
          const a = points[cur];
          const b = points[cur + 1];
          crack.lineTo(a.x + (b.x - a.x) * frac, a.y + (b.y - a.y) * frac);
        }
        crack.strokePath();
      },
    });

    // Center pop when the crack peaks open (~150ms)
    scene.time.delayedCall(150, () => {
      const pop = scene.add.circle(cx, cy, 6, 0xffffff, 0.95);
      pop.setDepth(10001);
      scene.tweens.add({
        targets: pop,
        scaleX: 7,
        scaleY: 4,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeOut',
        onComplete: () => pop.destroy(),
      });
    });

    // Mote ejecta (180-500ms) — 16 small dark ellipses, biased upward like splatter
    scene.time.delayedCall(180, () => {
      const moteCount = 16;
      for (let i = 0; i < moteCount; i++) {
        const startX = cx - halfW + (halfW * 2 * i) / (moteCount - 1);
        const startY = cy;
        // Bias angles toward the upper hemisphere so motes erupt skyward (more violent than even spread)
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.85;
        const dist = 70 + Math.random() * 50;

        const mote = scene.add.graphics();
        mote.setDepth(10000);
        const moteW = 6 + Math.random() * 4;
        const moteH = 4 + Math.random() * 3;
        mote.fillStyle(tint, 0.95);
        mote.fillEllipse(0, 0, moteW * 2, moteH * 2);
        mote.fillStyle(0xffffff, 0.55);
        mote.fillEllipse(0, 0, moteW, moteH);
        mote.setPosition(startX, startY);
        mote.setRotation(Math.random() * Math.PI);

        scene.tweens.add({
          targets: mote,
          x: startX + Math.cos(angle) * dist,
          y: startY + Math.sin(angle) * dist,
          alpha: 0,
          rotation: mote.rotation + Math.PI,
          duration: 320,
          delay: i * 6,
          ease: 'Power2.easeOut',
          onComplete: () => mote.destroy(),
        });
      }

      // Camera flash with the tint — sells "reality just tore open"
      const r = (tint >> 16) & 0xff;
      const g = (tint >> 8) & 0xff;
      const b = tint & 0xff;
      scene.cameras.main.flash(150, r, g, b, false);
    });

    // Crack snaps closed (380-500ms)
    scene.time.delayedCall(380, () => {
      scene.tweens.add({
        targets: crack,
        alpha: 0,
        duration: 120,
        ease: 'Power2.easeIn',
        onComplete: () => crack.destroy(),
      });
    });

    shakeSprite(scene, sprite, 9);
    flashSpriteTint(scene, sprite, tint, 380);
  },
};

/** Heartbeat leak — a slim vertical seam parts on the target, bleeds a single
 *  dark pulse outward (oval expanding briefly), then contracts shut. Lower
 *  drama than Rupture/Collapse so the rift's escalation reads cleanly. For
 *  the_rift's chip-and-spawn move (Seep / enemy_rift_pulse). */
const riftSeep: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const seamH = sprite.displayHeight * 0.45;

    // Vertical seam draws open (0-130ms) — outer halo + tint body + white core
    const seam = scene.add.graphics();
    seam.setDepth(10000);
    const open = { v: 0 };
    scene.tweens.add({
      targets: open,
      v: 1,
      duration: 130,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        seam.clear();
        const half = (seamH * 0.5) * open.v;
        seam.lineStyle(11, tint, 0.5);
        seam.lineBetween(cx, cy - half, cx, cy + half);
        seam.lineStyle(7, tint, 1);
        seam.lineBetween(cx, cy - half, cx, cy + half);
        seam.lineStyle(2.5, 0xffffff, 0.9);
        seam.lineBetween(cx, cy - half, cx, cy + half);
      },
    });

    // Dark fluid pulse — oval expands outward from seam (90-280ms)
    scene.time.delayedCall(90, () => {
      const pulse = scene.add.graphics();
      pulse.setDepth(9999);
      pulse.fillStyle(tint, 0.55);
      pulse.fillEllipse(0, 0, sprite.displayWidth * 0.4, seamH * 0.7);
      pulse.fillStyle(tint, 0.85);
      pulse.fillEllipse(0, 0, sprite.displayWidth * 0.22, seamH * 0.4);
      pulse.setPosition(cx, cy);
      pulse.setScale(0.2);
      pulse.setAlpha(0.9);
      scene.tweens.add({
        targets: pulse,
        scaleX: 1.2,
        scaleY: 1.0,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeOut',
        onComplete: () => pulse.destroy(),
      });
    });

    // Five dark droplets bleed out — fall + fade (140-360ms)
    scene.time.delayedCall(140, () => {
      const dropCount = 5;
      for (let i = 0; i < dropCount; i++) {
        const sideSign = i % 2 === 0 ? -1 : 1;
        const startX = cx + sideSign * (4 + Math.random() * 6);
        const startY = cy + (Math.random() - 0.5) * seamH * 0.6;

        const drop = scene.add.graphics();
        drop.setDepth(10000);
        drop.fillStyle(tint, 0.95);
        drop.fillCircle(0, 0, 3 + Math.random() * 1.5);
        drop.setPosition(startX, startY);

        const arcX = startX + sideSign * (16 + Math.random() * 14);
        const arcY = startY + 14 + Math.random() * 18;
        scene.tweens.add({
          targets: drop,
          x: arcX,
          y: arcY,
          alpha: 0,
          duration: 220,
          delay: i * 18,
          ease: 'Sine.easeIn',
          onComplete: () => drop.destroy(),
        });
      }
    });

    // Seam contracts shut (260-360ms)
    scene.time.delayedCall(260, () => {
      const close = { v: 1 };
      scene.tweens.add({
        targets: close,
        v: 0,
        duration: 100,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          seam.clear();
          const half = (seamH * 0.5) * close.v;
          seam.lineStyle(11, tint, 0.5 * close.v);
          seam.lineBetween(cx, cy - half, cx, cy + half);
          seam.lineStyle(7, tint, close.v);
          seam.lineBetween(cx, cy - half, cx, cy + half);
          seam.lineStyle(2.5, 0xffffff, 0.9 * close.v);
          seam.lineBetween(cx, cy - half, cx, cy + half);
        },
        onComplete: () => seam.destroy(),
      });
    });

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 220);
  },
};

/** Per-summon entrance: dark portal coalesces at the spawn point and the new
 *  sprite materializes from inside. Outer ring shrinks and slams into the
 *  sprite's center, white-purple flash on contact, particles erupt outward.
 *  Used in CombatScene.addSummonedEnemy when the host's defensive type is dark
 *  (Rift's void motes). The sprite itself is fade-tweened in parallel. */
const voidEmerge: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const startR = Math.max(36, sprite.displayHeight * 0.55);

    // Outer portal ring tightens onto the spawn point (0-220ms)
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    const ringState = { r: startR, alpha: 0.85 };
    scene.tweens.add({
      targets: ringState,
      r: 8,
      alpha: 1,
      duration: 220,
      ease: 'Power3.easeIn',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(6, tint, ringState.alpha);
        ring.strokeCircle(cx, cy, ringState.r);
        ring.lineStyle(2, 0xffffff, ringState.alpha * 0.7);
        ring.strokeCircle(cx, cy, ringState.r);
      },
      onComplete: () => {
        // Convergence flash + particles (220-460ms)
        const flash = scene.add.circle(cx, cy, 8, 0xffffff, 0.95);
        flash.setDepth(10001);
        scene.tweens.add({
          targets: flash,
          scaleX: 4,
          scaleY: 4,
          alpha: 0,
          duration: 220,
          ease: 'Power2.easeOut',
          onComplete: () => flash.destroy(),
        });

        const particleCount = 10;
        for (let i = 0; i < particleCount; i++) {
          const a = (Math.PI * 2 * i) / particleCount;
          const p = scene.add.graphics();
          p.fillStyle(tint, 1);
          p.fillCircle(0, 0, 3 + Math.random() * 2);
          p.setPosition(cx, cy);
          p.setDepth(10000);
          const dist = 28 + Math.random() * 18;
          scene.tweens.add({
            targets: p,
            x: cx + Math.cos(a) * dist,
            y: cy + Math.sin(a) * dist,
            alpha: 0,
            duration: 240,
            ease: 'Power2.easeOut',
            onComplete: () => p.destroy(),
          });
        }

        ring.destroy();
      },
    });

    // Spiral wisps trail the ring (3 short arcs that follow the contraction)
    for (let i = 0; i < 3; i++) {
      const wisp = scene.add.graphics();
      wisp.setDepth(9999);
      const phase = (Math.PI * 2 * i) / 3;
      const wispState = { t: 0 };
      scene.tweens.add({
        targets: wispState,
        t: 1,
        duration: 220,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          wisp.clear();
          const r = startR * (1 - wispState.t);
          const a = phase + wispState.t * Math.PI * 1.5;
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          wisp.fillStyle(tint, 0.8 * (1 - wispState.t * 0.4));
          wisp.fillCircle(x, y, 4);
          wisp.fillStyle(0xffffff, 0.5 * (1 - wispState.t * 0.4));
          wisp.fillCircle(x, y, 1.5);
        },
        onComplete: () => wisp.destroy(),
      });
    }

    // Caster glow pulse on the new sprite as it materializes
    if (sprite.postFX) {
      const glow = sprite.postFX.addGlow(tint, 0, 0, false, 0.1, 8);
      scene.tweens.add({
        targets: glow,
        outerStrength: 0.35,
        duration: 220,
        ease: 'Sine.easeIn',
        onComplete: () => {
          scene.time.delayedCall(180, () => {
            if (sprite.postFX) sprite.postFX.remove(glow);
            if ('restoreVisual' in scene) {
              (scene as { restoreVisual: (s: Phaser.GameObjects.Sprite) => void }).restoreVisual(sprite);
            }
          });
        },
      });
    }

    flashSpriteTint(scene, sprite, tint, 280);
  },
};

/** Two-stage void detonation: tendrils sweep inward from sprite edges to center,
 *  then an 8-pointed dark starburst explodes outward with ground-up dust and a
 *  full-screen flash. For the_rift's consume-and-lash signature (Collapse). */
const voidCollapse: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayHeight * 0.65;

    // Stage 1: 8 tendrils sweep INWARD from edges to center (0-180ms)
    const tendrilCount = 8;
    for (let i = 0; i < tendrilCount; i++) {
      const angle = (Math.PI * 2 * i) / tendrilCount;
      const startX = cx + Math.cos(angle) * reach;
      const startY = cy + Math.sin(angle) * reach;

      const tendril = scene.add.graphics();
      tendril.setDepth(10000);
      const t = { v: 0 };
      scene.tweens.add({
        targets: t,
        v: 1,
        duration: 180,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          tendril.clear();
          const width = 2 + t.v * 8;
          const headX = startX + (cx - startX) * t.v;
          const headY = startY + (cy - startY) * t.v;
          tendril.lineStyle(width, tint, 0.85);
          tendril.beginPath();
          tendril.moveTo(startX, startY);
          tendril.lineTo(headX, headY);
          tendril.strokePath();
          tendril.lineStyle(Math.max(1, width * 0.35), 0xffffff, 0.7);
          tendril.beginPath();
          tendril.moveTo(startX, startY);
          tendril.lineTo(headX, headY);
          tendril.strokePath();
        },
        onComplete: () => {
          scene.tweens.add({
            targets: tendril,
            alpha: 0,
            duration: 100,
            onComplete: () => tendril.destroy(),
          });
        },
      });
    }

    // Stage 2: 8-pointed dark starburst explodes outward (180-400ms)
    scene.time.delayedCall(180, () => {
      const outerR = sprite.displayHeight * 0.6;
      const innerR = outerR * 0.3;
      const rayCount = 8;

      const star = scene.add.graphics();
      star.setDepth(10001);
      star.fillStyle(tint, 0.95);
      star.beginPath();
      for (let i = 0; i < rayCount * 2; i++) {
        const a = (Math.PI * 2 * i) / (rayCount * 2) - Math.PI / 2;
        const r = i % 2 === 0 ? outerR : innerR;
        const px = Math.cos(a) * r;
        const py = Math.sin(a) * r;
        if (i === 0) star.moveTo(px, py);
        else star.lineTo(px, py);
      }
      star.closePath();
      star.fillPath();
      star.fillStyle(0xffffff, 0.6);
      star.beginPath();
      for (let i = 0; i < rayCount * 2; i++) {
        const a = (Math.PI * 2 * i) / (rayCount * 2) - Math.PI / 2;
        const r = i % 2 === 0 ? outerR * 0.5 : innerR * 0.5;
        const px = Math.cos(a) * r;
        const py = Math.sin(a) * r;
        if (i === 0) star.moveTo(px, py);
        else star.lineTo(px, py);
      }
      star.closePath();
      star.fillPath();

      star.setPosition(cx, cy);
      star.setScale(0.15);

      scene.tweens.add({
        targets: star,
        scaleX: 1.4,
        scaleY: 1.4,
        rotation: Math.PI * 0.18,
        duration: 140,
        ease: 'Power3.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: star,
            alpha: 0,
            scaleX: 1.8,
            scaleY: 1.8,
            duration: 220,
            ease: 'Power2.easeIn',
            onComplete: () => star.destroy(),
          });
        },
      });

      // Ground-up dust ejecta (8 plumes from sprite base)
      const dustCount = 8;
      for (let i = 0; i < dustCount; i++) {
        const baseX = cx - sprite.displayWidth * 0.3 + (sprite.displayWidth * 0.6 * i) / (dustCount - 1);
        const baseY = sprite.y;
        const dust = scene.add.graphics();
        dust.fillStyle(tint, 0.7);
        dust.fillCircle(0, 0, 6 + Math.random() * 3);
        dust.setPosition(baseX, baseY);
        dust.setDepth(9999);

        scene.tweens.add({
          targets: dust,
          y: baseY - 30 - Math.random() * 20,
          alpha: 0,
          scale: 0.3,
          duration: 350,
          delay: 30 + i * 15,
          ease: 'Power2.easeOut',
          onComplete: () => dust.destroy(),
        });
      }

      const r = (tint >> 16) & 0xff;
      const g = (tint >> 8) & 0xff;
      const b = tint & 0xff;
      scene.cameras.main.flash(180, r, g, b, false);
      scene.cameras.main.shake(220, 0.020);
    });

    shakeSprite(scene, sprite, 12);
    flashSpriteTint(scene, sprite, tint, 380);
  },
};

/** Needle-in-the-mind: a thin astral spike snaps in from above, lances through the
 *  target's head area, splinters into 3 short fracture-cracks, and a small wisp of
 *  astral motes drifts upward.  Quick, surgical, ominous — tuned for Despair (~10k
 *  momentum debuff setup).  Distinct from `stab` (physical thrust from center),
 *  `shadow_burst` (full implode-release ritual), and `lightning_strike` (sky bolt). */
const mindNeedle: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    // Anchor visually toward the upper-third of the sprite — "mind" zone.
    const headY = cy - sprite.displayHeight * 0.25;
    const needleLen = Math.max(28, sprite.displayHeight * 0.32);
    const startX = cx;
    const startY = headY - needleLen - 10;

    // Phase 1 — needle snaps down (90ms).  Thin pointed line, white-hot core.
    const needle = scene.add.graphics();
    needle.setDepth(10000);
    needle.setPosition(startX, startY);

    const drawNeedle = (g: Phaser.GameObjects.Graphics, len: number) => {
      g.clear();
      // Outer glow halo — astral tint, soft
      g.lineStyle(5, tint, 0.55);
      g.lineBetween(0, 0, 0, len);
      // Inner core — white, sharp
      g.lineStyle(2, 0xffffff, 1);
      g.lineBetween(0, 0, 0, len);
    };
    drawNeedle(needle, needleLen);

    scene.tweens.add({
      targets: needle,
      y: headY,
      duration: 90,
      ease: 'Power3.easeIn',
      onComplete: () => {
        // Phase 2 — impact pulse: brief flash + 3 fracture cracks splaying from impact (180ms).
        needle.destroy();

        // Tiny impact flash dot at the strike point.
        const flash = scene.add.graphics();
        flash.fillStyle(0xffffff, 1);
        flash.fillCircle(0, 0, 4);
        flash.setPosition(cx, headY);
        flash.setDepth(10001);
        scene.tweens.add({
          targets: flash,
          alpha: 0,
          scaleX: 2.4,
          scaleY: 2.4,
          duration: 160,
          ease: 'Power2.easeOut',
          onComplete: () => flash.destroy(),
        });

        // 3 short fracture cracks — thin angular slivers radiating from the strike.
        const crackAngles = [-Math.PI * 0.55, -Math.PI * 0.78, -Math.PI * 0.32];
        for (let i = 0; i < crackAngles.length; i++) {
          const a = crackAngles[i];
          const reach = needleLen * 0.55;
          const crack = scene.add.graphics();
          crack.setDepth(10000);
          crack.setPosition(cx, headY);
          // Two-segment crack for a fractured, jagged feel
          const midX = Math.cos(a) * reach * 0.55;
          const midY = Math.sin(a) * reach * 0.55;
          const tipX = midX + Math.cos(a + 0.35) * reach * 0.5;
          const tipY = midY + Math.sin(a + 0.35) * reach * 0.5;
          crack.lineStyle(3, tint, 0.85);
          crack.lineBetween(0, 0, midX, midY);
          crack.lineBetween(midX, midY, tipX, tipY);

          scene.tweens.add({
            targets: crack,
            alpha: 0,
            duration: 200,
            delay: 30 + i * 20,
            ease: 'Power2.easeOut',
            onComplete: () => crack.destroy(),
          });
        }

        // Phase 3 — astral motes rising from the target. 5 small motes drift upward past
        // the head, tinted astral, fading.
        for (let i = 0; i < 5; i++) {
          const mote = scene.add.graphics();
          mote.fillStyle(tint, 0.95);
          mote.fillCircle(0, 0, 2);
          // Small white inner pip for layered detail
          mote.fillStyle(0xffffff, 0.9);
          mote.fillCircle(0, 0, 1);
          const offsetX = (Math.random() - 0.5) * sprite.displayWidth * 0.45;
          const offsetY = (Math.random() - 0.2) * 12;
          mote.setPosition(cx + offsetX, headY + offsetY);
          mote.setDepth(10000);

          scene.tweens.add({
            targets: mote,
            y: headY - 38 - Math.random() * 22,
            alpha: 0,
            duration: 460,
            delay: 60 + i * 40,
            ease: 'Sine.easeOut',
            onComplete: () => mote.destroy(),
          });
        }

        // Tiny camera flash — reinforces the "moment of cracking certainty".
        const r = (tint >> 16) & 0xff;
        const g = (tint >> 8) & 0xff;
        const b = tint & 0xff;
        scene.cameras.main.flash(60, r, g, b, false);
      },
    });

    // Target reaction: quick small shake (surgical, not heavy) + tint flash.
    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 200);
  },
};

/** Quick violet sigil-snap with two short fracture lines — Illusionism's light spam.
 *  Shares the kit's white-hot-core / violet-halo / fracture-crack motif with
 *  `mind_needle`, but reads as cheap chip damage: shorter, smaller, no camera flash,
 *  no rising motes. Designed to NOT fatigue across many casts (Dread is the spam builder). */
const mindPinprick: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const headY = cy - sprite.displayHeight * 0.18;
    const reach = Math.max(22, sprite.displayHeight * 0.22);

    // A small 4-pointed sigil snaps into existence at the strike point (0-90ms),
    // then fades. Layered: violet outline + white core diamond.
    const sigil = scene.add.graphics();
    sigil.setDepth(10000);
    sigil.setPosition(cx, headY);
    sigil.lineStyle(3, tint, 0.95);
    sigil.strokeTriangle(0, -reach * 0.55, reach * 0.32, 0, 0, reach * 0.55);
    sigil.strokeTriangle(0, -reach * 0.55, -reach * 0.32, 0, 0, reach * 0.55);
    sigil.fillStyle(0xffffff, 0.9);
    sigil.fillTriangle(0, -reach * 0.30, reach * 0.16, 0, 0, reach * 0.30);
    sigil.fillTriangle(0, -reach * 0.30, -reach * 0.16, 0, 0, reach * 0.30);
    sigil.setScale(0.2);
    sigil.setRotation(Math.random() * 0.6 - 0.3);

    scene.tweens.add({
      targets: sigil,
      scaleX: 1,
      scaleY: 1,
      duration: 90,
      ease: 'Power3.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: sigil,
          alpha: 0,
          scaleX: 1.25,
          scaleY: 1.25,
          duration: 140,
          ease: 'Power2.easeIn',
          onComplete: () => sigil.destroy(),
        });
      },
    });

    // Two short fracture cracks splay outward (one up-left, one up-right).
    const crackAngles = [-Math.PI * 0.7, -Math.PI * 0.3];
    for (let i = 0; i < crackAngles.length; i++) {
      const a = crackAngles[i];
      const len = reach * 0.6;
      const crack = scene.add.graphics();
      crack.setDepth(10000);
      crack.setPosition(cx, headY);
      const tipX = Math.cos(a) * len;
      const tipY = Math.sin(a) * len;
      crack.lineStyle(2, tint, 0.85);
      crack.lineBetween(0, 0, tipX, tipY);
      crack.lineStyle(1, 0xffffff, 0.9);
      crack.lineBetween(0, 0, tipX, tipY);
      scene.tweens.add({
        targets: crack,
        alpha: 0,
        duration: 180,
        delay: 40 + i * 15,
        ease: 'Power2.easeOut',
        onComplete: () => crack.destroy(),
      });
    }

    // Surgical reaction — no camera flash, light shake. This is the cheap builder.
    shakeSprite(scene, sprite, 3);
    flashSpriteTint(scene, sprite, tint, 140);
  },
};

/** Sweeping ring of violet sigils orbiting the target + jitter shake — Bewilder's
 *  AoE confusion stagger. Plays on each enemy hit. Larger and slower than
 *  `mind_pinprick`, conveys "the world spins." Shares the violet-halo / white-core
 *  motif. Light camera flash because Bewilder is the heavier of the two builders. */
const mindSwarm: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const radius = Math.max(36, sprite.displayHeight * 0.35);

    // A faint violet halo ring snaps in around the sprite's center.
    const halo = scene.add.graphics();
    halo.setDepth(9999);
    halo.lineStyle(4, tint, 0.55);
    halo.strokeEllipse(0, 0, radius * 2.1, radius * 0.9);
    halo.lineStyle(2, 0xffffff, 0.6);
    halo.strokeEllipse(0, 0, radius * 2.1, radius * 0.9);
    halo.setPosition(cx, cy);
    halo.setScale(0.4);

    scene.tweens.add({
      targets: halo,
      scaleX: 1,
      scaleY: 1,
      alpha: 0,
      duration: 320,
      ease: 'Power2.easeOut',
      onComplete: () => halo.destroy(),
    });

    // 6 small triangular sigils orbit and fade. Staggered start so they read as a swarm.
    const sigilCount = 6;
    for (let i = 0; i < sigilCount; i++) {
      const startA = (Math.PI * 2 * i) / sigilCount;
      const sigil = scene.add.graphics();
      sigil.setDepth(10000);
      // Triangular sigil — varied shape, not a circle particle.
      sigil.fillStyle(tint, 0.9);
      sigil.fillTriangle(0, -6, 5, 4, -5, 4);
      sigil.fillStyle(0xffffff, 0.8);
      sigil.fillTriangle(0, -3, 2.5, 2, -2.5, 2);
      const startX = cx + Math.cos(startA) * radius;
      const startY = cy + Math.sin(startA) * radius * 0.5;
      sigil.setPosition(startX, startY);
      sigil.setRotation(startA + Math.PI / 2);

      // Each sigil orbits a quarter turn then fades into the target.
      const orbitState = { t: 0 };
      scene.tweens.add({
        targets: orbitState,
        t: 1,
        duration: 280,
        delay: i * 25,
        ease: 'Sine.easeIn',
        onUpdate: () => {
          const a = startA + orbitState.t * Math.PI * 0.6;
          const r = radius * (1 - orbitState.t * 0.5);
          sigil.setPosition(cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.5);
          sigil.setRotation(a + Math.PI / 2);
          sigil.setAlpha(1 - orbitState.t);
        },
        onComplete: () => sigil.destroy(),
      });
    }

    // Brief camera-tint flash at peak (180ms in) — the disorientation moment.
    scene.time.delayedCall(180, () => {
      const r = (tint >> 16) & 0xff;
      const g = (tint >> 8) & 0xff;
      const b = tint & 0xff;
      scene.cameras.main.flash(70, r, g, b, false);
    });

    // Target reaction: jitter (back-and-forth nudges, NOT a clean shake) +
    // small scale wobble. Reads as "the world spins."
    const originalX = sprite.x;
    scene.tweens.add({
      targets: sprite,
      x: originalX + 4,
      duration: 40,
      yoyo: true,
      repeat: 4,
      onComplete: () => { sprite.x = originalX; },
    });
    flashSpriteTint(scene, sprite, tint, 220);
  },
};

/** Pale-violet stars descend and form a crown above the ally's head — Acuity buff.
 *  Uplifting, NOT aggressive. Shares the kit's white-core / violet-halo motif,
 *  but flipped tonally: stars rise/settle (gift), no shake, gentle scale pulse.
 *  Distinct from `buff_ward` by motif (4-pointed sigil-stars instead of trifold,
 *  crown formation above head instead of ring at center, brighter pale-violet
 *  palette anchored to the illusionism family). */
const mindClarity: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const crownY = cy - sprite.displayHeight * 0.42;

    // Soft pale-violet wash on the sprite (keeps tint authoritative on cast color).
    flashSpriteTint(scene, sprite, tint, 320);

    // 7 sigil-stars descend from above and arc into a crown formation.
    const starCount = 7;
    const crownRadiusX = sprite.displayWidth * 0.45;
    const crownRadiusY = sprite.displayHeight * 0.10;
    for (let i = 0; i < starCount; i++) {
      const t = i / (starCount - 1);
      const targetA = -Math.PI + Math.PI * t; // top semicircle
      const targetX = cx + Math.cos(targetA) * crownRadiusX;
      const targetY = crownY + Math.sin(targetA) * crownRadiusY;

      const star = scene.add.graphics();
      star.setDepth(10000);
      // 4-pointed sigil-star (kit motif)
      star.fillStyle(tint, 0.95);
      star.fillTriangle(0, -7, 2, 0, 0, 7);
      star.fillTriangle(0, -7, -2, 0, 0, 7);
      star.fillTriangle(-7, 0, 0, 2, 7, 0);
      star.fillTriangle(-7, 0, 0, -2, 7, 0);
      // White core
      star.fillStyle(0xffffff, 1);
      star.fillCircle(0, 0, 2);

      const startX = cx + (Math.random() - 0.5) * sprite.displayWidth * 0.6;
      const startY = crownY - sprite.displayHeight * 0.45 - Math.random() * 18;
      star.setPosition(startX, startY);
      star.setAlpha(0);
      star.setScale(0.4);

      // Descend & settle (0-280ms)
      scene.tweens.add({
        targets: star,
        x: targetX,
        y: targetY,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 280,
        delay: i * 35,
        ease: 'Sine.easeOut',
      });
      // Slow rotation while settled
      scene.tweens.add({
        targets: star,
        rotation: Math.PI * 0.5,
        duration: 400,
        delay: i * 35 + 280,
        ease: 'Linear',
      });
      // Fade out
      scene.tweens.add({
        targets: star,
        alpha: 0,
        scaleX: 1.4,
        scaleY: 1.4,
        duration: 220,
        delay: i * 35 + 320,
        ease: 'Power2.easeIn',
        onComplete: () => star.destroy(),
      });
    }

    // Connecting arc of light underneath the crown (snaps in once stars are placed).
    scene.time.delayedCall(220, () => {
      const arc = scene.add.graphics();
      arc.lineStyle(2, 0xffffff, 0.85);
      arc.beginPath();
      arc.arc(0, 0, crownRadiusX, -Math.PI, 0, false);
      arc.strokePath();
      arc.lineStyle(4, tint, 0.55);
      arc.beginPath();
      arc.arc(0, 0, crownRadiusX, -Math.PI, 0, false);
      arc.strokePath();
      arc.setPosition(cx, crownY);
      arc.setDepth(9999);
      arc.setScale(0.6, 0.6);
      scene.tweens.add({
        targets: arc,
        scaleX: 1,
        scaleY: 1,
        alpha: 0,
        duration: 260,
        ease: 'Power2.easeOut',
        onComplete: () => arc.destroy(),
      });
    });

    // Gentle uplifting scale pulse — no shake (this is a gift, not a hit).
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.05,
      scaleY: bs * 1.05,
      duration: 180,
      delay: 80,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Cinematic mind-shatter — Nightmare's signature Break payload.  Three-act
 *  vision: (1) violet visions converge inward as ghost-sigils, (2) the target's
 *  perception cracks open as a giant 8-pointed sigil-star explodes outward, (3)
 *  fractured shards rain down + heavy camera shake. */
const mindCollapse: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = Math.max(80, sprite.displayHeight * 0.7);

    // ---- ACT 1 (0-220ms) — visions converge inward ----
    const visionCount = 8;
    for (let i = 0; i < visionCount; i++) {
      const angle = (Math.PI * 2 * i) / visionCount;
      const startX = cx + Math.cos(angle) * reach;
      const startY = cy + Math.sin(angle) * reach * 0.6;

      const vision = scene.add.graphics();
      vision.setDepth(10000);
      // Ghost-sigil — violet teardrop with white core
      vision.fillStyle(tint, 0.85);
      vision.fillEllipse(0, 0, 14, 8);
      vision.fillStyle(0xffffff, 0.9);
      vision.fillEllipse(0, 0, 6, 3);
      vision.setPosition(startX, startY);
      vision.setRotation(angle + Math.PI);

      scene.tweens.add({
        targets: vision,
        x: cx,
        y: cy,
        scaleX: 0.4,
        scaleY: 0.4,
        alpha: 0.5,
        duration: 220,
        delay: i * 8,
        ease: 'Power3.easeIn',
        onComplete: () => vision.destroy(),
      });
    }

    // ---- ACT 2 (220-440ms) — perception cracks open: giant 8-pointed sigil-star ----
    scene.time.delayedCall(220, () => {
      const outerR = reach * 1.05;
      const innerR = outerR * 0.32;
      const rayCount = 8;

      // Build star polygon
      const star = scene.add.graphics();
      star.setDepth(10001);
      const drawStar = (g: Phaser.GameObjects.Graphics, fillColor: number, scale: number, alpha: number) => {
        g.fillStyle(fillColor, alpha);
        g.beginPath();
        for (let i = 0; i < rayCount * 2; i++) {
          const a = (Math.PI * 2 * i) / (rayCount * 2) - Math.PI / 2;
          const r = (i % 2 === 0 ? outerR : innerR) * scale;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.fillPath();
      };
      drawStar(star, tint, 1, 0.92);
      drawStar(star, 0xffffff, 0.45, 0.95);
      star.setPosition(cx, cy);
      star.setScale(0.18);

      scene.tweens.add({
        targets: star,
        scaleX: 1.5,
        scaleY: 1.5,
        rotation: Math.PI * 0.22,
        duration: 160,
        ease: 'Power3.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: star,
            alpha: 0,
            scaleX: 1.95,
            scaleY: 1.95,
            duration: 220,
            ease: 'Power2.easeIn',
            onComplete: () => star.destroy(),
          });
        },
      });

      // Cracking sigil-rim — 4 angular rune-like cracks radiating from center
      const rimAngles = [
        -Math.PI / 2 - 0.12,
        -Math.PI / 2 + 0.12,
        Math.PI / 2 - 0.18,
        Math.PI / 2 + 0.18,
      ];
      for (let i = 0; i < rimAngles.length; i++) {
        const a = rimAngles[i];
        const crack = scene.add.graphics();
        crack.setDepth(10002);
        crack.setPosition(cx, cy);
        const segLen = reach * 0.85;
        const midX = Math.cos(a) * segLen * 0.55;
        const midY = Math.sin(a) * segLen * 0.55;
        const tipX = midX + Math.cos(a + (i % 2 === 0 ? 0.45 : -0.45)) * segLen * 0.5;
        const tipY = midY + Math.sin(a + (i % 2 === 0 ? 0.45 : -0.45)) * segLen * 0.5;
        crack.lineStyle(7, tint, 0.95);
        crack.lineBetween(0, 0, midX, midY);
        crack.lineBetween(midX, midY, tipX, tipY);
        crack.lineStyle(3, 0xffffff, 1);
        crack.lineBetween(0, 0, midX, midY);
        crack.lineBetween(midX, midY, tipX, tipY);
        scene.tweens.add({
          targets: crack,
          alpha: 0,
          duration: 280,
          delay: 40 + i * 25,
          ease: 'Power2.easeOut',
          onComplete: () => crack.destroy(),
        });
      }

      // Camera flash on the open
      const r = (tint >> 16) & 0xff;
      const g = (tint >> 8) & 0xff;
      const b = tint & 0xff;
      scene.cameras.main.flash(180, r, g, b, false);
    });

    // ---- ACT 3 (380-700ms) — fractured shards rain down ----
    scene.time.delayedCall(380, () => {
      const shardCount = 10;
      for (let i = 0; i < shardCount; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.3;
        const dist = reach * (0.7 + Math.random() * 0.5);
        const shard = scene.add.graphics();
        shard.setDepth(10000);
        // Diamond-shaped shard
        const shardLen = 6 + Math.random() * 4;
        shard.fillStyle(tint, 0.95);
        shard.fillTriangle(0, -shardLen, shardLen * 0.5, 0, 0, shardLen);
        shard.fillTriangle(0, -shardLen, -shardLen * 0.5, 0, 0, shardLen);
        shard.fillStyle(0xffffff, 0.85);
        shard.fillTriangle(0, -shardLen * 0.5, shardLen * 0.2, 0, 0, shardLen * 0.5);
        shard.fillTriangle(0, -shardLen * 0.5, -shardLen * 0.2, 0, 0, shardLen * 0.5);
        shard.setPosition(cx, cy);
        shard.setRotation(Math.random() * Math.PI);

        scene.tweens.add({
          targets: shard,
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist,
          alpha: 0,
          rotation: shard.rotation + Math.PI * (Math.random() > 0.5 ? 1 : -1),
          duration: 320,
          delay: i * 12,
          ease: 'Power2.easeOut',
          onComplete: () => shard.destroy(),
        });
      }
    });

    // Heavy reaction: big shake + extended tint flash. Cinematic weight.
    shakeSprite(scene, sprite, 11);
    flashSpriteTint(scene, sprite, tint, 480);
  },
};

// ---------------------------------------------------------------------------
// Necrotics (dark) — bone-grey + violet, scythe / drain-spiral / void-collapse
// Shared kit motif: pale-bone outer line + violet mid + white-hot core,
// curved/sickle shapes (scythes, spirals, ribs) — distinct from the angular
// sigil family of Illusionism and the radial implosion of shadow_burst.
// ---------------------------------------------------------------------------

/** Reaper's scythe: a wide curved arc sweeps across the target, cutting from
 *  upper-right to lower-left in a sickle motion. Spectral bone-grey trail with
 *  violet halo and white-hot inner edge. Identity beat for Reap (signature
 *  Fervor-builder + low-HP execute) — distinct from `crescent_sweep` (frontal
 *  arc) by the sickle curve and the bone-grey stroke. */
const reaperScythe: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const radius = sprite.displayHeight * 0.6;

    // Sickle arc — drawn as a wide elliptical sweep, top-right around to bottom-left
    const arc = scene.add.graphics();
    arc.setDepth(10000);
    const sweep = { progress: 0 };
    const startAngle = -Math.PI * 0.35;
    const fullSweep = Math.PI * 1.25;

    scene.tweens.add({
      targets: sweep,
      progress: 1,
      duration: 170,
      ease: 'Power3.easeOut',
      onUpdate: () => {
        arc.clear();
        const currentEnd = startAngle + fullSweep * sweep.progress;
        const trailStart = startAngle + fullSweep * Math.max(0, sweep.progress - 0.55);
        // Outer pale-bone trail (slightly stretched to feel like a scythe path)
        arc.lineStyle(10, 0xddd2bb, 0.55);
        arc.beginPath();
        arc.arc(cx, cy, radius * 1.1, trailStart, currentEnd, false);
        arc.strokePath();
        // Mid violet edge — kit tint
        arc.lineStyle(6, tint, 0.85);
        arc.beginPath();
        arc.arc(cx, cy, radius * 1.05, trailStart, currentEnd, false);
        arc.strokePath();
        // White-hot inner cut
        arc.lineStyle(2, 0xffffff, 0.95);
        arc.beginPath();
        arc.arc(cx, cy, radius, trailStart, currentEnd, false);
        arc.strokePath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: arc,
          alpha: 0,
          duration: 180,
          ease: 'Power2.easeIn',
          onComplete: () => arc.destroy(),
        });
      },
    });

    // 5 spectral wisps trailing off the cut path — bone-grey teardrops drifting
    // perpendicular to the arc, fading. Reads as soul-fragments lifted by the swing.
    scene.time.delayedCall(120, () => {
      for (let i = 0; i < 5; i++) {
        const a = startAngle + fullSweep * (0.3 + i * 0.16);
        const wispX = cx + Math.cos(a) * radius;
        const wispY = cy + Math.sin(a) * radius;
        const wisp = scene.add.graphics();
        wisp.setDepth(10001);
        wisp.fillStyle(0xddd2bb, 0.85);
        wisp.fillEllipse(0, 0, 8, 4);
        wisp.fillStyle(0xffffff, 0.6);
        wisp.fillEllipse(0, 0, 4, 2);
        wisp.setPosition(wispX, wispY);
        wisp.setRotation(a + Math.PI / 2);

        const driftAngle = a - Math.PI / 2;
        scene.tweens.add({
          targets: wisp,
          x: wispX + Math.cos(driftAngle) * 22,
          y: wispY + Math.sin(driftAngle) * 22 - 8,
          alpha: 0,
          scale: 0.4,
          duration: 280,
          delay: i * 18,
          ease: 'Sine.easeOut',
          onComplete: () => wisp.destroy(),
        });
      }
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 220);
    scene.cameras.main.shake(70, 0.005);
  },
};

/** Devour: a violet-and-bone whirlpool consumes the target. Particles spiral inward
 *  along TWO bands (outer slow bone, inner fast violet) and erupt as a brief
 *  white-hot core flash when they converge. Identity beat for Devour (AoE Fervor
 *  spender) — distinct from `vortex` (single uniform spiral) by the dual-band
 *  layered feel that reads as "drained life pulled through." */
const voidDevour: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const maxRadius = sprite.displayHeight * 0.6;

    // Outer band — pale bone-grey teardrops, slow inward spiral (8 particles)
    const outerCount = 8;
    for (let i = 0; i < outerCount; i++) {
      const startA = (Math.PI * 2 * i) / outerCount;
      const wisp = scene.add.graphics();
      wisp.setDepth(10000);
      wisp.fillStyle(0xddd2bb, 0.85);
      wisp.fillEllipse(0, 0, 7, 4);
      wisp.fillStyle(0xffffff, 0.55);
      wisp.fillEllipse(0, 0, 3, 2);
      const startR = maxRadius + Math.random() * 8;
      wisp.setPosition(cx + Math.cos(startA) * startR, cy + Math.sin(startA) * startR * 0.55);
      wisp.setRotation(startA + Math.PI / 2);

      const spiral = { angle: startA, radius: startR };
      scene.tweens.add({
        targets: spiral,
        angle: startA + Math.PI * 2.2,
        radius: 0,
        duration: 420,
        delay: i * 18,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          wisp.setPosition(
            cx + Math.cos(spiral.angle) * spiral.radius,
            cy + Math.sin(spiral.angle) * spiral.radius * 0.55,
          );
          wisp.setRotation(spiral.angle + Math.PI / 2);
          wisp.setAlpha(0.3 + 0.7 * (spiral.radius / maxRadius));
        },
        onComplete: () => wisp.destroy(),
      });
    }

    // Inner band — violet diamond shards, fast tight spiral (8 particles, smaller radius)
    const innerCount = 8;
    for (let i = 0; i < innerCount; i++) {
      const startA = (Math.PI * 2 * i) / innerCount + Math.PI / innerCount;
      const shard = scene.add.graphics();
      shard.setDepth(10001);
      shard.fillStyle(tint, 0.95);
      shard.fillTriangle(0, -4, 2, 0, 0, 4);
      shard.fillTriangle(0, -4, -2, 0, 0, 4);
      shard.fillStyle(0xffffff, 0.85);
      shard.fillCircle(0, 0, 1.2);
      const startR = maxRadius * 0.7;
      shard.setPosition(cx + Math.cos(startA) * startR, cy + Math.sin(startA) * startR * 0.55);

      const spiral = { angle: startA, radius: startR };
      scene.tweens.add({
        targets: spiral,
        angle: startA + Math.PI * 3,
        radius: 0,
        duration: 340,
        delay: 60 + i * 14,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          shard.setPosition(
            cx + Math.cos(spiral.angle) * spiral.radius,
            cy + Math.sin(spiral.angle) * spiral.radius * 0.55,
          );
          shard.setRotation(spiral.angle * 2);
        },
        onComplete: () => shard.destroy(),
      });
    }

    // Convergence core flash + small shockwave ring
    scene.time.delayedCall(400, () => {
      const flash = scene.add.graphics();
      flash.setDepth(10002);
      flash.fillStyle(0xffffff, 0.9);
      flash.fillCircle(0, 0, 8);
      flash.fillStyle(tint, 0.7);
      flash.fillCircle(0, 0, 14);
      flash.setPosition(cx, cy);
      flash.setScale(0.3);
      scene.tweens.add({
        targets: flash,
        scaleX: 2.2,
        scaleY: 2.2,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeOut',
        onComplete: () => flash.destroy(),
      });

      const ring = scene.add.graphics();
      ring.setDepth(10001);
      ring.lineStyle(3, tint, 0.9);
      ring.strokeEllipse(0, 0, maxRadius * 1.2, maxRadius * 0.7);
      ring.lineStyle(1.5, 0xffffff, 0.8);
      ring.strokeEllipse(0, 0, maxRadius * 1.2, maxRadius * 0.7);
      ring.setPosition(cx, cy);
      ring.setScale(0.2);
      scene.tweens.add({
        targets: ring,
        scaleX: 1,
        scaleY: 1,
        alpha: 0,
        duration: 260,
        ease: 'Power2.easeOut',
        onComplete: () => ring.destroy(),
      });
    });

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 320);
  },
};

/** Oblivion: signature dark single-target nuke. Two-act collapse — (1) violet
 *  sigil-circle implodes to a singular white-hot point on the target, (2) a
 *  jagged 6-rayed dark starburst explodes outward with bone-grey shrapnel rain.
 *  Heavier weight than reaper_scythe; quieter cinematic than mind_collapse
 *  (no full-screen flash, but bigger shake than oblivion's old devastating_impact). */
const oblivionVoid: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayHeight * 0.7;

    // ---- ACT 1 (0-200ms): violet sigil-circle implodes to a point ----
    const sigil = scene.add.graphics();
    sigil.setDepth(10000);
    // Outer ring
    sigil.lineStyle(4, tint, 0.9);
    sigil.strokeCircle(0, 0, reach);
    // Inner ring
    sigil.lineStyle(2, 0xddd2bb, 0.85);
    sigil.strokeCircle(0, 0, reach * 0.65);
    // 6 rune-tick marks around outer ring
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      const x1 = Math.cos(a) * reach;
      const y1 = Math.sin(a) * reach;
      const x2 = Math.cos(a) * (reach * 1.15);
      const y2 = Math.sin(a) * (reach * 1.15);
      sigil.lineStyle(3, tint, 0.95);
      sigil.lineBetween(x1, y1, x2, y2);
    }
    sigil.setPosition(cx, cy);
    sigil.setAlpha(0);
    sigil.setScale(1.4);

    scene.tweens.add({
      targets: sigil,
      alpha: 1,
      scaleX: 0.05,
      scaleY: 0.05,
      rotation: -Math.PI * 0.4,
      duration: 200,
      ease: 'Power3.easeIn',
      onComplete: () => sigil.destroy(),
    });

    // ---- ACT 2 (200-460ms): jagged 6-rayed dark starburst ----
    scene.time.delayedCall(200, () => {
      const outerR = reach * 1.1;
      const innerR = outerR * 0.28;
      const rayCount = 6;
      const star = scene.add.graphics();
      star.setDepth(10001);

      const drawStar = (g: Phaser.GameObjects.Graphics, color: number, scale: number, alpha: number) => {
        g.fillStyle(color, alpha);
        g.beginPath();
        for (let i = 0; i < rayCount * 2; i++) {
          const a = (Math.PI * 2 * i) / (rayCount * 2) - Math.PI / 2;
          const r = (i % 2 === 0 ? outerR : innerR) * scale;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.fillPath();
      };
      drawStar(star, tint, 1, 0.95);
      drawStar(star, 0xffffff, 0.4, 0.85);
      star.setPosition(cx, cy);
      star.setScale(0.15);

      scene.tweens.add({
        targets: star,
        scaleX: 1.4,
        scaleY: 1.4,
        rotation: Math.PI * 0.18,
        duration: 130,
        ease: 'Power3.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: star,
            alpha: 0,
            scaleX: 1.7,
            scaleY: 1.7,
            duration: 200,
            ease: 'Power2.easeIn',
            onComplete: () => star.destroy(),
          });
        },
      });

      // Bone-grey shrapnel rain — 8 small chips fly outward & downward
      for (let i = 0; i < 8; i++) {
        const angle = -Math.PI / 2 + (i - 3.5) * 0.42;
        const dist = reach * (0.6 + Math.random() * 0.5);
        const chip = scene.add.graphics();
        chip.setDepth(10000);
        const sz = 4 + Math.random() * 3;
        chip.fillStyle(0xddd2bb, 0.95);
        chip.fillTriangle(0, -sz, sz * 0.5, 0, 0, sz);
        chip.fillTriangle(0, -sz, -sz * 0.5, 0, 0, sz);
        chip.setPosition(cx, cy);
        chip.setRotation(Math.random() * Math.PI);

        scene.tweens.add({
          targets: chip,
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist + 12,
          alpha: 0,
          rotation: chip.rotation + Math.PI * (Math.random() > 0.5 ? 1.3 : -1.3),
          duration: 320,
          delay: i * 14,
          ease: 'Power2.easeOut',
          onComplete: () => chip.destroy(),
        });
      }

      // Brief tint-flash on camera (smaller than mind_collapse — kept quieter)
      const r = (tint >> 16) & 0xff;
      const g = (tint >> 8) & 0xff;
      const b = tint & 0xff;
      scene.cameras.main.flash(110, r, g, b, false);
    });

    shakeSprite(scene, sprite, 10);
    flashSpriteTint(scene, sprite, tint, 380);
    scene.cameras.main.shake(140, 0.012);
  },
};

// ---------------------------------------------------------------------------
// Pyromancy (fire) — orange-core + white-hot inner, projected flames
// ---------------------------------------------------------------------------

/** Flame jet: a focused tongue of fire projects across the target and bursts.
 *  Layered: outer orange flame body + white-hot core + ember sparks shedding
 *  off the leading edge. Identity beat for Fire Blast (R5 ranged fire nuke) —
 *  distinct from `starburst` (radial explosion) by the directional jet shape. */
const flameJet: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayHeight * 0.7;

    // Phase 1: a horizontal flame jet draws in from the left, leading edge
    // ballooning outward as it lands.
    const jet = scene.add.graphics();
    jet.setDepth(10000);

    const draw = { progress: 0 };
    scene.tweens.add({
      targets: draw,
      progress: 1,
      duration: 140,
      ease: 'Power3.easeOut',
      onUpdate: () => {
        jet.clear();
        const tipX = cx + (-reach * 0.6) + reach * 1.2 * draw.progress;
        const baseX = cx - reach * 0.6;
        const headW = 18 + 14 * draw.progress;
        const tailW = 8;

        // Outer flame body — tapered ellipse from tail to head
        jet.fillStyle(tint, 0.9);
        jet.beginPath();
        jet.moveTo(baseX, cy - tailW);
        jet.lineTo(tipX - 8, cy - headW);
        jet.lineTo(tipX, cy);
        jet.lineTo(tipX - 8, cy + headW);
        jet.lineTo(baseX, cy + tailW);
        jet.closePath();
        jet.fillPath();

        // Mid amber band
        jet.fillStyle(0xffaa44, 0.85);
        jet.beginPath();
        jet.moveTo(baseX + 4, cy - tailW * 0.6);
        jet.lineTo(tipX - 12, cy - headW * 0.6);
        jet.lineTo(tipX - 4, cy);
        jet.lineTo(tipX - 12, cy + headW * 0.6);
        jet.lineTo(baseX + 4, cy + tailW * 0.6);
        jet.closePath();
        jet.fillPath();

        // White-hot core
        jet.fillStyle(0xffffff, 0.95);
        jet.beginPath();
        jet.moveTo(baseX + 6, cy - tailW * 0.25);
        jet.lineTo(tipX - 16, cy - headW * 0.25);
        jet.lineTo(tipX - 8, cy);
        jet.lineTo(tipX - 16, cy + headW * 0.25);
        jet.lineTo(baseX + 6, cy + tailW * 0.25);
        jet.closePath();
        jet.fillPath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: jet,
          alpha: 0,
          duration: 160,
          ease: 'Power2.easeIn',
          onComplete: () => jet.destroy(),
        });
      },
    });

    // Phase 2: 7 ember sparks shed off the impact point, fanning forward and up
    scene.time.delayedCall(110, () => {
      for (let i = 0; i < 7; i++) {
        const ember = scene.add.graphics();
        ember.setDepth(10001);
        const sz = 2 + Math.random() * 2;
        ember.fillStyle(0xffaa44, 0.9);
        ember.fillCircle(0, 0, sz);
        ember.fillStyle(0xffffff, 0.85);
        ember.fillCircle(0, 0, sz * 0.5);
        ember.setPosition(cx + reach * 0.5, cy);
        const a = -Math.PI * 0.25 + (Math.random() - 0.5) * Math.PI * 0.7;
        const dist = 28 + Math.random() * 24;
        scene.tweens.add({
          targets: ember,
          x: ember.x + Math.cos(a) * dist,
          y: ember.y + Math.sin(a) * dist,
          alpha: 0,
          scale: 0.4,
          duration: 320,
          delay: i * 12,
          ease: 'Sine.easeOut',
          onComplete: () => ember.destroy(),
        });
      }
    });

    shakeSprite(scene, sprite, 6);
    flashSpriteTint(scene, sprite, tint, 240);
    scene.cameras.main.shake(80, 0.006);
  },
};

/** Pyre Immolate: target is engulfed in an upward column of flame with crackling
 *  ember halos and a hovering ring of fire. Identity beat for Immolate (R10
 *  Fervor-spending burn-extender) — sells the "set them on fire" fantasy with
 *  vertical column + sustained ember field. Distinct from `flame_jet` (lateral
 *  projection) and `starburst` (radial). */
const pyreImmolate: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const baseY = sprite.y - 4;
    const h = sprite.displayHeight;

    // ---- ACT 1 (0-180ms): vertical flame column rises from base ----
    const column = scene.add.graphics();
    column.setDepth(10000);
    const rise = { progress: 0 };
    scene.tweens.add({
      targets: rise,
      progress: 1,
      duration: 180,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        column.clear();
        const colH = h * 0.95 * rise.progress;
        const baseW = h * 0.32;
        const tipW = baseW * 0.35;
        // Outer flame body — tapered top
        column.fillStyle(tint, 0.85);
        column.beginPath();
        column.moveTo(cx - baseW * 0.5, baseY);
        column.lineTo(cx - tipW * 0.5, baseY - colH);
        column.lineTo(cx + tipW * 0.5, baseY - colH);
        column.lineTo(cx + baseW * 0.5, baseY);
        column.closePath();
        column.fillPath();
        // Mid amber band
        column.fillStyle(0xffaa44, 0.9);
        column.beginPath();
        column.moveTo(cx - baseW * 0.32, baseY);
        column.lineTo(cx - tipW * 0.32, baseY - colH * 0.85);
        column.lineTo(cx + tipW * 0.32, baseY - colH * 0.85);
        column.lineTo(cx + baseW * 0.32, baseY);
        column.closePath();
        column.fillPath();
        // White-hot inner core
        column.fillStyle(0xffffff, 0.85);
        column.beginPath();
        column.moveTo(cx - baseW * 0.12, baseY);
        column.lineTo(cx - tipW * 0.15, baseY - colH * 0.7);
        column.lineTo(cx + tipW * 0.15, baseY - colH * 0.7);
        column.lineTo(cx + baseW * 0.12, baseY);
        column.closePath();
        column.fillPath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: column,
          alpha: 0,
          duration: 220,
          ease: 'Power2.easeIn',
          onComplete: () => column.destroy(),
        });
      },
    });

    // ---- ACT 2 (140-440ms): hovering ember halo at sprite mid-height ----
    scene.time.delayedCall(140, () => {
      const ring = scene.add.graphics();
      ring.setDepth(9999);
      ring.lineStyle(5, tint, 0.7);
      ring.strokeEllipse(0, 0, h * 0.55, h * 0.18);
      ring.lineStyle(2, 0xffaa44, 0.85);
      ring.strokeEllipse(0, 0, h * 0.55, h * 0.18);
      ring.setPosition(cx, cy);
      ring.setScale(0.3);
      scene.tweens.add({
        targets: ring,
        scaleX: 1.1,
        scaleY: 1.1,
        alpha: 0,
        duration: 280,
        ease: 'Power2.easeOut',
        onComplete: () => ring.destroy(),
      });
    });

    // ---- ACT 3 (160-520ms): rising embers (10 small motes drifting up) ----
    scene.time.delayedCall(160, () => {
      for (let i = 0; i < 10; i++) {
        const ember = scene.add.graphics();
        ember.setDepth(10001);
        const sz = 2 + Math.random() * 2;
        ember.fillStyle(0xffaa44, 0.95);
        ember.fillCircle(0, 0, sz);
        ember.fillStyle(0xffffff, 0.8);
        ember.fillCircle(0, 0, sz * 0.45);
        const offsetX = (Math.random() - 0.5) * h * 0.45;
        ember.setPosition(cx + offsetX, baseY);
        scene.tweens.add({
          targets: ember,
          y: baseY - h * (0.7 + Math.random() * 0.35),
          x: ember.x + (Math.random() - 0.5) * 12,
          alpha: 0,
          duration: 360 + Math.random() * 100,
          delay: i * 22,
          ease: 'Sine.easeOut',
          onComplete: () => ember.destroy(),
        });
      }
    });

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 380);
    scene.cameras.main.shake(120, 0.008);
  },
};

// ---------------------------------------------------------------------------
// Atrophics (dark) — withering inward, decay-violet, ribcage-line cracks
// ---------------------------------------------------------------------------

/** Harrow: the target is bound by an inward-tightening cage of dark filigree, then
 *  crushed into a hollow. Identity beat for Harrow (R15 single-target finisher)
 *  — distinct from oblivion_void (outward starburst) by reading as "cage cinches
 *  shut" instead of "bomb goes off." */
const harrowCurse: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const reach = sprite.displayHeight * 0.6;

    // ---- ACT 1 (0-220ms): 6 curving filigree ribs sweep inward like a closing cage ----
    const ribCount = 6;
    for (let i = 0; i < ribCount; i++) {
      const baseAngle = (Math.PI * 2 * i) / ribCount - Math.PI / 2;
      const rib = scene.add.graphics();
      rib.setDepth(10000);

      // Curve each rib by drawing 3 line segments arcing toward center
      const draw = { t: 0 };
      scene.tweens.add({
        targets: draw,
        t: 1,
        duration: 220,
        delay: i * 14,
        ease: 'Power2.easeIn',
        onUpdate: () => {
          rib.clear();
          const startR = reach * 1.05;
          const endR = reach * 0.15 * (1 - draw.t) + reach * 0.05;
          const segs = 4;
          const pts: Array<{ x: number; y: number }> = [];
          for (let s = 0; s <= segs; s++) {
            const tt = s / segs;
            const r = startR + (endR - startR) * tt;
            // Add curve by tilting each segment perpendicular to the radial
            const a = baseAngle + tt * 0.55 * (i % 2 === 0 ? 1 : -1);
            pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r * 0.7 });
          }
          // Outer violet stroke
          rib.lineStyle(4, tint, 0.9);
          rib.beginPath();
          rib.moveTo(pts[0].x, pts[0].y);
          for (let s = 1; s < pts.length; s++) rib.lineTo(pts[s].x, pts[s].y);
          rib.strokePath();
          // Inner bone-grey highlight
          rib.lineStyle(1.5, 0xddd2bb, 0.95);
          rib.beginPath();
          rib.moveTo(pts[0].x, pts[0].y);
          for (let s = 1; s < pts.length; s++) rib.lineTo(pts[s].x, pts[s].y);
          rib.strokePath();
        },
        onComplete: () => {
          scene.tweens.add({
            targets: rib,
            alpha: 0,
            duration: 160,
            ease: 'Power2.easeIn',
            onComplete: () => rib.destroy(),
          });
        },
      });
      rib.setPosition(cx, cy);
    }

    // ---- ACT 2 (240-440ms): inward implosion flash + dark afterimage ring ----
    scene.time.delayedCall(240, () => {
      const flash = scene.add.graphics();
      flash.setDepth(10002);
      flash.fillStyle(tint, 0.9);
      flash.fillCircle(0, 0, 18);
      flash.fillStyle(0xddd2bb, 0.85);
      flash.fillCircle(0, 0, 8);
      flash.fillStyle(0xffffff, 0.95);
      flash.fillCircle(0, 0, 3);
      flash.setPosition(cx, cy);
      flash.setScale(0.3);
      scene.tweens.add({
        targets: flash,
        scaleX: 1.6,
        scaleY: 1.6,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeOut',
        onComplete: () => flash.destroy(),
      });

      // Dark afterimage ring expanding outward (the curse "exhaling")
      const ring = scene.add.graphics();
      ring.setDepth(10001);
      ring.lineStyle(3, tint, 0.85);
      ring.strokeCircle(0, 0, reach * 0.8);
      ring.setPosition(cx, cy);
      ring.setScale(0.2);
      scene.tweens.add({
        targets: ring,
        scaleX: 1.2,
        scaleY: 0.7,
        alpha: 0,
        duration: 260,
        ease: 'Power2.easeOut',
        onComplete: () => ring.destroy(),
      });
    });

    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 360);
  },
};

// ---------------------------------------------------------------------------
// Verdancy (nature) — bloom motif, opening petals, soft green + accent rose
// ---------------------------------------------------------------------------

/** Bloom: a flower opens above the ally's head, petals spiral outward, motes of
 *  pollen drift up. Capstone heal beat for Bloom (R10 AoE heal) — distinct from
 *  `heal_pulse` (drifting motes) by the structured petal-opening shape that says
 *  "blossom of life." */
const bloomPetals: AnimationPreset = {
  play(scene, sprite, _tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const headY = cy - sprite.displayHeight * 0.3;

    // Soft green wash + heal tint
    flashSpriteTint(scene, sprite, 0x88ff88, 320);

    // ---- ACT 1 (0-280ms): 6 petals open outward from head ----
    const petalCount = 6;
    for (let i = 0; i < petalCount; i++) {
      const angle = -Math.PI / 2 + (Math.PI * 2 * i) / petalCount;
      const petal = scene.add.graphics();
      petal.setDepth(10000);
      // Outer green petal
      petal.fillStyle(0x66dd77, 0.9);
      petal.fillEllipse(0, -10, 9, 18);
      // Inner pale-rose accent
      petal.fillStyle(0xffaacc, 0.65);
      petal.fillEllipse(0, -10, 4, 10);
      petal.setPosition(cx, headY);
      petal.setRotation(angle + Math.PI / 2);
      petal.setScale(0.2);

      scene.tweens.add({
        targets: petal,
        scaleX: 1,
        scaleY: 1,
        duration: 240,
        delay: i * 22,
        ease: 'Back.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: petal,
            alpha: 0,
            scaleX: 1.4,
            scaleY: 1.4,
            duration: 320,
            delay: 80,
            ease: 'Sine.easeOut',
            onComplete: () => petal.destroy(),
          });
        },
      });
    }

    // ---- ACT 2 (140-380ms): central bloom core ----
    scene.time.delayedCall(140, () => {
      const core = scene.add.graphics();
      core.setDepth(10001);
      core.fillStyle(0xffeeaa, 0.95);
      core.fillCircle(0, 0, 6);
      core.fillStyle(0xffffff, 0.9);
      core.fillCircle(0, 0, 3);
      core.setPosition(cx, headY);
      core.setScale(0.3);
      scene.tweens.add({
        targets: core,
        scaleX: 1.4,
        scaleY: 1.4,
        duration: 200,
        ease: 'Power2.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: core,
            alpha: 0,
            scaleX: 1.8,
            scaleY: 1.8,
            duration: 260,
            ease: 'Power2.easeIn',
            onComplete: () => core.destroy(),
          });
        },
      });
    });

    // ---- ACT 3 (200-560ms): pollen motes drift upward ----
    scene.time.delayedCall(200, () => {
      for (let i = 0; i < 8; i++) {
        const mote = scene.add.graphics();
        mote.setDepth(10000);
        mote.fillStyle(0xaaff99, 0.9);
        mote.fillCircle(0, 0, 2);
        mote.fillStyle(0xffffff, 0.8);
        mote.fillCircle(0, 0, 0.9);
        const offsetX = (Math.random() - 0.5) * sprite.displayWidth * 0.6;
        mote.setPosition(cx + offsetX, headY + (Math.random() - 0.5) * 12);
        scene.tweens.add({
          targets: mote,
          y: mote.y - 36 - Math.random() * 22,
          alpha: 0,
          duration: 360 + Math.random() * 120,
          delay: i * 28,
          ease: 'Sine.easeOut',
          onComplete: () => mote.destroy(),
        });
      }
    });

    // Gentle uplift scale pulse — gift, no shake
    const bs = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.05,
      scaleY: bs * 1.05,
      duration: 180,
      delay: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

// ---------------------------------------------------------------------------
// Sigil Collapse — Sorcery Fervor spender (Discharge)
// ---------------------------------------------------------------------------
// Lays down the Sorcery kit motif: arcane sigils (geometric rings + glyph
// runes) + constellation lines. A hexagram-ringed sigil materializes around
// the target, snaps inward to a singular point, then detonates as a 6-node
// constellation network with bright spoke-lines. Distinct from starburst
// (radial 8-pointed star) — this is GEOMETRY collapsing then NETWORKING.
const sigilCollapse: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const baseR = sprite.displayHeight * 0.55;

    // ---- ACT 1 (0-180ms): Outer hexagram sigil materializes + spins inward
    const sigil = scene.add.graphics();
    sigil.setDepth(10000);
    sigil.lineStyle(4, tint, 0.95);
    // Outer hex ring
    sigil.beginPath();
    for (let i = 0; i <= 6; i++) {
      const a = (Math.PI * 2 * i) / 6 - Math.PI / 2;
      const px = Math.cos(a) * baseR;
      const py = Math.sin(a) * baseR;
      if (i === 0) sigil.moveTo(px, py);
      else sigil.lineTo(px, py);
    }
    sigil.strokePath();
    // Inner 6-pointed star (two overlaid triangles) — the hexagram
    sigil.lineStyle(3, 0xffffff, 0.85);
    for (let tri = 0; tri < 2; tri++) {
      sigil.beginPath();
      for (let i = 0; i <= 3; i++) {
        const a = (Math.PI * 2 * i) / 3 - Math.PI / 2 + tri * Math.PI;
        const px = Math.cos(a) * baseR * 0.78;
        const py = Math.sin(a) * baseR * 0.78;
        if (i === 0) sigil.moveTo(px, py);
        else sigil.lineTo(px, py);
      }
      sigil.strokePath();
    }
    // Tiny glyph notches around the ring (4 small ticks)
    sigil.lineStyle(2, tint, 0.9);
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      const ix = Math.cos(a) * baseR * 1.05;
      const iy = Math.sin(a) * baseR * 1.05;
      const ox = Math.cos(a) * baseR * 1.18;
      const oy = Math.sin(a) * baseR * 1.18;
      sigil.beginPath();
      sigil.moveTo(ix, iy);
      sigil.lineTo(ox, oy);
      sigil.strokePath();
    }
    sigil.setPosition(cx, cy);
    sigil.setScale(1.4);
    sigil.setAlpha(0);

    // Materialize fast, then collapse inward with counter-rotation
    scene.tweens.add({
      targets: sigil,
      alpha: 1,
      scaleX: 1.0,
      scaleY: 1.0,
      rotation: -Math.PI * 0.35,
      duration: 170,
      ease: 'Power2.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: sigil,
          scaleX: 0.05,
          scaleY: 0.05,
          rotation: -Math.PI * 0.9,
          alpha: 0,
          duration: 120,
          ease: 'Power3.easeIn',
          onComplete: () => sigil.destroy(),
        });
      },
    });

    // ---- ACT 2 (290ms): Singularity flash at collapse point
    scene.time.delayedCall(290, () => {
      const core = scene.add.graphics();
      core.setDepth(10001);
      core.fillStyle(0xffffff, 1);
      core.fillCircle(0, 0, 8);
      core.fillStyle(tint, 0.85);
      core.fillCircle(0, 0, 14);
      core.setPosition(cx, cy);
      core.setScale(0.2);
      scene.tweens.add({
        targets: core,
        scaleX: 1.6,
        scaleY: 1.6,
        alpha: 0,
        duration: 180,
        ease: 'Power3.easeOut',
        onComplete: () => core.destroy(),
      });

      // ---- ACT 3 (290-520ms): Constellation explosion — 6 nodes + spoke lines
      const nodeCount = 6;
      const nodeR = baseR * 0.95;
      const nodes: { x: number; y: number; g: Phaser.GameObjects.Graphics }[] = [];
      for (let i = 0; i < nodeCount; i++) {
        const a = (Math.PI * 2 * i) / nodeCount - Math.PI / 2 + 0.3;
        const nx = Math.cos(a) * nodeR;
        const ny = Math.sin(a) * nodeR;
        // Spoke line from center to node
        const spoke = scene.add.graphics();
        spoke.setDepth(10000);
        spoke.lineStyle(3, tint, 0.95);
        spoke.beginPath();
        spoke.moveTo(0, 0);
        spoke.lineTo(nx, ny);
        spoke.strokePath();
        // White-hot inner spoke
        spoke.lineStyle(1.5, 0xffffff, 0.9);
        spoke.beginPath();
        spoke.moveTo(0, 0);
        spoke.lineTo(nx, ny);
        spoke.strokePath();
        spoke.setPosition(cx, cy);
        spoke.setScale(0.1, 1);
        scene.tweens.add({
          targets: spoke,
          scaleX: 1,
          duration: 110,
          ease: 'Power3.easeOut',
          onComplete: () => {
            scene.tweens.add({
              targets: spoke,
              alpha: 0,
              duration: 180,
              ease: 'Sine.easeIn',
              onComplete: () => spoke.destroy(),
            });
          },
        });

        // Diamond node at tip
        const node = scene.add.graphics();
        node.setDepth(10001);
        node.fillStyle(tint, 1);
        node.beginPath();
        node.moveTo(0, -7);
        node.lineTo(7, 0);
        node.lineTo(0, 7);
        node.lineTo(-7, 0);
        node.closePath();
        node.fillPath();
        node.fillStyle(0xffffff, 0.95);
        node.beginPath();
        node.moveTo(0, -3);
        node.lineTo(3, 0);
        node.lineTo(0, 3);
        node.lineTo(-3, 0);
        node.closePath();
        node.fillPath();
        node.setPosition(cx + nx, cy + ny);
        node.setScale(0.2);
        nodes.push({ x: nx, y: ny, g: node });
        scene.tweens.add({
          targets: node,
          scaleX: 1.2,
          scaleY: 1.2,
          duration: 120,
          delay: 80,
          ease: 'Back.easeOut',
          onComplete: () => {
            scene.tweens.add({
              targets: node,
              alpha: 0,
              scaleX: 0.4,
              scaleY: 0.4,
              duration: 200,
              ease: 'Sine.easeIn',
              onComplete: () => node.destroy(),
            });
          },
        });
      }

      // Inter-node constellation links (every other node) — the network
      scene.time.delayedCall(150, () => {
        const links = scene.add.graphics();
        links.setDepth(10000);
        links.lineStyle(2, tint, 0.7);
        for (let i = 0; i < nodeCount; i++) {
          const a = nodes[i];
          const b = nodes[(i + 2) % nodeCount];
          links.beginPath();
          links.moveTo(a.x, a.y);
          links.lineTo(b.x, b.y);
          links.strokePath();
        }
        links.setPosition(cx, cy);
        links.setAlpha(0);
        scene.tweens.add({
          targets: links,
          alpha: 1,
          duration: 80,
          ease: 'Power2.easeOut',
          onComplete: () => {
            scene.tweens.add({
              targets: links,
              alpha: 0,
              duration: 180,
              ease: 'Sine.easeIn',
              onComplete: () => links.destroy(),
            });
          },
        });
      });
    });

    // ---- Camera + sprite reactions
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.time.delayedCall(290, () => scene.cameras.main.flash(110, r, g, b, false));
    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 240);
  },
};

/** Astral Brand — Conjuration trap-plant cast. Etches a Weave-pattern onto the target:
 *  outer ring with tick marks, inner counter-rotating ring, 6-pointed star at center.
 *  ~600ms total. Lingers briefly, pulses once, fades. NO shake — this is a marker, not
 *  impact (the detonation is sigil_collapse). Distinct from holy_brand (cross + sparks);
 *  the Weave-ring + hexagram motif is the astral language. */
const astralBrand: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const baseR = sprite.displayHeight * 0.32;

    // Phase 1 (0-200ms): Outer Weave-ring with 6 tick marks draws on, rotates clockwise.
    const outerRing = scene.add.graphics();
    outerRing.setDepth(10000);
    outerRing.lineStyle(3, tint, 0.9);
    outerRing.strokeCircle(0, 0, baseR);
    outerRing.lineStyle(2, tint, 0.85);
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      const ix = Math.cos(a) * baseR * 1.0;
      const iy = Math.sin(a) * baseR * 1.0;
      const ox = Math.cos(a) * baseR * 1.18;
      const oy = Math.sin(a) * baseR * 1.18;
      outerRing.beginPath();
      outerRing.moveTo(ix, iy);
      outerRing.lineTo(ox, oy);
      outerRing.strokePath();
    }
    outerRing.setPosition(cx, cy);
    outerRing.setScale(0.4);
    outerRing.setAlpha(0);
    scene.tweens.add({
      targets: outerRing,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      rotation: Math.PI * 0.5,
      duration: 200,
      ease: 'Power3.easeOut',
    });

    // Phase 2 (120-300ms): Inner ring + 6-pointed star (hexagram) counter-rotates in.
    scene.time.delayedCall(120, () => {
      const innerSigil = scene.add.graphics();
      innerSigil.setDepth(10001);
      innerSigil.lineStyle(2, tint, 0.95);
      innerSigil.strokeCircle(0, 0, baseR * 0.55);
      innerSigil.lineStyle(2, 0xffffff, 0.9);
      for (let tri = 0; tri < 2; tri++) {
        innerSigil.beginPath();
        for (let i = 0; i <= 3; i++) {
          const a = (Math.PI * 2 * i) / 3 - Math.PI / 2 + tri * Math.PI;
          const px = Math.cos(a) * baseR * 0.45;
          const py = Math.sin(a) * baseR * 0.45;
          if (i === 0) innerSigil.moveTo(px, py);
          else innerSigil.lineTo(px, py);
        }
        innerSigil.strokePath();
      }
      innerSigil.setPosition(cx, cy);
      innerSigil.setScale(0.3);
      innerSigil.setAlpha(0);
      scene.tweens.add({
        targets: innerSigil,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        rotation: -Math.PI * 0.4,
        duration: 180,
        ease: 'Power2.easeOut',
      });

      // Phase 3 (340-600ms): pulse once, fade both layers out.
      scene.time.delayedCall(220, () => {
        scene.tweens.add({
          targets: [outerRing, innerSigil],
          scaleX: 1.15,
          scaleY: 1.15,
          duration: 90,
          yoyo: true,
          ease: 'Sine.easeInOut',
        });
        scene.tweens.add({
          targets: [outerRing, innerSigil],
          alpha: 0,
          duration: 200,
          delay: 180,
          ease: 'Power2.easeIn',
          onComplete: () => {
            outerRing.destroy();
            innerSigil.destroy();
          },
        });
      });
    });

    flashSpriteTint(scene, sprite, tint, 200);
  },
};

// ---------------------------------------------------------------------------
// Verdant Surge — Verdancy Fervor spender (Overgrowth, AOE)
// ---------------------------------------------------------------------------
// Harmonizes with bloom_petals: same petal-and-pollen language but as a
// HOSTILE all-enemies surge instead of a friendly bloom. Heavy roots erupt
// from the ground, then a volley of curling petals (red-edged, like the
// crimson-flowered Anthura motif) snap open across the target with a pollen
// burst. Distinct from ground_eruption (just spikes) and from bloom_petals
// (gentle uplift with no shake) — this is bloom-language weaponized.
const verdantSurge: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const groundY = sprite.y;
    const w = sprite.displayWidth;
    const h = sprite.displayHeight;

    flashSpriteTint(scene, sprite, tint, 280);

    // ---- ACT 1 (0-200ms): 4 thick roots erupt from the ground, twisting up
    const rootCount = 4;
    for (let i = 0; i < rootCount; i++) {
      const root = scene.add.graphics();
      root.setDepth(9999);
      const offsetX = (i - (rootCount - 1) / 2) * (w * 0.32);
      const reach = h * (0.45 + Math.random() * 0.2);
      // Tapered curling root: dark-green outline + lighter core
      root.fillStyle(0x224422, 0.95);
      root.beginPath();
      root.moveTo(-9, 0);
      root.lineTo(-3, -reach * 0.5);
      root.lineTo(0, -reach);
      root.lineTo(3, -reach * 0.5);
      root.lineTo(9, 0);
      root.closePath();
      root.fillPath();
      // Inner highlight using tint
      root.fillStyle(tint, 0.85);
      root.beginPath();
      root.moveTo(-4, 0);
      root.lineTo(-1, -reach * 0.5);
      root.lineTo(0, -reach);
      root.lineTo(1, -reach * 0.5);
      root.lineTo(4, 0);
      root.closePath();
      root.fillPath();
      root.setPosition(cx + offsetX, groundY);
      root.setScale(1, 0);
      root.setRotation((Math.random() - 0.5) * 0.3);

      scene.tweens.add({
        targets: root,
        scaleY: 1,
        duration: 180,
        delay: i * 30,
        ease: 'Back.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: root,
            alpha: 0,
            scaleY: 1.1,
            duration: 220,
            delay: 100,
            ease: 'Sine.easeIn',
            onComplete: () => root.destroy(),
          });
        },
      });
    }

    // ---- ACT 2 (180-440ms): 8 hostile petals snap open across the target
    scene.time.delayedCall(180, () => {
      const petalCount = 8;
      for (let i = 0; i < petalCount; i++) {
        const angle = (Math.PI * 2 * i) / petalCount;
        const petal = scene.add.graphics();
        petal.setDepth(10001);
        // Red-edged outer petal (hostile bloom — red flowers, not green)
        petal.fillStyle(0xaa3344, 0.92);
        petal.fillEllipse(0, -16, 11, 22);
        // Green inner — keeps verdancy DNA
        petal.fillStyle(tint, 0.85);
        petal.fillEllipse(0, -14, 6, 14);
        // White-hot rachis along the spine
        petal.fillStyle(0xffeebb, 0.9);
        petal.fillRect(-1, -22, 2, 14);
        petal.setPosition(cx, cy);
        petal.setRotation(angle + Math.PI / 2);
        petal.setScale(0.15);

        scene.tweens.add({
          targets: petal,
          scaleX: 1.1,
          scaleY: 1.1,
          duration: 160,
          delay: i * 18,
          ease: 'Back.easeOut',
          onComplete: () => {
            scene.tweens.add({
              targets: petal,
              alpha: 0,
              scaleX: 1.5,
              scaleY: 1.5,
              duration: 220,
              ease: 'Sine.easeOut',
              onComplete: () => petal.destroy(),
            });
          },
        });
      }
    });

    // ---- ACT 3 (300-560ms): Pollen burst — drifts up + scatters outward
    scene.time.delayedCall(300, () => {
      for (let i = 0; i < 10; i++) {
        const mote = scene.add.graphics();
        mote.setDepth(10000);
        mote.fillStyle(0xddff99, 0.9);
        mote.fillCircle(0, 0, 2.5);
        mote.fillStyle(0xffffff, 0.85);
        mote.fillCircle(0, 0, 1);
        const ox = (Math.random() - 0.5) * w * 0.7;
        const oy = (Math.random() - 0.5) * h * 0.4;
        mote.setPosition(cx + ox, cy + oy);
        scene.tweens.add({
          targets: mote,
          x: mote.x + (Math.random() - 0.5) * 32,
          y: mote.y - 30 - Math.random() * 28,
          alpha: 0,
          duration: 320 + Math.random() * 140,
          delay: i * 22,
          ease: 'Sine.easeOut',
          onComplete: () => mote.destroy(),
        });
      }
    });

    shakeSprite(scene, sprite, 7);
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(80, r, g, b, false);
  },
};

// ---------------------------------------------------------------------------
// Prowess (Duelist) — finesse blade family
// ---------------------------------------------------------------------------

/** Displace: sharp percussive horizontal blade-tap that throws the target off-rhythm.
 *  Quick horizontal flick line + small expanding ring + heavier shake (stagger feel).
 *  Distinct from `slash` (diagonal, sweeping) and `stab` (extending point thrust). */
const displaceStrike: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.45;

    // Sharp horizontal flick line — left-to-right, snappier than slash
    const gfx = scene.add.graphics();
    gfx.setDepth(10000);
    const draw = { progress: 0 };
    scene.tweens.add({
      targets: draw,
      progress: 1,
      duration: 70,
      ease: 'Power3.easeOut',
      onUpdate: () => {
        gfx.clear();
        const headX = cx - len * 0.5 + len * draw.progress;
        // Outer line
        gfx.lineStyle(5, tint, 1);
        gfx.beginPath();
        gfx.moveTo(cx - len * 0.5, cy);
        gfx.lineTo(headX, cy);
        gfx.strokePath();
        // White inner core for crisp readability
        gfx.lineStyle(2, 0xffffff, 0.95);
        gfx.beginPath();
        gfx.moveTo(cx - len * 0.5, cy);
        gfx.lineTo(headX, cy);
        gfx.strokePath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 90,
          onComplete: () => gfx.destroy(),
        });
      },
    });

    // Expanding ring shockwave — sells the percussive impact
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    const ringData = { radius: 4, alpha: 0.85 };
    scene.tweens.add({
      targets: ringData,
      radius: sprite.displayHeight * 0.32,
      alpha: 0,
      duration: 240,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(3, tint, ringData.alpha);
        ring.strokeCircle(cx, cy, ringData.radius);
      },
      onComplete: () => ring.destroy(),
    });

    shakeSprite(scene, sprite, 7);
    flashSpriteTint(scene, sprite, tint, 130);
  },
};

/** Counter Flash — quick reactive blade flash directed BACK at the attacker.
 *  Reads as "you got countered" — distinct from any normal player attack.
 *  Quick reverse arc + tempo color flash + light shake. */
const counterFlash: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.5;

    // Reverse-direction arc — right-to-left + slight upward curve, signals "counter"
    const gfx = scene.add.graphics();
    gfx.setDepth(10001);
    const arc = { progress: 0 };
    scene.tweens.add({
      targets: arc,
      progress: 1,
      duration: 90,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        gfx.clear();
        const segments = 12;
        gfx.lineStyle(4, tint, 1);
        gfx.beginPath();
        for (let i = 0; i <= segments * arc.progress; i++) {
          const t = i / segments;
          // Right-to-left arc that curves up at the end
          const x = cx + len * 0.5 - len * t;
          const y = cy + Math.sin(t * Math.PI) * (-len * 0.25);
          if (i === 0) gfx.moveTo(x, y); else gfx.lineTo(x, y);
        }
        gfx.strokePath();
        // White inner core
        gfx.lineStyle(2, 0xffffff, 0.9);
        gfx.beginPath();
        for (let i = 0; i <= segments * arc.progress; i++) {
          const t = i / segments;
          const x = cx + len * 0.5 - len * t;
          const y = cy + Math.sin(t * Math.PI) * (-len * 0.25);
          if (i === 0) gfx.moveTo(x, y); else gfx.lineTo(x, y);
        }
        gfx.strokePath();
      },
      onComplete: () => {
        scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 110,
          onComplete: () => gfx.destroy(),
        });
      },
    });

    // Tempo-flash glint at the strike point — small bright pulse
    const glint = scene.add.graphics();
    glint.setDepth(10002);
    glint.fillStyle(0xffffff, 1);
    glint.fillCircle(cx, cy - len * 0.05, 4);
    glint.fillStyle(tint, 0.8);
    glint.fillCircle(cx, cy - len * 0.05, 8);
    scene.tweens.add({
      targets: glint,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 200,
      ease: 'Power2.easeOut',
      onComplete: () => glint.destroy(),
    });

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 110);
  },
};

/** Bladework Combo — multi-hit precision blade sequence. 5 quick slash lines at
 *  varying angles, fade in/out fast. Distinct from `rapid_strikes` (which uses
 *  impact circles for brute fists). This is bladework — thin sharp lines. */
const bladeworkCombo: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.4;

    // 5 slash lines at varying angles, staggered. Each is a thin precise cut.
    const slashAngles = [
      -Math.PI * 0.25, // ↘
      Math.PI * 0.2,   // ↗
      Math.PI * 0.5,   // ↓
      -Math.PI * 0.1,  // ↘ shallow
      Math.PI * 0.35,  // ↗ steep
    ];
    slashAngles.forEach((angle, i) => {
      scene.time.delayedCall(i * 55, () => {
        const dx = Math.cos(angle) * len * 0.45;
        const dy = Math.sin(angle) * len * 0.45;
        const offsetX = (Math.random() - 0.5) * len * 0.2;
        const offsetY = (Math.random() - 0.5) * len * 0.2;
        const x1 = cx + offsetX - dx;
        const y1 = cy + offsetY - dy;
        const x2 = cx + offsetX + dx;
        const y2 = cy + offsetY + dy;

        const gfx = scene.add.graphics();
        gfx.setDepth(10000);
        // Outer tinted blade line
        gfx.lineStyle(3, tint, 1);
        gfx.beginPath();
        gfx.moveTo(x1, y1);
        gfx.lineTo(x2, y2);
        gfx.strokePath();
        // White inner core
        gfx.lineStyle(1.5, 0xffffff, 0.9);
        gfx.beginPath();
        gfx.moveTo(x1, y1);
        gfx.lineTo(x2, y2);
        gfx.strokePath();

        scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 130,
          ease: 'Power2.easeOut',
          onComplete: () => gfx.destroy(),
        });

        shakeSprite(scene, sprite, 3);
      });
    });

    flashSpriteTint(scene, sprite, tint, 220);
  },
};

// ---------------------------------------------------------------------------
// Harborfiend kit (Ch 11) — three bespoke water presets
// ---------------------------------------------------------------------------

/** Watery tendrils rise from the sprite's feet, curl inward, and yank the target
 *  downward with a knee-buckle compression. Concentric ripple at the base reads
 *  as "the water grabbed me." Use for grab/drag/drown abilities. */
const drownPull: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const footY = sprite.y;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    // Sprite knee-buckle: brief downward squash + recovery
    const baseScale = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleY: baseScale * 0.92,
      y: footY + 4,
      duration: 110,
      ease: 'Power2.easeIn',
      yoyo: true,
      onComplete: () => {
        sprite.setScale(baseScale);
        sprite.y = footY;
      },
    });

    // 4 watery tendrils rising from foot edges, curling toward center
    const tendrilCount = 4;
    for (let i = 0; i < tendrilCount; i++) {
      const t = i / (tendrilCount - 1);
      const startX = cx + (t - 0.5) * w * 0.7;
      const peakY = cy - h * 0.1;
      const tendril = scene.add.graphics();
      tendril.setDepth(10000);

      const progress = { p: 0 };
      scene.tweens.add({
        targets: progress,
        p: 1,
        duration: 280,
        delay: i * 30,
        ease: 'Sine.easeOut',
        onUpdate: () => {
          tendril.clear();
          // Curving tendril from foot up to center, with curl toward middle
          const segs = 10;
          tendril.lineStyle(4, tint, 1 - progress.p * 0.4);
          tendril.beginPath();
          for (let s = 0; s <= segs * progress.p; s++) {
            const sp = s / segs;
            const x = startX + (cx - startX) * sp * sp;
            const y = footY - (footY - peakY) * sp;
            if (s === 0) tendril.moveTo(x, y);
            else tendril.lineTo(x, y);
          }
          tendril.strokePath();

          // White inner core
          tendril.lineStyle(2, 0xe6f7ff, (1 - progress.p) * 0.8);
          tendril.beginPath();
          for (let s = 0; s <= segs * progress.p; s++) {
            const sp = s / segs;
            const x = startX + (cx - startX) * sp * sp;
            const y = footY - (footY - peakY) * sp;
            if (s === 0) tendril.moveTo(x, y);
            else tendril.lineTo(x, y);
          }
          tendril.strokePath();
        },
        onComplete: () => {
          scene.tweens.add({
            targets: tendril,
            alpha: 0,
            duration: 120,
            onComplete: () => tendril.destroy(),
          });
        },
      });
    }

    // Concentric ripple rings at the feet (the pull origin)
    for (let r = 0; r < 3; r++) {
      const ripple = scene.add.graphics();
      ripple.setDepth(10001);
      ripple.lineStyle(3, tint, 0.85);
      ripple.strokeEllipse(0, 0, w * 0.55, h * 0.13);
      ripple.setPosition(cx, footY - 3);
      scene.tweens.add({
        targets: ripple,
        scaleX: 1.8 + r * 0.3,
        scaleY: 1.8 + r * 0.3,
        alpha: 0,
        duration: 380,
        delay: r * 60,
        ease: 'Sine.easeOut',
        onComplete: () => ripple.destroy(),
      });
    }

    shakeSprite(scene, sprite, 4);
    flashSpriteTint(scene, sprite, tint, 180);
  },
};

/** Horizontal wave sweep with foam crests rolling across the target. Sprite gets
 *  a sideways shove as the water passes through. AoE-friendly: visually reads as
 *  "a wave is rolling over me right now." */
const tidalSweep: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;
    const sweepReach = w * 1.6;

    // Main wave shape — a wavy horizontal band that sweeps L→R across the target
    const wave = scene.add.graphics();
    wave.setDepth(10000);
    const waveState = { x: cx - sweepReach * 0.5, alpha: 1 };

    function renderWave(): void {
      wave.clear();
      // Outer wave (tinted, translucent)
      wave.fillStyle(tint, 0.55 * waveState.alpha);
      wave.beginPath();
      const segs = 16;
      const bandH = h * 0.55;
      const xStart = waveState.x - w * 0.45;
      const xEnd = waveState.x + w * 0.45;
      // Top edge (wavy)
      for (let s = 0; s <= segs; s++) {
        const t = s / segs;
        const px = xStart + (xEnd - xStart) * t;
        const py = cy - bandH * 0.5 + Math.sin(t * Math.PI * 3) * h * 0.06;
        if (s === 0) wave.moveTo(px, py);
        else wave.lineTo(px, py);
      }
      // Bottom edge (wavy, offset)
      for (let s = segs; s >= 0; s--) {
        const t = s / segs;
        const px = xStart + (xEnd - xStart) * t;
        const py = cy + bandH * 0.5 + Math.sin(t * Math.PI * 3 + 1.2) * h * 0.06;
        wave.lineTo(px, py);
      }
      wave.closePath();
      wave.fillPath();

      // Inner foam crests (white-cyan)
      wave.fillStyle(0xe6f7ff, 0.7 * waveState.alpha);
      for (let s = 0; s < 4; s++) {
        const t = (s + 0.5) / 4;
        const px = xStart + (xEnd - xStart) * t;
        const py = cy - bandH * 0.45 + Math.sin(t * Math.PI * 3) * h * 0.06;
        wave.fillTriangle(px - 6, py + 8, px + 6, py + 8, px, py - 6);
      }
    }

    scene.tweens.add({
      targets: waveState,
      x: cx + sweepReach * 0.5,
      duration: 380,
      ease: 'Power2.easeIn',
      onUpdate: renderWave,
      onComplete: () => {
        scene.tweens.add({
          targets: waveState,
          alpha: 0,
          duration: 120,
          onUpdate: renderWave,
          onComplete: () => wave.destroy(),
        });
      },
    });

    // Foam droplet trail
    for (let i = 0; i < 8; i++) {
      const drop = scene.add.graphics();
      drop.setDepth(10001);
      drop.fillStyle(0xffffff, 0.85);
      drop.fillCircle(0, 0, 2 + Math.random() * 1.5);
      drop.setPosition(cx - sweepReach * 0.4, cy + (Math.random() - 0.5) * h * 0.5);
      scene.tweens.add({
        targets: drop,
        x: cx + sweepReach * 0.5 + Math.random() * 30,
        y: drop.y + 10 + Math.random() * 20,
        alpha: 0,
        duration: 420,
        delay: 80 + i * 30,
        ease: 'Power2.easeIn',
        onComplete: () => drop.destroy(),
      });
    }

    // Sprite gets shoved sideways by the wave
    const origX = sprite.x;
    scene.tweens.add({
      targets: sprite,
      x: origX + 6,
      duration: 100,
      delay: 150,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => { sprite.x = origX; },
    });

    scene.cameras.main.shake(180, 0.008);
    flashSpriteTint(scene, sprite, tint, 220);
  },
};

/** Abyssal water column slams down from far above with a compression burst at
 *  the feet, double ripple ring, and 12-droplet fan. The boss climax — vertical
 *  water hammer. Heavier and slower than water_crash. */
const abyssalCollapse: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const footY = sprite.y;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    // Camera flash in tint — heavy
    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(220, r, g, b, false);

    // Tall outer water column (taller than water_crash — abyssal scale)
    const columnH = h * 2.4;
    const columnW = w * 0.6;
    const columnCY = footY - columnH / 2;
    const outer = scene.add.rectangle(cx, columnCY, columnW, columnH, tint, 0.65);
    outer.setDepth(9999);
    const inner = scene.add.rectangle(cx, columnCY, columnW * 0.32, columnH, 0xe6f7ff, 0.85);
    inner.setDepth(10000);
    outer.setScale(0, 1);
    inner.setScale(0, 1);

    scene.tweens.add({
      targets: [outer, inner],
      scaleX: 1,
      duration: 130,
      ease: 'Power3.easeOut',
    });
    scene.tweens.add({
      targets: outer,
      alpha: 0,
      scaleX: 1.9,
      duration: 420,
      delay: 150,
      ease: 'Power2.easeIn',
      onComplete: () => outer.destroy(),
    });
    scene.tweens.add({
      targets: inner,
      alpha: 0,
      duration: 320,
      delay: 140,
      ease: 'Power2.easeIn',
      onComplete: () => inner.destroy(),
    });

    // Sprite squash on impact (compression from above)
    const baseScale = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleY: baseScale * 0.85,
      scaleX: baseScale * 1.1,
      duration: 140,
      delay: 100,
      ease: 'Power2.easeIn',
      yoyo: true,
      onComplete: () => sprite.setScale(baseScale),
    });

    // Double ripple ring burst at feet — first ring large, second ring echoes
    for (let ring = 0; ring < 2; ring++) {
      const splash = scene.add.graphics();
      splash.setDepth(10001);
      splash.lineStyle(5, tint, 0.95);
      splash.strokeEllipse(0, 0, w * 0.7, h * 0.18);
      splash.setPosition(cx, footY - 4);
      scene.tweens.add({
        targets: splash,
        scaleX: 3.0 - ring * 0.4,
        scaleY: 3.0 - ring * 0.4,
        alpha: 0,
        duration: 540,
        delay: 130 + ring * 90,
        ease: 'Power2.easeOut',
        onComplete: () => splash.destroy(),
      });
    }

    // 12-droplet explosive fan (bigger than devastating_impact's 6)
    for (let i = 0; i < 12; i++) {
      const angle = -Math.PI * (0.05 + 0.9 * (i / 11));
      const dist = h * (0.5 + Math.random() * 0.45);
      const radius = 3 + Math.random() * 2.5;

      const drop = scene.add.graphics();
      drop.setDepth(10002);
      drop.fillStyle(tint, 0.95);
      drop.fillCircle(0, 0, radius);
      drop.fillStyle(0xffffff, 0.7);
      drop.fillCircle(-radius * 0.3, -radius * 0.3, radius * 0.4);
      drop.setPosition(cx, cy);

      scene.tweens.add({
        targets: drop,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 520,
        delay: 130 + i * 14,
        ease: 'Power2.easeOut',
        onComplete: () => drop.destroy(),
      });
    }

    // Hit sprite + heavy shake at the impact moment
    scene.time.delayedCall(120, () => {
      showHitEffect(scene, cx, cy, tint);
    });

    shakeSprite(scene, sprite, 14);
    scene.cameras.main.shake(280, 0.024);
    flashSpriteTint(scene, sprite, tint, 280);
  },
};

// ---------------------------------------------------------------------------
// Tidemancy — chain-bouncer / heal-on-strike water family
// ---------------------------------------------------------------------------

/** Crestfall — a tall cresting wave rises and breaks down across the target with
 *  three bright foam chain-arcs spraying outward. Each chain hop fires this preset
 *  separately; the per-hit story is wave-rises, wave-breaks, foam-ricochets. The
 *  chain-arc spray is the chain-identity read at small scale. Stronger and more
 *  vertical than tidal_sweep (which moves L→R), more aggressive than water_crash. */
const waveCrest: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    const wave = scene.add.graphics();
    wave.setDepth(10000);
    const waveState = { progress: 0 };
    const waveH = h * 0.85;
    const baseY = cy + h * 0.15;

    function renderWave(): void {
      wave.clear();
      const p = waveState.progress;
      wave.fillStyle(tint, 0.85);
      wave.beginPath();
      const segs = 18;
      const xStart = cx - w * 0.55;
      const xEnd = cx + w * 0.55;
      for (let s = 0; s <= segs; s++) {
        const t = s / segs;
        const px = xStart + (xEnd - xStart) * t;
        const crestY = baseY - waveH * (Math.sin(t * Math.PI * 0.85) * p);
        if (s === 0) wave.moveTo(px, baseY);
        else wave.lineTo(px, crestY);
      }
      wave.lineTo(xEnd, baseY);
      wave.closePath();
      wave.fillPath();
      wave.lineStyle(5, 0xe6f7ff, 0.95);
      wave.beginPath();
      for (let s = 0; s <= segs; s++) {
        const t = s / segs;
        const px = xStart + (xEnd - xStart) * t;
        const crestY = baseY - waveH * (Math.sin(t * Math.PI * 0.85) * p);
        if (s === 0) wave.moveTo(px, crestY);
        else wave.lineTo(px, crestY);
      }
      wave.strokePath();
    }

    scene.tweens.add({
      targets: waveState,
      progress: 1,
      duration: 180,
      ease: 'Power2.easeOut',
      onUpdate: renderWave,
    });

    scene.time.delayedCall(180, () => {
      scene.tweens.add({
        targets: wave,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeIn',
        onComplete: () => wave.destroy(),
      });

      const arcAngles = [-Math.PI * 0.75, -Math.PI * 0.5, -Math.PI * 0.25];
      for (let i = 0; i < arcAngles.length; i++) {
        const angle = arcAngles[i];
        const arc = scene.add.graphics();
        arc.setDepth(10001);
        const reach = h * (0.55 + Math.random() * 0.15);
        const arcEndX = cx + Math.cos(angle) * reach;
        const arcEndY = cy + Math.sin(angle) * reach;
        const drawProg = { p: 0 };

        scene.tweens.add({
          targets: drawProg,
          p: 1,
          duration: 200,
          delay: i * 28,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            arc.clear();
            arc.lineStyle(5, tint, 0.95);
            arc.beginPath();
            arc.moveTo(cx, cy);
            const segs = 4;
            for (let s = 1; s <= segs; s++) {
              const t = (s / segs) * drawProg.p;
              if (t > 1) break;
              const baseX = cx + (arcEndX - cx) * t;
              const baseY2 = cy + (arcEndY - cy) * t;
              const jitter = (s % 2 === 0 ? 1 : -1) * 8 * (1 - drawProg.p * 0.3);
              const perpX = Math.cos(angle + Math.PI / 2) * jitter;
              const perpY = Math.sin(angle + Math.PI / 2) * jitter;
              arc.lineTo(baseX + perpX, baseY2 + perpY);
            }
            arc.strokePath();
            arc.lineStyle(2, 0xe6f7ff, drawProg.p);
            arc.strokePath();
          },
          onComplete: () => {
            const splash = scene.add.graphics();
            splash.setDepth(10002);
            splash.fillStyle(0xe6f7ff, 0.95);
            for (let d = 0; d < 4; d++) {
              const dAng = angle + (Math.random() - 0.5) * 0.8;
              const dDist = 6 + Math.random() * 4;
              splash.fillCircle(Math.cos(dAng) * dDist, Math.sin(dAng) * dDist, 3 + Math.random() * 1.5);
            }
            splash.setPosition(arcEndX, arcEndY);
            scene.tweens.add({
              targets: splash,
              alpha: 0,
              scaleX: 1.6,
              scaleY: 1.6,
              duration: 160,
              ease: 'Sine.easeOut',
              onComplete: () => splash.destroy(),
            });
            scene.tweens.add({
              targets: arc,
              alpha: 0,
              duration: 160,
              onComplete: () => arc.destroy(),
            });
          },
        });
      }

      const burstFoam = scene.add.graphics();
      burstFoam.setDepth(10001);
      for (let f = 0; f < 8; f++) {
        const fAng = (Math.PI * 2 * f) / 8;
        burstFoam.fillStyle(0xe6f7ff, 0.92);
        burstFoam.fillCircle(Math.cos(fAng) * 14, Math.sin(fAng) * 14, 4);
      }
      burstFoam.setPosition(cx, cy);
      burstFoam.setScale(0.4);
      scene.tweens.add({
        targets: burstFoam,
        scaleX: 1.6,
        scaleY: 1.6,
        alpha: 0,
        duration: 280,
        ease: 'Power2.easeOut',
        onComplete: () => burstFoam.destroy(),
      });
    });

    const origX = sprite.x;
    scene.time.delayedCall(170, () => {
      scene.tweens.add({
        targets: sprite,
        x: origX + 5,
        duration: 80,
        yoyo: true,
        ease: 'Sine.easeInOut',
        onComplete: () => { sprite.x = origX; },
      });
    });
    scene.cameras.main.shake(160, 0.01);
    flashSpriteTint(scene, sprite, tint, 260);
  },
};

/** Tidemend — a luminous water ribbon descends from upper-back, pours into the
 *  ally's chest, and bursts as a soft cyan-mint heal pulse. Engine fires this on
 *  primary heal AND on each chain-bounce hop. Different from heal_pulse (generic
 *  floaty particles) and bloom_petals (verdancy floral). The descending ribbon
 *  is the read. */
const tideMend: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    const ribbon = scene.add.graphics();
    ribbon.setDepth(9999);
    const ribState = { progress: 0 };
    const startX = cx + w * 0.7;
    const startY = cy - h * 0.9;
    const endX = cx;
    const endY = cy;

    function renderRibbon(): void {
      ribbon.clear();
      const p = ribState.progress;
      const segs = 14;
      ribbon.lineStyle(7, tint, 0.85);
      ribbon.beginPath();
      for (let s = 0; s <= segs; s++) {
        const t = (s / segs) * p;
        const ctrlX = cx + w * 0.25;
        const ctrlY = cy - h * 0.45;
        const px = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * ctrlX + t * t * endX;
        const py = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * ctrlY + t * t * endY;
        if (s === 0) ribbon.moveTo(px, py);
        else ribbon.lineTo(px, py);
      }
      ribbon.strokePath();
      ribbon.lineStyle(3, 0xddffee, 0.95);
      ribbon.beginPath();
      for (let s = 0; s <= segs; s++) {
        const t = (s / segs) * p;
        const ctrlX = cx + w * 0.25;
        const ctrlY = cy - h * 0.45;
        const px = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * ctrlX + t * t * endX;
        const py = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * ctrlY + t * t * endY;
        if (s === 0) ribbon.moveTo(px, py);
        else ribbon.lineTo(px, py);
      }
      ribbon.strokePath();
    }

    scene.tweens.add({
      targets: ribState,
      progress: 1,
      duration: 220,
      ease: 'Power2.easeIn',
      onUpdate: renderRibbon,
      onComplete: () => {
        scene.tweens.add({
          targets: ribbon,
          alpha: 0,
          duration: 200,
          ease: 'Sine.easeIn',
          onComplete: () => ribbon.destroy(),
        });
      },
    });

    scene.time.delayedCall(200, () => {
      for (let i = 0; i < 8; i++) {
        const splash = scene.add.graphics();
        splash.setDepth(10001);
        splash.fillStyle(0xddffee, 0.95);
        splash.fillCircle(0, 0, 4);
        splash.fillStyle(0xffffff, 0.85);
        splash.fillCircle(0, 0, 2);
        splash.setPosition(cx, cy);
        const angle = (Math.PI * 2 * i) / 8 - Math.PI / 2;
        const dist = h * 0.25;
        scene.tweens.add({
          targets: splash,
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist,
          alpha: 0,
          scaleX: 1.4,
          scaleY: 1.4,
          duration: 320,
          ease: 'Sine.easeOut',
          onComplete: () => splash.destroy(),
        });
      }
      const glow = scene.add.graphics();
      glow.setDepth(10000);
      glow.fillStyle(0xddffee, 0.7);
      glow.fillCircle(0, 0, h * 0.18);
      glow.fillStyle(0xffffff, 0.85);
      glow.fillCircle(0, 0, h * 0.08);
      glow.setPosition(cx, cy);
      glow.setScale(0.4);
      scene.tweens.add({
        targets: glow,
        scaleX: 1.4,
        scaleY: 1.4,
        alpha: 0,
        duration: 380,
        ease: 'Power2.easeOut',
        onComplete: () => glow.destroy(),
      });
      for (let i = 0; i < 6; i++) {
        const mote = scene.add.graphics();
        mote.setDepth(10000);
        mote.fillStyle(0xddffee, 0.9);
        mote.fillCircle(0, 0, 3);
        const ox = (Math.random() - 0.5) * w * 0.5;
        mote.setPosition(cx + ox, cy + h * 0.15);
        scene.tweens.add({
          targets: mote,
          y: mote.y - h * 0.55 - Math.random() * 20,
          x: mote.x + (Math.random() - 0.5) * 18,
          alpha: 0,
          duration: 480 + Math.random() * 120,
          delay: i * 40,
          ease: 'Sine.easeOut',
          onComplete: () => mote.destroy(),
        });
      }
    });

    const baseScale = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.04,
      scaleY: baseScale * 1.04,
      duration: 180,
      delay: 200,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(baseScale),
    });

    flashSpriteTint(scene, sprite, 0xddffee, 320);
  },
};

/** Confluence — self-buff. Two intertwined water spirals rise from the caster's
 *  feet up to chest height, one deep navy and one pale cyan, merging into a bright
 *  glow knot at the chest. Halo droplets spiral outward. The double-helix is the
 *  read — two flows merging into one. */
const confluenceAura: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const footY = sprite.y;
    const h = sprite.displayHeight;
    const w = sprite.displayWidth;

    flashSpriteTint(scene, sprite, tint, 600);

    const strandA = scene.add.graphics();
    const strandB = scene.add.graphics();
    strandA.setDepth(9999);
    strandB.setDepth(10001);

    const helixState = { progress: 0 };
    const helixHeight = h * 0.95;
    const helixRadius = w * 0.32;
    const turns = 2.2;

    function renderHelix(): void {
      strandA.clear();
      strandB.clear();
      const p = helixState.progress;
      const segs = 28;
      strandA.lineStyle(6, 0x224477, 0.95);
      strandA.beginPath();
      for (let s = 0; s <= segs * p; s++) {
        const t = s / segs;
        const angle = t * turns * Math.PI * 2;
        const x = cx + Math.cos(angle) * helixRadius;
        const y = footY - t * helixHeight;
        if (s === 0) strandA.moveTo(x, y);
        else strandA.lineTo(x, y);
      }
      strandA.strokePath();
      strandB.lineStyle(6, tint, 0.95);
      strandB.beginPath();
      for (let s = 0; s <= segs * p; s++) {
        const t = s / segs;
        const angle = t * turns * Math.PI * 2 + Math.PI;
        const x = cx + Math.cos(angle) * helixRadius;
        const y = footY - t * helixHeight;
        if (s === 0) strandB.moveTo(x, y);
        else strandB.lineTo(x, y);
      }
      strandB.strokePath();
      strandB.lineStyle(2, 0xe6f7ff, 0.9);
      strandB.strokePath();
    }

    scene.tweens.add({
      targets: helixState,
      progress: 1,
      duration: 450,
      ease: 'Sine.easeOut',
      onUpdate: renderHelix,
    });

    scene.time.delayedCall(350, () => {
      const knot = scene.add.graphics();
      knot.setDepth(10002);
      knot.fillStyle(tint, 0.7);
      knot.fillCircle(0, 0, h * 0.16);
      knot.fillStyle(0xe6f7ff, 0.9);
      knot.fillCircle(0, 0, h * 0.1);
      knot.fillStyle(0xffffff, 0.95);
      knot.fillCircle(0, 0, h * 0.05);
      knot.setPosition(cx, cy);
      knot.setScale(0.2);
      scene.tweens.add({
        targets: knot,
        scaleX: 1.0,
        scaleY: 1.0,
        duration: 200,
        ease: 'Back.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: knot,
            alpha: 0,
            scaleX: 1.4,
            scaleY: 1.4,
            duration: 240,
            ease: 'Sine.easeOut',
            onComplete: () => knot.destroy(),
          });
        },
      });
    });

    scene.time.delayedCall(300, () => {
      for (let i = 0; i < 10; i++) {
        const drop = scene.add.graphics();
        drop.setDepth(10001);
        drop.fillStyle(0xe6f7ff, 0.9);
        drop.fillCircle(0, 0, 3);
        drop.fillStyle(0xffffff, 0.85);
        drop.fillCircle(0, 0, 1.5);
        const angle = (Math.PI * 2 * i) / 10;
        const startR = h * 0.05;
        drop.setPosition(cx + Math.cos(angle) * startR, cy + Math.sin(angle) * startR);
        const endR = h * 0.42;
        scene.tweens.add({
          targets: drop,
          x: cx + Math.cos(angle) * endR,
          y: cy + Math.sin(angle) * endR,
          alpha: 0,
          duration: 360,
          delay: i * 22,
          ease: 'Sine.easeOut',
          onComplete: () => drop.destroy(),
        });
      }
    });

    scene.time.delayedCall(500, () => {
      scene.tweens.add({
        targets: [strandA, strandB],
        alpha: 0,
        duration: 200,
        ease: 'Sine.easeIn',
        onComplete: () => {
          strandA.destroy();
          strandB.destroy();
        },
      });
    });

    const anchor = scene.add.graphics();
    anchor.setDepth(9998);
    anchor.lineStyle(3, tint, 0.85);
    anchor.strokeEllipse(0, 0, w * 0.6, h * 0.14);
    anchor.setPosition(cx, footY - 2);
    scene.tweens.add({
      targets: anchor,
      scaleX: 1.6,
      scaleY: 1.6,
      alpha: 0,
      duration: 420,
      ease: 'Sine.easeOut',
      onComplete: () => anchor.destroy(),
    });

    const baseScale = getBaseScale(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.05,
      scaleY: baseScale * 1.05,
      duration: 240,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(baseScale),
    });
  },
};

/** Engage — Tactics Tempo builder. A commander-called precision strike: a steel chevron
 *  thrusts inward at the target from the upper-right, lands with a golden command-spark
 *  flash. Reads as "officer barked the order, the strike landed exactly where they aimed." */
const tacticsEngageStrike: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const len = sprite.displayHeight * 0.55;

    // Steel chevron (>) thrusts inward from upper-right toward center
    const chevron = scene.add.graphics();
    chevron.setDepth(10000);
    const data = { progress: 0 };

    scene.tweens.add({
      targets: data,
      progress: 1,
      duration: 180,
      ease: 'Power3.easeOut',
      onUpdate: () => {
        chevron.clear();
        const cx0 = cx + len * 0.6 * (1 - data.progress);
        const cy0 = cy - len * 0.5 * (1 - data.progress);
        const size = 18;
        chevron.lineStyle(6, tint, 1);
        chevron.beginPath();
        chevron.moveTo(cx0 + size, cy0 - size);
        chevron.lineTo(cx0, cy0);
        chevron.lineTo(cx0 + size, cy0 + size);
        chevron.strokePath();
        chevron.lineStyle(2, 0xffffff, 0.95);
        chevron.beginPath();
        chevron.moveTo(cx0 + size, cy0 - size);
        chevron.lineTo(cx0, cy0);
        chevron.lineTo(cx0 + size, cy0 + size);
        chevron.strokePath();
      },
      onComplete: () => {
        // Golden command-spark on impact (8-point star burst)
        const spark = scene.add.graphics();
        spark.setDepth(10001);
        const sparkData = { scale: 0.3, alpha: 1 };
        scene.tweens.add({
          targets: sparkData,
          scale: 1.6,
          alpha: 0,
          duration: 220,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            spark.clear();
            const r = 24 * sparkData.scale;
            spark.fillStyle(0xffdd44, sparkData.alpha);
            spark.beginPath();
            spark.moveTo(cx, cy - r);
            spark.lineTo(cx + r * 0.3, cy - r * 0.3);
            spark.lineTo(cx + r, cy);
            spark.lineTo(cx + r * 0.3, cy + r * 0.3);
            spark.lineTo(cx, cy + r);
            spark.lineTo(cx - r * 0.3, cy + r * 0.3);
            spark.lineTo(cx - r, cy);
            spark.lineTo(cx - r * 0.3, cy - r * 0.3);
            spark.closePath();
            spark.fillPath();
            spark.fillStyle(0xffffff, sparkData.alpha * 0.9);
            spark.fillCircle(cx, cy, r * 0.25);
          },
          onComplete: () => spark.destroy(),
        });
        scene.tweens.add({
          targets: chevron,
          alpha: 0,
          duration: 130,
          onComplete: () => chevron.destroy(),
        });
      },
    });

    // Sprite reaction at chevron arrival
    scene.time.delayedCall(180, () => {
      shakeSprite(scene, sprite, 5);
      flashSpriteTint(scene, sprite, tint, 140);
    });
  },
};

/** Suppress — Tactics Stagger applier. Two heavy steel brackets descend on the target,
 *  pinning briefly with red disrupted-motion lines spraying outward and a Y-compress
 *  on the sprite. Reads as "command authority crushes the enemy's rhythm." */
const tacticsSuppressPress: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const w = sprite.displayHeight * 0.5;

    const leftBracket = scene.add.graphics();
    const rightBracket = scene.add.graphics();
    leftBracket.setDepth(10000);
    rightBracket.setDepth(10000);

    const data = { progress: 0 };

    scene.tweens.add({
      targets: data,
      progress: 1,
      duration: 170,
      ease: 'Power3.easeIn',
      onUpdate: () => {
        leftBracket.clear();
        rightBracket.clear();
        const offsetY = -w * 1.3 * (1 - data.progress);
        const leftX = cx - w * 0.55;
        const rightX = cx + w * 0.55;
        const armLen = w * 0.32;

        // Left bracket [-shape
        leftBracket.lineStyle(7, tint, 1);
        leftBracket.beginPath();
        leftBracket.moveTo(leftX + armLen, cy + offsetY - armLen);
        leftBracket.lineTo(leftX, cy + offsetY - armLen);
        leftBracket.lineTo(leftX, cy + offsetY + armLen);
        leftBracket.lineTo(leftX + armLen, cy + offsetY + armLen);
        leftBracket.strokePath();
        leftBracket.lineStyle(2, 0xffffff, 0.9);
        leftBracket.beginPath();
        leftBracket.moveTo(leftX + armLen, cy + offsetY - armLen);
        leftBracket.lineTo(leftX, cy + offsetY - armLen);
        leftBracket.lineTo(leftX, cy + offsetY + armLen);
        leftBracket.lineTo(leftX + armLen, cy + offsetY + armLen);
        leftBracket.strokePath();

        // Right bracket ]-shape
        rightBracket.lineStyle(7, tint, 1);
        rightBracket.beginPath();
        rightBracket.moveTo(rightX - armLen, cy + offsetY - armLen);
        rightBracket.lineTo(rightX, cy + offsetY - armLen);
        rightBracket.lineTo(rightX, cy + offsetY + armLen);
        rightBracket.lineTo(rightX - armLen, cy + offsetY + armLen);
        rightBracket.strokePath();
        rightBracket.lineStyle(2, 0xffffff, 0.9);
        rightBracket.beginPath();
        rightBracket.moveTo(rightX - armLen, cy + offsetY - armLen);
        rightBracket.lineTo(rightX, cy + offsetY - armLen);
        rightBracket.lineTo(rightX, cy + offsetY + armLen);
        rightBracket.lineTo(rightX - armLen, cy + offsetY + armLen);
        rightBracket.strokePath();
      },
      onComplete: () => {
        // Brackets snap closed — heavy press impact
        shakeSprite(scene, sprite, 7);
        flashSpriteTint(scene, sprite, 0xff2222, 170);
        scene.cameras.main.shake(90, 0.005);

        // 6 short red disrupted-motion fragment lines spraying sideways
        for (let i = 0; i < 6; i++) {
          const angle = (i < 3 ? Math.PI : 0) + (i % 3 - 1) * 0.45;
          const frag = scene.add.graphics();
          frag.setDepth(10001);
          const fragData = { progress: 0, alpha: 1 };
          const distance = w * 0.75;
          scene.tweens.add({
            targets: fragData,
            progress: 1,
            alpha: 0,
            duration: 280,
            ease: 'Power2.easeOut',
            onUpdate: () => {
              frag.clear();
              frag.lineStyle(4, 0xff2222, fragData.alpha);
              const x1 = cx + Math.cos(angle) * distance * fragData.progress * 0.4;
              const y1 = cy + Math.sin(angle) * distance * fragData.progress * 0.4;
              const x2 = cx + Math.cos(angle) * distance * fragData.progress;
              const y2 = cy + Math.sin(angle) * distance * fragData.progress;
              frag.beginPath();
              frag.moveTo(x1, y1);
              frag.lineTo(x2, y2);
              frag.strokePath();
            },
            onComplete: () => frag.destroy(),
          });
        }

        // Sprite Y-compress (pinned moment)
        const baseScale = getBaseScale(sprite);
        scene.tweens.add({
          targets: sprite,
          scaleY: baseScale * 0.88,
          duration: 90,
          yoyo: true,
          ease: 'Power2.easeOut',
        });

        // Fade brackets
        scene.tweens.add({
          targets: [leftBracket, rightBracket],
          alpha: 0,
          duration: 220,
          onComplete: () => {
            leftBracket.destroy();
            rightBracket.destroy();
          },
        });
      },
    });
  },
};

/** Standing Order — Tactics ally buff. A bugle call: golden chevron forms above the ally,
 *  three expanding golden soundwave rings emanate outward, ally bobs upward with a golden
 *  flash. Echoes the icon (bugle + chevron + soundwaves). Authoritative ally empowerment. */
const tacticsStandingOrderCall: AnimationPreset = {
  play(scene, sprite, _tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const headY = cy - sprite.displayHeight * 0.45;

    // 1. Golden chevron forms above ally (drops into position with bounce)
    const chevron = scene.add.graphics();
    chevron.setDepth(10001);
    const chevronData = { progress: 0, alpha: 0 };
    scene.tweens.add({
      targets: chevronData,
      progress: 1,
      alpha: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onUpdate: () => {
        chevron.clear();
        const size = 20;
        const yOffset = headY - 14 + 22 * (1 - chevronData.progress);
        chevron.lineStyle(6, 0xffdd44, chevronData.alpha);
        chevron.beginPath();
        chevron.moveTo(cx - size, yOffset + size * 0.6);
        chevron.lineTo(cx, yOffset - size * 0.5);
        chevron.lineTo(cx + size, yOffset + size * 0.6);
        chevron.strokePath();
        chevron.lineStyle(2, 0xffffff, chevronData.alpha * 0.9);
        chevron.beginPath();
        chevron.moveTo(cx - size, yOffset + size * 0.6);
        chevron.lineTo(cx, yOffset - size * 0.5);
        chevron.lineTo(cx + size, yOffset + size * 0.6);
        chevron.strokePath();
      },
      onComplete: () => {
        scene.time.delayedCall(300, () => {
          scene.tweens.add({
            targets: chevron,
            alpha: 0,
            duration: 240,
            onComplete: () => chevron.destroy(),
          });
        });
      },
    });

    // 2. Three expanding golden soundwave rings
    for (let i = 0; i < 3; i++) {
      scene.time.delayedCall(80 + i * 110, () => {
        const ring = scene.add.graphics();
        ring.setDepth(9999);
        const ringData = { radius: 10, alpha: 0.85 };
        scene.tweens.add({
          targets: ringData,
          radius: sprite.displayHeight * 0.6,
          alpha: 0,
          duration: 380,
          ease: 'Power2.easeOut',
          onUpdate: () => {
            ring.clear();
            ring.lineStyle(4, 0xffdd44, ringData.alpha);
            ring.strokeCircle(cx, headY, ringData.radius);
            ring.lineStyle(1, 0xffffff, ringData.alpha * 0.7);
            ring.strokeCircle(cx, headY, ringData.radius * 0.95);
          },
          onComplete: () => ring.destroy(),
        });
      });
    }

    // 3. Ally bob upward + golden tint flash
    const baseY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: baseY - 14,
      duration: 200,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => { sprite.y = baseY; },
    });
    flashSpriteTint(scene, sprite, 0xffdd88, 320);

    // 4. Upward sparkle particles
    for (let i = 0; i < 5; i++) {
      const particle = scene.add.graphics();
      particle.fillStyle(0xffdd44, 0.9);
      particle.fillCircle(0, 0, 3);
      particle.setDepth(9999);
      const px = cx + (Math.random() - 0.5) * sprite.displayHeight * 0.55;
      const py = cy + (Math.random() - 0.5) * 18;
      particle.setPosition(px, py);
      scene.tweens.add({
        targets: particle,
        y: py - sprite.displayHeight * 0.45,
        alpha: 0,
        duration: 520,
        delay: i * 55,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  },
};

/** Coordinate — Tactics single-ally timeline push. Three golden right-pointing chevrons race
 *  forward in sequence (left → right of the ally), each larger than the previous, riding a
 *  steel-blue motion trail. Ally bumps upward + golden flash. Final golden burst at the tip
 *  of the push anchors the arrival beat. Distinct from `buff_shimmer` by the directional
 *  chevron parade — visualizes "I pushed my ally forward in the timeline." */
const tacticsCoordinatePush: AnimationPreset = {
  play(scene, sprite, _tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const h = sprite.displayHeight;

    // Steel-blue motion trail underneath, sweeping left → right across the path
    const trail = scene.add.graphics();
    trail.setDepth(9999);
    const trailData = { progress: 0, alpha: 1 };
    scene.tweens.add({
      targets: trailData,
      progress: 1,
      alpha: 0,
      duration: 480,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        trail.clear();
        trail.lineStyle(4, 0x6688bb, trailData.alpha * 0.75);
        const leftX = cx - h * 0.55 + trailData.progress * h * 0.5;
        const rightX = cx + h * 0.55 + trailData.progress * h * 0.4;
        trail.beginPath();
        trail.moveTo(leftX, cy);
        trail.lineTo(rightX, cy);
        trail.strokePath();
      },
      onComplete: () => trail.destroy(),
    });

    // Three golden chevrons racing forward, each larger than the last
    for (let i = 0; i < 3; i++) {
      const startX = cx - h * 0.55;
      const endX = cx + h * 0.55 + i * (h * 0.05);
      const sizeBase = 10 + i * 8; // 10, 18, 26
      const yJitter = (i - 1) * h * 0.06;
      const chevron = scene.add.graphics();
      chevron.setDepth(10000);
      const data = { x: startX, alpha: 0 };

      scene.tweens.add({
        targets: data,
        x: endX,
        alpha: 1,
        duration: 260,
        delay: i * 80,
        ease: 'Power3.easeIn',
        onUpdate: () => {
          chevron.clear();
          chevron.lineStyle(6, 0xffdd44, data.alpha);
          chevron.beginPath();
          chevron.moveTo(data.x - sizeBase, cy + yJitter - sizeBase);
          chevron.lineTo(data.x, cy + yJitter);
          chevron.lineTo(data.x - sizeBase, cy + yJitter + sizeBase);
          chevron.strokePath();
          chevron.lineStyle(2, 0xffffff, data.alpha * 0.95);
          chevron.beginPath();
          chevron.moveTo(data.x - sizeBase, cy + yJitter - sizeBase);
          chevron.lineTo(data.x, cy + yJitter);
          chevron.lineTo(data.x - sizeBase, cy + yJitter + sizeBase);
          chevron.strokePath();
        },
        onComplete: () => {
          scene.tweens.add({
            targets: chevron,
            alpha: 0,
            duration: 140,
            onComplete: () => chevron.destroy(),
          });
        },
      });
    }

    // Ally bumps upward + scale pulse + golden tint flash
    const baseScale = getBaseScale(sprite);
    const baseY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: baseY - h * 0.08,
      duration: 140,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => { sprite.y = baseY; },
    });
    scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.04,
      scaleY: baseScale * 1.04,
      duration: 140,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => sprite.setScale(baseScale),
    });
    flashSpriteTint(scene, sprite, 0xffdd44, 280);

    // Final golden burst at the tip of the push (anchors the arrival beat)
    scene.time.delayedCall(340, () => {
      const burst = scene.add.graphics();
      burst.setDepth(10001);
      const burstData = { r: 6, alpha: 1 };
      scene.tweens.add({
        targets: burstData,
        r: h * 0.22,
        alpha: 0,
        duration: 240,
        ease: 'Power2.easeOut',
        onUpdate: () => {
          burst.clear();
          burst.fillStyle(0xffdd44, burstData.alpha * 0.6);
          burst.fillCircle(cx + h * 0.55, cy, burstData.r);
          burst.fillStyle(0xffffff, burstData.alpha * 0.9);
          burst.fillCircle(cx + h * 0.55, cy, burstData.r * 0.35);
        },
        onComplete: () => burst.destroy(),
      });
    });
  },
};

/** Reckoning — Zealotry Fervor spender. Hammer-slam wedge descends, starburst impact,
 *  then horizontal healing-crescent ripples sweep outward — the party-heal pulse beat.
 *  Geometric language is short-wedge + outward rings, distinct from Judgement's vertical pillar. */
const holyRadianceStrike: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const sw = sprite.displayWidth;
    const sh = sprite.displayHeight;
    const coreTint = 0xfff5cc;

    // Phase 1: descending golden wedge (anvil head) — short, decisive
    const wedge = scene.add.triangle(
      cx,
      cy - sh * 0.9,
      0, -sh * 0.35,
      -sw * 0.18, sh * 0.10,
      sw * 0.18, sh * 0.10,
      tint,
      0.95,
    );
    wedge.setDepth(10000);
    scene.tweens.add({
      targets: wedge,
      y: cy - sh * 0.05,
      duration: 100,
      ease: 'Power3.easeIn',
      onComplete: () => {
        scene.tweens.add({
          targets: wedge,
          alpha: 0,
          scaleY: 0.6,
          duration: 180,
          ease: 'Power2.easeOut',
          onComplete: () => wedge.destroy(),
        });
      },
    });

    // Phase 2: starburst at impact — 8 radial rays explode outward (110ms in)
    scene.time.delayedCall(100, () => {
      const rayCount = 8;
      const rayLen = sw * 0.55;
      for (let i = 0; i < rayCount; i++) {
        const angle = (Math.PI * 2 * i) / rayCount;
        const ray = scene.add.rectangle(cx, cy, rayLen, 4, tint, 0.95);
        ray.setOrigin(0, 0.5);
        ray.setRotation(angle);
        ray.setDepth(10001);
        ray.setScale(0, 1);
        scene.tweens.add({
          targets: ray,
          scaleX: 1,
          duration: 110,
          ease: 'Power3.easeOut',
        });
        scene.tweens.add({
          targets: ray,
          alpha: 0,
          duration: 200,
          delay: 140,
          ease: 'Power2.easeIn',
          onComplete: () => ray.destroy(),
        });
      }

      // White-hot center pop at the impact
      const core = scene.add.circle(cx, cy, sw * 0.10, coreTint, 1);
      core.setDepth(10002);
      core.setScale(0.3);
      scene.tweens.add({
        targets: core,
        scaleX: 1.4,
        scaleY: 1.4,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeOut',
        onComplete: () => core.destroy(),
      });
    });

    // Phase 3: healing crescent ripples — 2 horizontal arcs sweeping outward (party-heal pulse)
    scene.time.delayedCall(200, () => {
      for (let r = 0; r < 2; r++) {
        const arc = scene.add.graphics();
        arc.setDepth(9998);
        const baseR = sw * 0.30;
        arc.lineStyle(3, tint, 0.85);
        // Crescent = bottom half-circle only (semicircular sweep at sprite floor)
        arc.beginPath();
        arc.arc(cx, cy + sh * 0.10, baseR, 0, Math.PI, false);
        arc.strokePath();
        arc.setScale(0.4);
        arc.setAlpha(0.9);
        scene.tweens.add({
          targets: arc,
          scaleX: 1.8 + r * 0.5,
          scaleY: 1.8 + r * 0.5,
          alpha: 0,
          duration: 380,
          delay: r * 90,
          ease: 'Power2.easeOut',
          onComplete: () => arc.destroy(),
        });
      }
    });

    flashSpriteTint(scene, sprite, tint, 220);
    shakeSprite(scene, sprite, 6);
  },
};

/** Castigate — Zealotry 2-turn cast finisher. Bigger, paler cousin of divine_pillar.
 *  Wide dual-column descending pillar with white-hot core flicker + ground-burst triangles. */
const wrathPillar: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y;
    const beamHeight = sprite.displayHeight * 3.0;
    const beamWidth = sprite.displayWidth * 0.30;
    const beamCenterY = baseY - beamHeight / 2;
    const beamGap = sprite.displayWidth * 0.45;
    const coreTint = 0xffffff;

    // Two parallel descending beams
    const leftBeam = scene.add.rectangle(cx - beamGap / 2, beamCenterY, beamWidth, beamHeight, tint, 0.6);
    const rightBeam = scene.add.rectangle(cx + beamGap / 2, beamCenterY, beamWidth, beamHeight, tint, 0.6);
    leftBeam.setDepth(9999);
    rightBeam.setDepth(9999);
    leftBeam.setScale(0, 1);
    rightBeam.setScale(0, 1);

    // Inner white cores
    const leftCore = scene.add.rectangle(cx - beamGap / 2, beamCenterY, beamWidth * 0.35, beamHeight, coreTint, 0.9);
    const rightCore = scene.add.rectangle(cx + beamGap / 2, beamCenterY, beamWidth * 0.35, beamHeight, coreTint, 0.9);
    leftCore.setDepth(10000);
    rightCore.setDepth(10000);
    leftCore.setScale(0, 1);
    rightCore.setScale(0, 1);

    scene.tweens.add({
      targets: [leftBeam, rightBeam, leftCore, rightCore],
      scaleX: 1,
      duration: 80,
      ease: 'Power3.easeOut',
    });

    // Hold + expand + fade
    scene.tweens.add({
      targets: [leftBeam, rightBeam],
      alpha: 0,
      scaleX: 1.4,
      duration: 350,
      delay: 200,
      ease: 'Power2.easeIn',
      onComplete: () => { leftBeam.destroy(); rightBeam.destroy(); },
    });
    scene.tweens.add({
      targets: [leftCore, rightCore],
      alpha: 0,
      duration: 300,
      delay: 200,
      ease: 'Power2.easeIn',
      onComplete: () => { leftCore.destroy(); rightCore.destroy(); },
    });

    // Ground-burst triangles erupting outward
    scene.time.delayedCall(150, () => {
      const triCount = 8;
      for (let i = 0; i < triCount; i++) {
        const angle = -Math.PI + (Math.PI * i) / (triCount - 1); // upper hemisphere
        const tri = scene.add.triangle(cx, baseY, 0, -8, -4, 8, 4, 8, tint, 0.95);
        tri.setDepth(10001);
        tri.setRotation(angle + Math.PI / 2);
        const dist = 40;
        scene.tweens.add({
          targets: tri,
          x: cx + Math.cos(angle) * dist,
          y: baseY + Math.sin(angle) * dist,
          alpha: 0,
          scaleX: 1.6,
          scaleY: 1.6,
          duration: 320,
          ease: 'Power2.easeOut',
          onComplete: () => tri.destroy(),
        });
      }
    });

    const r = (tint >> 16) & 0xff;
    const g = (tint >> 8) & 0xff;
    const b = tint & 0xff;
    scene.cameras.main.flash(160, r, g, b, false);
    shakeSprite(scene, sprite, 12);
    flashSpriteTint(scene, sprite, tint, 250);
  },
};

/** Condemn — Zealotry single-target mark. 4-pointed cross/star sigil sears in via rotating
 *  arc above target's chest, then descending sparks. No camera shake — this is a mark, not a hit. */
const holyBrand: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const armLen = sprite.displayWidth * 0.20;

    // Rotating 4-pointed cross sigil
    const sigil = scene.add.graphics();
    sigil.setDepth(10000);
    sigil.setPosition(cx, cy);
    sigil.setRotation(-Math.PI / 2);
    sigil.lineStyle(3, tint, 0.95);
    sigil.beginPath();
    sigil.moveTo(-armLen, 0); sigil.lineTo(armLen, 0);
    sigil.moveTo(0, -armLen); sigil.lineTo(0, armLen);
    sigil.strokePath();
    sigil.fillStyle(0xffffff, 0.85);
    sigil.fillCircle(0, 0, 3);
    sigil.setAlpha(0);
    sigil.setScale(0.5);

    scene.tweens.add({
      targets: sigil,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      rotation: 0,
      duration: 180,
      ease: 'Power3.easeOut',
    });
    scene.tweens.add({
      targets: sigil,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 250,
      delay: 300,
      ease: 'Power2.easeIn',
      onComplete: () => sigil.destroy(),
    });

    // 4 sparks drifting downward
    for (let i = 0; i < 4; i++) {
      const spark = scene.add.circle(cx + (i - 1.5) * 6, cy + armLen, 2.5, tint, 0.9);
      spark.setDepth(10001);
      scene.tweens.add({
        targets: spark,
        y: spark.y + 18,
        alpha: 0,
        duration: 380,
        delay: 120 + i * 40,
        ease: 'Power2.easeOut',
        onComplete: () => spark.destroy(),
      });
    }

    flashSpriteTint(scene, sprite, tint, 180);
    shakeSprite(scene, sprite, 3);
  },
};

/** Manifest Blade — Manifestation native self-augment cast. Three-phase bespoke preset:
 *  1) Vertical light-line writes in beside the caster (~120ms etch — radiant scribe).
 *  2) Blade silhouette forms (elongated diamond, gold outline + white core), holds ~200ms with subtle pulse.
 *  3) Blade fragments into 6–8 streaks that sweep inward into the caster's chest + tint flash + bs*1.05 yoyo.
 *  Total ~500ms. No camera shake — this is a self-augment, not an impact. Paler-gold tint (0xffe28a expected). */
const buffManifestBlade: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    // Position the conjured blade beside the caster — offset to the right of sprite.
    const bladeX = cx + sprite.displayWidth * 0.30;
    const bladeHalfH = sprite.displayHeight * 0.22;
    const bladeHalfW = 5;

    // Phase 1 (0–120ms): vertical light-line etches in.
    const etch = scene.add.graphics();
    etch.lineStyle(2, 0xffffff, 0.9);
    etch.lineBetween(bladeX, cy - bladeHalfH, bladeX, cy + bladeHalfH);
    etch.setDepth(10000);
    etch.setAlpha(0);
    etch.setScale(1, 0.1);
    scene.tweens.add({
      targets: etch,
      alpha: 1,
      scaleY: 1,
      duration: 120,
      ease: 'Power2.easeOut',
    });
    // Etch fades as the blade body forms over it.
    scene.tweens.add({
      targets: etch,
      alpha: 0,
      duration: 140,
      delay: 140,
      onComplete: () => etch.destroy(),
    });

    // Phase 2 (120–320ms): elongated diamond blade — gold outline + white core.
    scene.time.delayedCall(120, () => {
      const blade = scene.add.graphics();
      blade.setDepth(10000);
      blade.setPosition(bladeX, cy);
      // Outline (tint = paler gold from def).
      blade.lineStyle(2, tint, 0.95);
      blade.beginPath();
      blade.moveTo(0, -bladeHalfH);
      blade.lineTo(bladeHalfW, 0);
      blade.lineTo(0, bladeHalfH);
      blade.lineTo(-bladeHalfW, 0);
      blade.closePath();
      blade.strokePath();
      // White core fill.
      blade.fillStyle(0xffffff, 0.7);
      blade.fillPath();
      blade.setAlpha(0);
      blade.setScale(0.6, 0.6);

      // Form in.
      scene.tweens.add({
        targets: blade,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Power2.easeOut',
      });
      // Subtle pulse mid-hold.
      scene.tweens.add({
        targets: blade,
        scaleX: 1.15,
        scaleY: 1.05,
        duration: 80,
        delay: 110,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });

      // Phase 3 (~320ms in): fragments sweep inward to caster's chest.
      scene.time.delayedCall(200, () => {
        // Dissolve the blade.
        scene.tweens.add({
          targets: blade,
          alpha: 0,
          duration: 80,
          onComplete: () => blade.destroy(),
        });
        // 7 streaks sweep from blade position to caster chest.
        for (let i = 0; i < 7; i++) {
          const streak = scene.add.graphics();
          const isCore = i % 3 === 0;
          streak.fillStyle(isCore ? 0xffffff : tint, 0.85);
          streak.fillTriangle(0, -2.5, 8, 0, 0, 2.5);
          const startOffsetY = (Math.random() - 0.5) * bladeHalfH * 1.8;
          streak.setPosition(bladeX, cy + startOffsetY);
          streak.setRotation(Math.PI); // pointing left toward caster
          streak.setDepth(10001);
          scene.tweens.add({
            targets: streak,
            x: cx,
            y: cy + startOffsetY * 0.3,
            alpha: 0,
            duration: 180,
            delay: i * 12,
            ease: 'Power3.easeIn',
            onComplete: () => streak.destroy(),
          });
        }
        // Tint flash + scale yoyo on caster as the augment lands.
        flashSpriteTint(scene, sprite, tint, 200);
        const bs = getBaseScale(sprite);
        scene.tweens.add({
          targets: sprite,
          scaleX: bs * 1.05,
          scaleY: bs * 1.05,
          duration: 90,
          delay: 60,
          yoyo: true,
          ease: 'Sine.easeInOut',
          onComplete: () => sprite.setScale(getBaseScale(sprite)),
        });
      });
    });
  },
};

// ---------------------------------------------------------------------------
// Fortification family (5 new abilities)
// ---------------------------------------------------------------------------

/** Fortify: settling-stance dip-and-snap + 4 pewter geometric plates fading in at edges. */
const buffFortify: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 280);
    const cy = spriteCenterY(sprite);

    // Dip-and-snap settling motion (weight on back foot)
    const startY = sprite.y;
    scene.tweens.killTweensOf(sprite);
    scene.tweens.add({
      targets: sprite,
      y: startY + 3,
      duration: 110,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    // 4 pewter rectangle plates at sprite corners — fade in/out (plate-stack visual)
    const w = sprite.displayWidth * 0.5;
    const h = sprite.displayHeight * 0.4;
    const corners: { x: number; y: number; ang: number }[] = [
      { x: sprite.x - w / 2, y: cy - h / 2, ang: -30 },
      { x: sprite.x + w / 2, y: cy - h / 2, ang: 30 },
      { x: sprite.x - w / 2, y: cy + h / 2, ang: 30 },
      { x: sprite.x + w / 2, y: cy + h / 2, ang: -30 },
    ];
    corners.forEach((c, i) => {
      const plate = scene.add.graphics();
      plate.setDepth(10000);
      plate.fillStyle(tint, 0.7);
      plate.lineStyle(2, 0xeeeeee, 0.85);
      plate.fillRect(-9, -3, 18, 6);
      plate.strokeRect(-9, -3, 18, 6);
      plate.setPosition(c.x, c.y);
      plate.setRotation((c.ang * Math.PI) / 180);
      plate.setAlpha(0);
      scene.tweens.add({
        targets: plate,
        alpha: 1,
        duration: 100,
        delay: i * 35,
        yoyo: true,
        hold: 110,
        onComplete: () => plate.destroy(),
      });
    });
  },
};

/** Bristle (cast): outward-radial spike triangles erupt from the sprite silhouette. */
const buffBristle: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 240);
    const cy = spriteCenterY(sprite);

    const count = 7;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count - Math.PI / 2;
      const startDist = sprite.displayWidth * 0.25;
      const endDist = sprite.displayWidth * 0.55;

      const spike = scene.add.graphics();
      spike.setDepth(10000);
      spike.fillStyle(0xccccdd, 0.95);
      spike.lineStyle(2, 0x000000, 0.55);
      // Pointed triangle aimed outward (tip up in local space, rotated below)
      spike.beginPath();
      spike.moveTo(0, -14);
      spike.lineTo(-3.5, 2);
      spike.lineTo(3.5, 2);
      spike.closePath();
      spike.fillPath();
      spike.strokePath();
      spike.setRotation(angle + Math.PI / 2);

      const startX = sprite.x + Math.cos(angle) * startDist;
      const startY = cy + Math.sin(angle) * startDist;
      const endX = sprite.x + Math.cos(angle) * endDist;
      const endY = cy + Math.sin(angle) * endDist;
      spike.setPosition(startX, startY);
      spike.setAlpha(0);

      scene.tweens.add({
        targets: spike,
        x: endX,
        y: endY,
        alpha: 1,
        duration: 120,
        yoyo: true,
        hold: 90,
        ease: 'Power2.easeOut',
        onComplete: () => spike.destroy(),
      });
    }
  },
};

/** Bristle retaliate proc (fired on the ATTACKER): silver stab line + crimson blood-spray drops. */
const bristleRetaliate: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, 0xc8d4dc, 120);
    shakeSprite(scene, sprite, 3);
    const cy = spriteCenterY(sprite);

    // Quick silver stab line drawn into the sprite
    const stab = scene.add.graphics();
    stab.setDepth(10001);
    stab.lineStyle(5, 0xeeeeff, 1);
    const startX = sprite.x - sprite.displayWidth * 0.3;
    const startY = cy - sprite.displayHeight * 0.1;
    stab.lineBetween(startX, startY, sprite.x, cy);
    stab.setAlpha(0);
    scene.tweens.add({
      targets: stab,
      alpha: 1,
      duration: 60,
      yoyo: true,
      hold: 40,
      onComplete: () => stab.destroy(),
    });

    // Crimson blood-drop particles fanning outward (slight downward bias for gravity)
    scene.time.delayedCall(80, () => {
      for (let i = 0; i < 5; i++) {
        const blood = scene.add.graphics();
        blood.setDepth(10000);
        blood.fillStyle(0xaa1122, 0.92);
        blood.beginPath();
        blood.moveTo(0, -4);
        blood.lineTo(-2.5, 3);
        blood.lineTo(2.5, 3);
        blood.closePath();
        blood.fillPath();
        const ang = (Math.PI * 2 * i) / 5 + Math.PI / 6;
        blood.setPosition(sprite.x, cy);
        blood.setRotation(ang);
        const dist = 18 + Math.random() * 12;
        scene.tweens.add({
          targets: blood,
          x: sprite.x + Math.cos(ang) * dist,
          y: cy + Math.sin(ang) * dist + 8,
          alpha: 0,
          duration: 240,
          ease: 'Power2.easeOut',
          onComplete: () => blood.destroy(),
        });
      }
    });
  },
};

/** Lockstep: steel-blue ring expands + horizontal tracer lines to ally positions. */
const buffLockstep: AnimationPreset = {
  play(scene, sprite, tint) {
    flashSpriteTint(scene, sprite, tint, 280);
    const cy = spriteCenterY(sprite);

    // Expanding ring outline (formation field)
    const ring = scene.add.graphics();
    ring.setDepth(10000);
    ring.lineStyle(3, tint, 1);
    ring.strokeCircle(sprite.x, cy, sprite.displayWidth * 0.35);
    scene.tweens.add({
      targets: ring,
      scale: 1.8,
      alpha: 0,
      duration: 360,
      ease: 'Power2.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Horizontal connection tracers (left & right toward where allies stand)
    for (const dir of [-1, 1]) {
      const tracer = scene.add.graphics();
      tracer.setDepth(10001);
      tracer.lineStyle(2, 0xaaccee, 0.85);
      const startX = sprite.x + dir * sprite.displayWidth * 0.4;
      const endX = sprite.x + dir * sprite.displayWidth * 1.2;
      tracer.lineBetween(startX, cy, endX, cy);
      tracer.setAlpha(0);
      scene.tweens.add({
        targets: tracer,
        alpha: 0.9,
        duration: 90,
        yoyo: true,
        hold: 80,
        delay: 50,
        onComplete: () => tracer.destroy(),
      });
    }

    // Subtle scale pulse
    const bs = getBaseScale(sprite);
    scene.tweens.killTweensOf(sprite);
    scene.tweens.add({
      targets: sprite,
      scaleX: bs * 1.03,
      scaleY: bs * 1.03,
      duration: 110,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(getBaseScale(sprite)),
    });
  },
};

/** Temper: warm orange flash (heated steel) → cool steel transition + glowing seam-lines. */
/** Temper: 8 inward-converging ribbon particles (Damascus fold) → dense flash + scale punch.
 *  Visual identity is the barrier-into-plate fold — ribbons converge on the wearer as the
 *  consumed Barrier becomes Fortified stacks. */
const buffTemper: AnimationPreset = {
  play(scene, sprite, tint) {
    const cy = spriteCenterY(sprite);

    // 8 inward-converging "ribbon" particles
    for (let i = 0; i < 8; i++) {
      const particle = scene.add.graphics();
      particle.setDepth(10000);
      particle.fillStyle(0xaaccff, 0.95);
      particle.fillRect(-4, -1.5, 8, 3);
      const ang = (Math.PI * 2 * i) / 8;
      const startDist = 40;
      particle.setPosition(
        sprite.x + Math.cos(ang) * startDist,
        cy + Math.sin(ang) * startDist,
      );
      particle.setRotation(ang + Math.PI / 2);
      scene.tweens.add({
        targets: particle,
        x: sprite.x,
        y: cy,
        alpha: 0,
        scale: 0.5,
        duration: 250,
        delay: i * 15,
        ease: 'Power3.easeIn',
        onComplete: () => particle.destroy(),
      });
    }

    // Convergence flash + scale punch + golden core
    scene.time.delayedCall(240, () => {
      flashSpriteTint(scene, sprite, tint, 200);
      const bs = getBaseScale(sprite);
      scene.tweens.killTweensOf(sprite);
      scene.tweens.add({
        targets: sprite,
        scaleX: bs * 1.05,
        scaleY: bs * 1.05,
        duration: 90,
        yoyo: true,
        ease: 'Sine.easeInOut',
        onComplete: () => sprite.setScale(getBaseScale(sprite)),
      });
      const core = scene.add.graphics();
      core.setDepth(10001);
      core.fillStyle(0xffddaa, 0.9);
      core.fillCircle(0, 0, 8);
      core.setPosition(sprite.x, cy);
      scene.tweens.add({
        targets: core,
        scale: 2.5,
        alpha: 0,
        duration: 280,
        ease: 'Power2.easeOut',
        onComplete: () => core.destroy(),
      });
    });
  },
};

/** Bombard shell — single heavy projectile streaks from above-offscreen and
 *  explodes on impact. Designed as one beat of a multi-hit bombardment: the
 *  AoE multi-hit pipeline plays it 3× at 240ms cadence for the three-shell
 *  barrage feel. Total duration ~230ms so each beat resolves before the next
 *  shell arrives — three distinct "ka-THUMPs" instead of overlapping rain. */
const bombardShell: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const cy = spriteCenterY(sprite);
    const baseY = sprite.y;
    const startY = cy - sprite.displayHeight * 1.4;

    // Shell streak — main body + 3 trailing afterimages, fading behind
    const trailOffsets = [0, 18, 32, 44];
    const trailAlphas = [1, 0.7, 0.45, 0.25];
    trailOffsets.forEach((offset, idx) => {
      const shell = scene.add.graphics();
      shell.fillStyle(tint, trailAlphas[idx]);
      shell.fillEllipse(0, 0, 6, 14);
      shell.fillStyle(0xffffff, trailAlphas[idx] * 0.9);
      shell.fillEllipse(0, 0, 3, 8);
      shell.setPosition(cx, startY - offset);
      shell.setDepth(10000);

      scene.tweens.add({
        targets: shell,
        y: cy - offset * 0.2,
        alpha: 0,
        duration: 110,
        ease: 'Power3.easeIn',
        onComplete: () => shell.destroy(),
      });
    });

    // Impact + ember burst + dust ring fire when the shell lands
    scene.time.delayedCall(105, () => {
      // Bright white core flash
      const flash = scene.add.circle(cx, cy, 12, 0xffffff, 0.95);
      flash.setDepth(10001);
      scene.tweens.add({
        targets: flash,
        scale: 4,
        alpha: 0,
        duration: 180,
        ease: 'Power2.easeOut',
        onComplete: () => flash.destroy(),
      });

      // Fire-tinted outer halo
      const halo = scene.add.circle(cx, cy, 18, tint, 0.7);
      halo.setDepth(10000);
      scene.tweens.add({
        targets: halo,
        scale: 3.5,
        alpha: 0,
        duration: 220,
        ease: 'Power2.easeOut',
        onComplete: () => halo.destroy(),
      });

      // 8 jagged ember shards radiating outward
      const emberCount = 8;
      const reach = sprite.displayHeight * 0.35;
      for (let i = 0; i < emberCount; i++) {
        const angle = (i / emberCount) * Math.PI * 2 + Math.random() * 0.3;
        const ember = scene.add.graphics();
        ember.fillStyle(tint, 0.9);
        ember.fillTriangle(0, -3, -2, 3, 2, 3);
        ember.setPosition(cx, cy);
        ember.setRotation(angle);
        ember.setDepth(10001);

        const dist = reach * (0.8 + Math.random() * 0.4);
        scene.tweens.add({
          targets: ember,
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist,
          alpha: 0,
          duration: 200,
          ease: 'Power2.easeOut',
          onComplete: () => ember.destroy(),
        });
      }

      // Low ground dust ring at sprite base
      const dust = scene.add.ellipse(cx, baseY - 4, 40, 12, 0xbba88a, 0.6);
      dust.setDepth(9999);
      scene.tweens.add({
        targets: dust,
        scaleX: 2.4,
        scaleY: 1.4,
        alpha: 0,
        duration: 240,
        ease: 'Power2.easeOut',
        onComplete: () => dust.destroy(),
      });

      shakeSprite(scene, sprite, 7);
      flashSpriteTint(scene, sprite, tint, 180);
    });
  },
};

/** Powderkeg blast — hidden barrel detonates at the target's feet. Bright
 *  core flash + wide low shockwave hugging the ground + embers blasted
 *  upward-and-outward + smoke puff rising. Sprite gets a brief upward kick
 *  to sell the lift. Distinct from `bombard_shell` (incoming shell from
 *  above) and `rain_down` (volley of small drops). */
const powderkegBlast: AnimationPreset = {
  play(scene, sprite, tint) {
    const cx = sprite.x;
    const baseY = sprite.y - 4;

    // White core flash at base
    const core = scene.add.circle(cx, baseY, 14, 0xffffff, 1);
    core.setDepth(10001);
    scene.tweens.add({
      targets: core,
      scale: 4.5,
      alpha: 0,
      duration: 200,
      ease: 'Power3.easeOut',
      onComplete: () => core.destroy(),
    });

    // Wide low shockwave — flat ellipse hugging the ground
    const shockwave = scene.add.ellipse(cx, baseY, 50, 18, tint, 0.75);
    shockwave.setDepth(10000);
    scene.tweens.add({
      targets: shockwave,
      scaleX: 3.2,
      scaleY: 2.8,
      alpha: 0,
      duration: 320,
      ease: 'Power2.easeOut',
      onComplete: () => shockwave.destroy(),
    });

    // 10 ember shards radiating upward-and-outward (upper hemisphere bias —
    // ground explosions throw debris UP, not down)
    const emberCount = 10;
    const reach = sprite.displayHeight * 0.45;
    for (let i = 0; i < emberCount; i++) {
      const t = i / (emberCount - 1);
      const angle = -Math.PI * (0.85 - t * 0.7) + (Math.random() - 0.5) * 0.25;
      const ember = scene.add.graphics();
      ember.fillStyle(tint, 0.95);
      ember.fillTriangle(0, -4, -2.5, 3, 2.5, 3);
      ember.setPosition(cx, baseY);
      ember.setRotation(angle + Math.PI * 0.5);
      ember.setDepth(10001);

      const dist = reach * (0.7 + Math.random() * 0.5);
      scene.tweens.add({
        targets: ember,
        x: cx + Math.cos(angle) * dist,
        y: baseY + Math.sin(angle) * dist,
        alpha: 0,
        duration: 260 + Math.random() * 80,
        ease: 'Power2.easeOut',
        onComplete: () => ember.destroy(),
      });
    }

    // Smoke puff rising above the blast
    scene.time.delayedCall(80, () => {
      const smoke = scene.add.ellipse(cx, baseY - 8, 22, 18, 0x6a5a4a, 0.55);
      smoke.setDepth(9998);
      scene.tweens.add({
        targets: smoke,
        y: baseY - 50,
        scaleX: 2.4,
        scaleY: 2.4,
        alpha: 0,
        duration: 320,
        ease: 'Sine.easeOut',
        onComplete: () => smoke.destroy(),
      });
    });

    // Sprite kick — brief upward lift, then settle. Sells the concussive blow.
    const originalY = sprite.y;
    scene.tweens.add({
      targets: sprite,
      y: originalY - 7,
      duration: 90,
      yoyo: true,
      ease: 'Power2.easeOut',
      onComplete: () => { sprite.y = originalY; },
    });

    shakeSprite(scene, sprite, 8);
    flashSpriteTint(scene, sprite, tint, 220);
  },
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const PRESETS: Record<string, AnimationPreset> = {
  slash,
  impact,
  burst,
  heal_pulse: healPulse,
  buff_glow: buffGlow,
  dot_burn: dotBurn,
  buff_shimmer: buffShimmer,
  debuff_flash: debuffFlash,
  charge_glow: chargeGlow,
  charge_buildup: chargeBuildup,
  target_lock: targetLock,
  water_gather: waterGather,
  devastating_impact: devastatingImpact,
  pierce,
  stab,
  knuckle_strike: knuckleStrike,
  snap_bite: snapBite,
  shadow_burst: shadowBurst,
  shadow_sting: shadowSting,
  summon_rise: summonRise,
  spore_cloud: sporeCloud,
  dot_bleed: dotBleed,
  shout_wave: shoutWave,
  ground_rumble: groundRumble,
  stone_resonance: stoneResonance,
  vanish_fade: vanishFade,
  flash_blind: flashBlind,
  gunshot,
  rapid_strikes: rapidStrikes,
  bone_crackers: boneCrackers,
  palm_strike_impact: palmStrikeImpact,
  haymaker_hook: haymakerHook,
  barrier_apply_water: barrierApplyWater,
  barrier_apply_steel: barrierApplySteel,
  barrier_shatter: barrierShatter,
  break_shatter: breakShatter,
  nature_afflict: natureAfflict,
  sandstorm_hit: sandstormHit,
  sand_crust: sandCrust,
  stone_jab: stoneJab,
  bark_form: barkForm,
  boss_intensify: bossIntensify,
  idle_pulse: idlePulse,
  divine_pillar: divinePillar,
  cross_slash: crossSlash,
  lightning_strike: lightningStrike,
  lightning_arc: lightningArc,
  water_crash: waterCrash,
  water_surge: waterSurge,
  drown_pull: drownPull,
  tidal_sweep: tidalSweep,
  abyssal_collapse: abyssalCollapse,
  rain_down: rainDown,
  crescent_sweep: crescentSweep,
  ground_eruption: groundEruption,
  vortex: vortexPreset,
  vine_lash: vineLash,
  tongue_hook: tongueHook,
  starburst,
  dye_bloom: dyeBloom,
  dye_stain: dyeStain,
  mordant_lash: mordantLash,
  mordant_spew: mordantSpew,
  dye_drink: dyeDrink,
  intercept_guard: interceptGuard,
  intercept_block: interceptBlock,
  arrow_thud: arrowThud,
  bolt_ricochet: boltRicochet,
  // DoT ticks
  dot_poison: dotPoison,
  dot_decay: dotDecay,
  // Per-debuff application
  debuff_soaked: debuffSoaked,
  debuff_marked: debuffMarked,
  debuff_stagger: debuffStagger,
  debuff_fractured: debuffFractured,
  debuff_sundered: debuffSundered,
  debuff_weakened: debuffWeakened,
  debuff_burn: debuffBurn,
  debuff_bleed: debuffBleed,
  debuff_poison: debuffPoison,
  debuff_decay: debuffDecay,
  // Per-buff application
  buff_fury: buffFury,
  buff_tailwind: buffTailwind,
  buff_venomcoat: buffVenomcoat,
  buff_iron_wall: buffIronWall,
  buff_brace: buffBrace,
  // Fortification family (5 abilities)
  buff_fortify: buffFortify,
  buff_bristle: buffBristle,
  bristle_retaliate: bristleRetaliate,
  buff_lockstep: buffLockstep,
  buff_temper: buffTemper,
  buff_catalyze: buffCatalyze,
  buff_soulstitch: buffSoulstitch,
  buff_intensify: buffIntensify,
  buff_ward: buffWard,
  // Reactive moments
  regen_tick: regenTick,
  crit_flash: critFlash,
  super_effective: superEffective,
  resist_flash: resistFlash,
  // Axecraft (greataxe — heavy slams + fissures + execute + Break)
  axe_chop: axeChop,
  axe_execute: axeExecute,
  earth_fissures: earthFissures,
  faultline: faultline,
  earth_gather: earthGather,
  aftershock_pulse: aftershockPulse,
  // The Rift (Ch 9 boss)
  void_rupture: voidRupture,
  void_collapse: voidCollapse,
  rift_seep: riftSeep,
  void_emerge: voidEmerge,
  // Illusionism (astral)
  mind_needle: mindNeedle,
  mind_pinprick: mindPinprick,
  mind_swarm: mindSwarm,
  mind_clarity: mindClarity,
  mind_collapse: mindCollapse,
  // Necrotics (dark — bone-grey + violet, scythe / drain-spiral / void-collapse)
  reaper_scythe: reaperScythe,
  void_devour: voidDevour,
  oblivion_void: oblivionVoid,
  // Pyromancy (orange-core, projected flames)
  flame_jet: flameJet,
  pyre_immolate: pyreImmolate,
  // Atrophics (decay-violet cage)
  harrow_curse: harrowCurse,
  // Verdancy (bloom petals)
  bloom_petals: bloomPetals,
  verdant_surge: verdantSurge,
  // Sorcery / Conjuration (sigils + constellations)
  sigil_collapse: sigilCollapse,
  astral_brand: astralBrand,
  // Prowess (Duelist — finesse blade family)
  displace_strike: displaceStrike,
  counter_flash: counterFlash,
  bladework_combo: bladeworkCombo,
  // Tactics (battlefield command — chevrons, brackets, bugle calls)
  tactics_engage_strike: tacticsEngageStrike,
  tactics_suppress_press: tacticsSuppressPress,
  tactics_standing_order_call: tacticsStandingOrderCall,
  tactics_coordinate_push: tacticsCoordinatePush,

  wave_crest: waveCrest,
  tide_mend: tideMend,
  confluence_aura: confluenceAura,
  // Zealotry (paladin holy — gold cross strike / dual pillar / mark sigil)
  holy_radiance_strike: holyRadianceStrike,
  wrath_pillar: wrathPillar,
  holy_brand: holyBrand,
  // Manifestation (holy native — radiant materialization, paler-gold palette)
  buff_manifest_blade: buffManifestBlade,
  // Artifice (explosive ordnance)
  bombard_shell: bombardShell,
  powderkeg_blast: powderkegBlast,
};

const DEFAULT_PRESET = 'slash';
const DEFAULT_TINT = 0xffffff;

export function playPreset(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  presetName: string,
  tint: number,
): void {
  const preset = PRESETS[presetName] ?? PRESETS[DEFAULT_PRESET];
  preset.play(scene, sprite, tint || DEFAULT_TINT);
}

// ---------------------------------------------------------------------------
// Transform Presets — bespoke marquee sequences for boss form transitions.
//
// Distinct from hit-effect PRESETS in three ways:
//   1) No tint param — transforms bake their own palette.
//   2) Longer duration budget (~600-1000ms vs 100-400ms for hits).
//   3) Triggered by `enemy_transformed` events at HP thresholds, not by hits.
//
// Wired via FormTransition.transformAnimationKey on the enemy template.
// CombatScene's transformEnemy reads the key off the event and dispatches.
// ---------------------------------------------------------------------------

export interface TransformPreset {
  play(scene: Phaser.Scene, sprite: Phaser.GameObjects.Sprite, targetScale: number): void;
}

/** Velara's dye-saturation transform — chapter-13 signature visual for the
 *  Mordant boss's two HP-threshold transitions. Violet fountain erupts upward,
 *  collapses into a deep violet sprite-flash, then 12 spatter blobs arc out
 *  and fall as drips. Heavy camera shake sells the marquee moment. ~900ms
 *  total — matches the COMBAT_ENEMY_TRANSFORMED delay in EventPlaybackManager
 *  so playback is gated on the visual. */
const velaraDyeTransform: TransformPreset = {
  play(scene, sprite, targetScale) {
    const cx = sprite.x;
    const cy = sprite.y - sprite.displayHeight * 0.5;
    const sh = sprite.displayHeight;
    const sw = sprite.displayWidth;
    const violet = 0x9933cc;
    const deepViolet = 0x6622aa;
    const textureKey = sprite.texture.key;

    // 1) Upward dye fountain — column erupts from sprite base
    const fountain = scene.add.graphics();
    fountain.setPosition(cx, sprite.y);
    fountain.setDepth(sprite.depth + 1);
    fountain.setBlendMode(Phaser.BlendModes.NORMAL);

    const fountainData = { h: 0, alpha: 1.0 };
    scene.tweens.add({
      targets: fountainData,
      h: sh * 1.1,
      duration: 320,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        fountain.clear();
        // Outer violet column — tapered teardrop
        fountain.fillStyle(violet, fountainData.alpha * 0.9);
        const w = sw * 0.45 * (1 - fountainData.h / (sh * 1.1) * 0.45);
        fountain.fillEllipse(0, -fountainData.h * 0.5, w, fountainData.h);
        // White-violet inner core
        fountain.fillStyle(0xffeeff, fountainData.alpha * 0.6);
        fountain.fillEllipse(0, -fountainData.h * 0.5, w * 0.4, fountainData.h * 0.85);
      },
    });
    scene.tweens.add({
      targets: fountainData,
      alpha: 0,
      duration: 240,
      delay: 320,
      ease: 'Sine.easeIn',
      onComplete: () => fountain.destroy(),
    });

    // 2) Deep violet sprite-shaped flash to sell the saturation
    const flash = scene.add.sprite(sprite.x, sprite.y, textureKey);
    flash.setOrigin(0.5, 1.0);
    flash.setScale(targetScale);
    flash.setTint(deepViolet);
    flash.setBlendMode(Phaser.BlendModes.NORMAL);
    flash.setAlpha(0);
    flash.setDepth(sprite.depth + 2);
    scene.tweens.add({
      targets: flash,
      alpha: 0.85,
      duration: 280,
      delay: 220,
      ease: 'Sine.easeOut',
      yoyo: true,
      hold: 80,
      onComplete: () => flash.destroy(),
    });

    // 3) White core flash on top — marks the moment of saturation
    const core = scene.add.sprite(sprite.x, sprite.y, textureKey);
    core.setOrigin(0.5, 1.0);
    core.setScale(targetScale);
    core.setTint(0xffffff);
    core.setBlendMode(Phaser.BlendModes.ADD);
    core.setAlpha(0);
    core.setDepth(sprite.depth + 3);
    scene.tweens.add({
      targets: core,
      alpha: 0.95,
      duration: 120,
      delay: 360,
      ease: 'Sine.easeOut',
    });
    scene.tweens.add({
      targets: core,
      alpha: 0,
      duration: 360,
      delay: 480,
      ease: 'Cubic.easeIn',
      onComplete: () => core.destroy(),
    });

    // 4) Twelve spatter-blobs arc out from the saturation moment
    scene.time.delayedCall(420, () => {
      const SPATTERS = 12;
      for (let i = 0; i < SPATTERS; i++) {
        const angle = (i / SPATTERS) * Math.PI * 2;
        const dist = sh * (0.55 + Math.random() * 0.4);
        const targetX = cx + Math.cos(angle) * dist;
        const peakY = cy + Math.sin(angle) * dist * 0.6;

        const blob = scene.add.graphics();
        blob.fillStyle(violet, 0.95);
        blob.fillCircle(0, 0, 6 + Math.random() * 3);
        blob.fillStyle(0xffeeff, 0.45);
        blob.fillCircle(-1, -1, 2.5);
        blob.setPosition(cx, cy);
        blob.setDepth(sprite.depth + 4);

        scene.tweens.add({
          targets: blob,
          x: targetX,
          y: peakY,
          duration: 280,
          ease: 'Quad.easeOut',
          onComplete: () => {
            scene.tweens.add({
              targets: blob,
              y: peakY + 60 + Math.random() * 40,
              alpha: 0,
              scaleY: 1.8,
              duration: 320,
              ease: 'Quad.easeIn',
              onComplete: () => blob.destroy(),
            });
          },
        });
      }
    });

    // 5) Camera flash + heavy shake (marquee moment vs. generic 0.004 shake)
    scene.cameras.main.flash(180, 153, 51, 204, false);
    scene.cameras.main.shake(420, 0.012);
  },
};

/** Generic white-flash fallback — used when a form transition has no
 *  bespoke transformAnimationKey wired (or the key doesn't resolve). */
const genericWhiteFlash: TransformPreset = {
  play(scene, sprite, targetScale) {
    const flash = scene.add.sprite(sprite.x, sprite.y, sprite.texture.key);
    flash.setOrigin(0.5, 1.0);
    flash.setScale(targetScale);
    flash.setTint(0xffffff);
    flash.setBlendMode(Phaser.BlendModes.ADD);
    flash.setAlpha(0.9);
    flash.setDepth(sprite.depth + 1);
    scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });
    scene.cameras.main.shake(180, 0.004);
  },
};

export const TRANSFORM_PRESETS: Record<string, TransformPreset> = {
  velara_dye: velaraDyeTransform,
  generic_flash: genericWhiteFlash,
};

const DEFAULT_TRANSFORM_PRESET = 'generic_flash';

export function playTransformPreset(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  targetScale: number,
  presetName?: string,
): void {
  const preset = (presetName ? TRANSFORM_PRESETS[presetName] : undefined)
    ?? TRANSFORM_PRESETS[DEFAULT_TRANSFORM_PRESET];
  preset.play(scene, sprite, targetScale);
}
